import { create } from 'zustand';
import {
  fetchConversationsListParams,
  SidebarItem,
} from '../../../components/blocks/layout/types/sidebar-conversation';
import {
  A2uiSurfaceStatus,
  Attachment,
  McpServiceAuthRequiredData,
  isConnectableAuthMethod,
  FileMetadata,
  initialFeaturesState,
  Message,
  MessageDeliveryStatus,
  MessageGeneration,
  SubagentStreamStatus,
  ToolCallStreamStatus,
  Mention,
  RelativeSearchQuestion,
  ReplyMessageData,
  SearchSource,
  Source,
  Tools,
} from '../../../features/chat/types/chat';
import { v4 as uuidv4 } from 'uuid';
import { clients } from '../../../lib/https';
import { useModelStore } from './use-model-store';
import { useMcpServiceChatStore } from './use-mcp-service-chat-store';
import { AssistantPositionRemainingType, PlatformResourceChangedData, ResponseTimeAnalytics, SSEResponseTypes, TokenAnalytics } from '../../../models/websockets.types';
import { isSyntheticAutoModel } from '../../../features/chat/utils/resolve-conversation-model';
import { WebsocketServices } from '../../../services/websocket-services';
import { toast } from 'hooks/use-toast';
import i18n from 'i18n/i18n';
import { ButtonIds } from 'features/chat/types/chat';
import { ConfigurationSettings } from '../../../models/configuration-settings';
import { getSearchRelatedQuestionList, getSSEStream, getGroupChatStream, getResumeChatStream, getTitle2, stopGeneration, buildCollaborationCancelPayload, updateMessage } from '../../../services/sse-service';
import type { IntegrationSource } from '../../../services/sse-service';
import { getFile } from '../../../services/storage-service';
import { normalizeSourceFromWire } from '../../../features/chat/utils/source-citations';

import {
  applyNewConnectionWhileStreaming,
  applyStreamReplayBurst,
  assistantBelongsToUserTurn,
  buildUserTurnIdSet,
  consolidateAssistantMessages,
  ensureStreamingAssistantPlaceholder,
  deriveGroupChatTitleFromParticipants,
  findUserMessageForLinkedId,
  insertUserBeforeStreamingAssistant,
  isMeaningfulConversationTitle,
  shouldQueueReconnectUntilUserPrompt,
  resolveConversationDisplayTitle,
  shouldAutoGenerateConversationTitle,
  shouldSkipSidebarConversationEntry,
  stripConversationDisplayQuotes,
  getUpdatedMessages,
  hasStreamingAssistantTarget,
  parseThinkingContent,
  preferLiveReconnectMessages,
  resolveStreamGenerationTarget,
  resolveStreamUserMessageId,
  resolveUserMessageRecordIdForAssistant,
  finalizeTurnInFlightAssistant,
  removeEchoUserMessageRows,
} from 'utils/chat-utilities';
import {
  notifyStreamCancelled,
  waitForStreamCancelled,
} from 'features/chat/utils/stream-cancelled-waiter';
import {
  ASSISTANT_STREAM_TIMEOUT_MS,
  DEFAULT_CONVERSATION_NAME,
  DEFAULT_MAIN_CHAT_AGENT_ID,
  isCatalogueAgentId,
  isDefaultMainChatAgentId,
  NEW_CONNECTION_WHILE_STREAMING,
} from 'utils/constants/chat-constants';
import { getConversationAccessErrorMessage } from 'utils/conversation-access-error';
import { getUserDisplayName } from 'utils/user-display-name';
import { Project, ProjectItem } from 'features/projects/types/create-project';
import { trackEvent } from 'analytics/tracker';
import { EVENTS, FeatureName } from 'analytics/events';
import { MessageRole } from 'features/chat/types/chat';
import { useCommonStore } from '../common/useCommonStore';
import { ApiConversationResponse, fetchProjectConversations } from 'features/projects/services/projects-services';
import { conversationVisualFields } from 'features/chat/utils/conversation-sidebar-visual';
import { getUsers, IamData } from 'features/iam/services/user-service';
import { setBuddyOpenConversationIds } from 'config/buddy/conversations';
import { getConversationById, ConversationEvent } from 'services/conversation-services';
import { getGroupChatParticipants } from 'features/group-chat/services/group-services';
import {
  setScheduledMessageTriggeredHandler,
  useCollaborationSocketStore,
} from '../collaboration/use-collaboration-store';
import { isToday, isYesterday } from 'date-fns';
import {
  formatShowMessageTimeForSend,
  messageGenerationTimeMs, parseMessageTimeForDisplay,
  showMessageTimeFromBackend,
  timestampFromBackendCreatedDate,
} from 'features/chat/utils/message-show-time';
import {
  parseSubagentRunFromStreamCompletedPayload,
  parseSubagentRunFromStreamStartedPayload,
  parseSubagentRunsFromMetadata,
  upsertMessageSubagentRun,
} from 'features/chat/utils/parse-message-subagent-runs';
import {
  parseToolCallsFromMetadata,
  parseToolCallFromStreamCompletedPayload,
  parseToolCallFromStreamStartedPayload,
  upsertMessageToolCall,
} from 'features/chat/utils/parse-message-tool-calls';
import {
  collectA2uiApprovals,
  collectA2uiSubmissions,
  parseA2uiSurfaceFromStreamPayload,
  parseA2uiSurfacesFromMetadata,
  parseApprovalRequiredFromStreamPayload,
  parseApprovalResolvedFromStreamPayload,
} from 'features/chat/utils/parse-message-a2ui';
import {
  expireActiveApprovals,
  isUserMessageRole,
} from 'features/chat/utils/approval-conversation-messages';
import {
  applyStreamApprovalResolved,
  applyStreamStatusEntry,
  applyStreamSurface,
  hydrateMessageStreamBlocks,
  markStreamSurfaceStatus,
} from 'features/chat/utils/stream-blocks';
import {
  formatSkillDisplayName,
  parseSkillEventFromStreamPayload,
  parseSkillEventsFromMetadata,
  streamEventTypeToSkillStreamStatus,
  upsertMessageSkillEvent,
} from 'features/chat/utils/parse-message-skill-events';
import {
  appendStreamStatusEntry,
  createSkillStreamStatusEntry,
  createSubagentStreamStatusEntry,
  upsertTodosStreamStatusEntry,
  nextStatusOffset,
} from 'features/chat/utils/stream-status-entries';
import { clearStreamingStatusFields } from 'features/chat/utils/clear-streaming-status-fields';
import { completeGenerationInProgressTodos } from 'features/chat/utils/stream-blocks';
import { resolveConversationModelName } from 'features/chat/utils/resolve-conversation-model';
import { shouldClearMessageLoadingForStreamEvent } from 'features/chat/utils/streaming-message-loader';
import { compactionStatusFromEventType } from 'features/chat/utils/conversation-compaction';
import { formatTimeWithAMPM } from 'lib/utils';

const extractLastResponseContent = (content: string) => {
  const lastIndefOfResponseOpenTag = content.lastIndexOf('<response>');
  if (lastIndefOfResponseOpenTag !== -1) {
    const lastIndefOfResponseCloseTag = content.indexOf('</response>', lastIndefOfResponseOpenTag);
    if (lastIndefOfResponseCloseTag !== -1) {
      return content.slice(
        lastIndefOfResponseOpenTag + '<response>'.length,
        lastIndefOfResponseCloseTag
      );
    } else {
      return content.slice(lastIndefOfResponseOpenTag + '<response>'.length);
    }
  }
  return content;
};

/**
 * Formats a date to show "Today at HH:mm AM/PM" or "Yesterday at HH:mm AM/PM" or date format
 */
const formatDateWithTime = (date: Date): string => {
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const ampm = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours % 12 || 12;
  const minutesStr = minutes.toString().padStart(2, '0');
  const timeStr = `${displayHours}:${minutesStr} ${ampm}`;

  if (isToday(date)) {
    return `Today at ${timeStr}`;
  }
  if (isYesterday(date)) {
    return `Yesterday at ${timeStr}`;
  }
  // For older dates, show the date
  const day = date.getDate();
  const month = date.toLocaleString('default', { month: 'short' });
  const year = date.getFullYear();
  return `${month} ${day}, ${year} at ${timeStr}`;
};

function posthogSSEEvent(responseType: SSEResponseTypes, additionalInfo: any) {
  const isError = [
    SSEResponseTypes.SERVER_ERROR,
    SSEResponseTypes.FILE_GENERATION_FAILED,
    SSEResponseTypes.IMAGE_GENERATION_FAILED,
    SSEResponseTypes.CONTEXT_WINDOW_EXCEEDED,
    SSEResponseTypes.IMAGE_GENERATION_LIMIT_EXCEEDED
  ].includes(responseType);

  const isRegularAnswer = [
    SSEResponseTypes.RESPONSE_SOURCES,
    SSEResponseTypes.SEARCH_SOURCES,
    SSEResponseTypes.TOKEN_ANALYTICS,
    SSEResponseTypes.CONVERSATION_TITLE,
    SSEResponseTypes.END_OF_STREAM,
    SSEResponseTypes.STREAM_CANCELLED,
    SSEResponseTypes.LLM_THINKING,
    SSEResponseTypes.LLM_ANSWER,
    SSEResponseTypes.SEARCH_RESPONSE,
    SSEResponseTypes.TOOL_CALL_STARTED,
    SSEResponseTypes.TOOL_CALL_COMPLETED,
    SSEResponseTypes.SUBAGENT_STARTED,
    SSEResponseTypes.SUBAGENT_COMPLETED,
    SSEResponseTypes.SKILL_USED,
    SSEResponseTypes.SKILL_SKIPPED,
    SSEResponseTypes.SKILL_RESOURCE_LOADED,
    SSEResponseTypes.A2UI_MESSAGE,
    SSEResponseTypes.APPROVAL_REQUIRED,
    SSEResponseTypes.APPROVAL_RESOLVED,
    SSEResponseTypes.CONVERSATION_COMPACTION_STARTED,
    SSEResponseTypes.CONVERSATION_COMPACTION_COMPLETED,
    SSEResponseTypes.CONVERSATION_COMPACTION_FAILED,
    SSEResponseTypes.CONTENT_FILTER_VIOLATION,
  ].includes(responseType);

  if (isError) {
    trackEvent(EVENTS.SSE_ERROR_RESPONSE(responseType, additionalInfo));
  } else if (!isRegularAnswer) {
    trackEvent(EVENTS.SSE_INFO_RESPONSE(responseType, additionalInfo));
  }
}


const extractFeedbackFromReactions = (
  reactions: any,
  currentUserId: string | undefined
): boolean | undefined => {
  if (!reactions || !currentUserId) {
    return undefined;
  }

  // Check if user has liked the message
  if (reactions.LIKE && Array.isArray(reactions.LIKE.userIds)) {
    if (reactions.LIKE.userIds.includes(currentUserId)) {
      return true;
    }
  }

  // Check if user has disliked the message
  if (reactions.DISLIKE && Array.isArray(reactions.DISLIKE.userIds)) {
    if (reactions.DISLIKE.userIds.includes(currentUserId)) {
      return false;
    }
  }

  return undefined;
};

export interface StreamingQueueItem {
  position: number;
  conversationId: string;
  messageID: string;
  type: string;
  userID: string[] | null;
  parentIds: string[];
  streamingResponse: any[];
}

export interface MessageQueueItem {
  position: number;
  key: string; // dummyUserMessageId
  content: string; // messageBody
  attachmentsMetadata?: FileMetadata[];
  parentIds?: string[];
  mentions?: string[];
  isRegenerate?: boolean;
  imageGeneration?: boolean;
  integrationSources?: IntegrationSource[];
  searchOnline?: boolean;
  selectedTools?: string[];
  selectedSkillIds?: string[];
}

export interface ConversationState {
  id: string;
  title: string;
  messages: Message[];
  isLoading: boolean;
  isCurrentMessageLoading: boolean;
  isCurrentResponseGenerating: boolean;
  isAnalyzing: boolean;
  isAnalyzed: boolean;
  hasFetchedInitialMessages: boolean;
  error?: string | null;
  modelName?: string;
  /** Invitation participant ids — used to restore the model after reload once the catalogue loads. */
  invitationParticipantIds?: string[];
  allFileIds?: string[];
  streamingAssistantMessageId?: string | null;
  streamingAssistantMessageIds?: string[]; // Array to track multiple concurrent streaming messages (for group chats)
  toolsState: Record<ButtonIds, boolean>;
  isGroupChat?: boolean;
  hasManualTitle?: boolean;
  participants?: IamData[];
  removedParticipants?: IamData[];
  isRecentlyAdded?: boolean;
  assistantResponseRemaining?: number;
  activeStreamsMap?: Map<string, boolean>;
  isCancelOnGoing?: boolean;
  /** Collaboration POST succeeded; Stop is allowed before the first stream frame. */
  canCancelGeneration?: boolean;
  customGptId?: string;
  agentId?: string;
  /** Seeded from SCHEDULED_MESSAGE_TRIGGERED before full history was fetched. */
  isScheduledLiveStub?: boolean;
  /** The agent no longer resolves for this user (made private / grant revoked). */
  agentUnavailable?: boolean;
  /** Assistant message ids whose last processed frame carried the reconnect replay reason. */
  replayingAssistantMessageIds?: string[];
}

export interface ChatStoreState {
  itemIdFilePathMap: Map<string, string>;
  activeConversationId: string | null;
  activeAudioMessageId: string | null;
  conversations: Record<string, ConversationState>;
  sidebarConversationsList: SidebarItem[];
  searchConversationsList: SidebarItem[];
  searchConversationsTotalCount: number;
  totalConversationsCount: number;
  totalIndividualConversationsCount: number;
  totalGroupConversationsCount: number;
  sidebarProjectsList: Project[];
  updateProjectConversation: (conversationId: string, configurationSettings: ConfigurationSettings) => Promise<void>;
  setSidebarProjectsList: (projects: Project[]) => void;
  removeProjectFromSidebar: (projectId: string) => void;
  updateProjectItems: (projectId: string, conversations: ProjectItem[]) => void;
  isSidebarLoading: boolean;
  isSidebarIndividualLoading: boolean;
  isSidebarGroupLoading: boolean;
  sidebarError: string | null;
  isNewChat: boolean;
  hasParticipant: boolean;
  setHasParticipant: (value: boolean) => void;
  hasParticipantsArray: IamData[];
  setHasParticipantsArray: (value: IamData[]) => void;
  isLoadingParticipants: boolean;
  setIsLoadingParticipants: (value: boolean) => void;
  hasAddedAssistantAsParticipant: boolean;
  setHasAddedAssistantAsParticipant: (value: boolean) => void;
  isGroupChat: boolean;
  setGroupChat: (value: boolean) => void;
  conversationTitleRefetched: boolean;
  setConversationTitleRefetched: (value: boolean) => void;
  replyMessage: ReplyMessageData | null;
  setReplyMessage: (value: ReplyMessageData | null) => void;
  commentMessage: Message | null;
  setCommentMessage: (value: Message | null) => void;
  _handleWebSocketMessage: (parsedResponse: any, config: ConfigurationSettings) => void;
  _addMessageToActiveConversation: (msg: Message, recordIdToUpdate?: string, conversationId?: string) => void;
  handleStreamingQueue: (streamingQueue: StreamingQueueItem[], config: ConfigurationSettings) => void;
  headerTitle: string;
  isTitleLoading: boolean;
  setIsTitleLoading: (value: boolean) => void;
  setHeaderTitle: (value: string) => void;
  hasGroupChatParticipantsArray: { [key: string]: IamData[] };

  setHasGroupChatParticipantsArray: (conversationId: string, participants: IamData[]) => void;
  fetchAndSetGroupChatParticipants: (conversationId: string, config: ConfigurationSettings, refetch?: boolean, loading?: boolean) => Promise<void>;
  isIncognito: boolean;
  setIsIncognitoChat: (value: boolean) => void;
  sendMessage: (
    content: string,
    config: ConfigurationSettings,
    newConversationId?: string,
    attachments?: Attachment[],
    isFeatureSelected?: Record<ButtonIds, boolean>,
    addToSidebar?: boolean,
    parentIds?: string[],
    sources?: IntegrationSource[],
    isRegenerate?: boolean,
    originalRegenerationMessageId?: string,
    mentions?: Mention[],
    parentUserGenerationId?: string,
    isIncognitoChat?: boolean,
    customGptId?: string,
    selectedTools?: string[],
    selectedSkillIds?: string[],
    agentId?: string,
    isAgentPreviewChat?: boolean,
    a2uiMetadata?: Record<string, unknown>,
  ) => Promise<void>;

  sendMessageToProject: (
    content: string,
    config: ConfigurationSettings,
    projectId: string,
    newConversationId?: string,
    attachments?: Attachment[],
    isFeatureSelected?: Record<ButtonIds, boolean>,
    sources?: IntegrationSource[],
    selectedTools?: string[],
  ) => Promise<void>;

  fetchAttachmentMetadata: (attachmentIds: string[], sender: MessageRole, config: ConfigurationSettings) => Promise<FileMetadata[]>;
  sendSSEMessage: (messageBody: string, config: ConfigurationSettings, conversationId: string) => void;
  sendGroupChatStream: (
    messageBody: string,
    config: ConfigurationSettings,
    conversationId: string,
    attachmentsMetadata?: FileMetadata[],
    replyMessage?: ReplyMessageData | null,
    mentions?: string[],
    isRegenerate?: boolean,
    originalRegenerationMessageId?: string,
    imageGeneration?: boolean,
    integrationSources?: IntegrationSource[],
    searchOnline?: boolean,
    retryMessageId?: string, // ID of message to retry (prevents creating new message)
    dummyUserMessageId?: string, // Dummy ID generated for the user message that needs to be replaced
    selectedTools?: string[],
    selectedSkillIds?: string[],
    a2uiMetadata?: Record<string, unknown>,
  ) => Promise<void>;
  /** Optimistically sets an A2UI surface's status wherever it lives in the conversation. */
  markA2uiSurfaceStatus: (
    conversationId: string,
    surfaceId: string,
    status: A2uiSurfaceStatus
  ) => void;
  /** Defensive composer reset (e.g. an approval expired): drops the generating flags without touching the run. */
  clearConversationGenerating: (conversationId: string) => void;
  /** Resumes the turn paused on `auth_required`: reuses the paused generation, no new user bubble. */
  resumePendingAuthTurn: (
    config: ConfigurationSettings,
    queryMetadata: {
      tool_ids?: string[];
      skill_ids?: string[];
      searchOnline?: boolean;
      imageGeneration?: boolean;
      integrationSources?: IntegrationSource[];
      /** Fallback Q1 text if the stored user generation content can't be resolved. */
      content?: string;
    }
  ) => Promise<void>;
  retryFailedMessage: (
    messageId: string,
    config: ConfigurationSettings,
    conversationId: string,
  ) => Promise<void>;
  regenerateResponse: (recordId: string, config: ConfigurationSettings) => Promise<void>;
  editUserMessage: (
    userRecordId: string,
    content: string,
    config: ConfigurationSettings,
    options?: { attachments?: Attachment[]; selectedModelId?: string }
  ) => Promise<boolean>;
  setActiveConversationId: (conversationId: string | null) => void;
  setActiveConversationModelName: (modelName: string) => void;
  setActiveAudioMessageId: (messageId: string | null) => void;
  setActiveConversation: (
    conversationId: string | null,
    config: ConfigurationSettings,
    participants?: IamData[]
  ) => Promise<void>;
  fetchAndSetConversationMessages: (
    conversationId: string,
    config: ConfigurationSettings,
    participants?: IamData[],
    options?: { silent?: boolean }
  ) => Promise<void>;
  invitedUsersData: IamData[];
  setInvitedUsersData: (users: IamData[]) => void;
  generateConversationTitle: (conversationId: string, config: ConfigurationSettings) => void;
  syncConversationTitleFromApi: (
    conversationId: string,
    apiTitle: string | null | undefined,
    config: ConfigurationSettings
  ) => void;
  syncGroupChatTitleFromParticipants: (conversationId: string, participants: IamData[]) => void;
  updateConversationTitleInStore: (
    conversationId: string,
    newTitle: string,
    options?: { isManual?: boolean }
  ) => void;
  setAgentAvailability: (conversationId: string, unavailable: boolean) => void;
  updateProjectNameInStore: (projectId: string, newName: string, color?: string) => void;
  fetchAndUpdateProjectAllConversations: (configurationSettings: ConfigurationSettings, projectId: string) => Promise<void>;
  appendToSidebarConversationsList: (newItem: SidebarItem) => void;
  appendProjectItemToSidebarProjectsList: (newItem: ProjectItem, projectId: string) => void;
  appendProjectToSidebarProjectsList: (newItem: Project) => void;
  _sortSidebarConversations: (newList: SidebarItem[]) => SidebarItem[];
  _moveConversationToToday: (conversationId: string) => void;
  fetchSidebarConversations: (
    config: ConfigurationSettings,
    params?: fetchConversationsListParams
  ) => Promise<void>;
  removeConversationFromSidebarById: (conversationId: string) => void;
  removeConversationFromSidebarProjectListById: (conversationId: string) => void;
  removeConversationFromRecentAndProjects: (conversationId: string) => void;
  fetchSearchResults: (
    search: string,
    config: ConfigurationSettings,
    options?: { page?: number; pageSize?: number }
  ) => Promise<void>;
  setItemIdFilePathMap: (key: string, value: string) => void;
  clearItemIdFilePathMap: () => void;
  changeActiveGenerationIndex: (recordId: string, index: number) => void;
  updateMessageFeedback: (
    recordId: string,
    messageId: string,
    feedback: boolean | undefined
  ) => void;
  setNewChat: (isNew: boolean) => void;
  disableNewChat: boolean;
  setDisableNewChat: (disable: boolean) => void;
  setNewChatWithModel: (isNew: boolean) => void;
  setChatErrorMessage: (conversationId: string) => void;
  reset: () => void;
  resetErrorByConversationId: (conversationId: string) => void;
  getSearchRelatedList: (data: { conversationId: string; messageId: string; type: string; modelName: string; }, config: ConfigurationSettings) => void;
  getSearchRelatedImageList: (data: { conversationId: string; messageId: string; type: string; modelName: string; }, config: ConfigurationSettings) => void;
  getSearchRelatedVideoList: (data: { conversationId: string; messageId: string; type: string; modelName: string; }, config: ConfigurationSettings) => void;
  setrelatedSearcheQuestionsList: (conversationId: string, relatedSearchedQuestionsList: RelativeSearchQuestion[], messageId?: string) => void;
  setrelatedSearcheImageList: (conversationId: string, relatedSearchedImageList: RelativeSearchQuestion[], messageId?: string) => void;
  setrelatedSearcheVideoList: (conversationId: string, relatedSearchedVideoList: RelativeSearchQuestion[], messageId?: string) => void;
  handleGroupChatNotification: (notification: any, config?: ConfigurationSettings, type?: string, userIds?: { invitedBy: string, invitedTo: string[] }) => Promise<void>;
  ensureScheduledConversationFromSocket: (notification: any) => void;
  handleStreamingMessageBatch: (messages: any[], config: ConfigurationSettings) => void;
  pendingStreamingNotifications: Record<string, any[]>;
  enqueuePendingStreamingNotification: (conversationId: string, notification: any) => void;
  flushPendingStreamingNotifications: (conversationId: string, config: ConfigurationSettings) => void;
  temporaryConversation: (
    content: string,
    attachments?: Attachment[],
    isFeatureSelected?: Record<ButtonIds, boolean>
  ) => { temporaryConversationId: string; recordId: string; assistantMessageId: string };
  replaceConversationId: (oldConversationId: string, newConversationId: string, isIncognitoChat: boolean) => void;
  fetchUsersData: (userIds: string[], config: ConfigurationSettings) => Promise<{ data: IamData[]; totalCount: number }>;
  messageQueue: MessageQueueItem[];
  addToMessageQueue: (item: MessageQueueItem) => void;
  removeFromMessageQueue: (key: string) => void;
  clearMessageQueue: () => void;
  isProcessingMessageQueue: boolean;
  setIsProcessingMessageQueue: (value: boolean) => void;
  processNextMessageFromQueue: (conversationId: string, config: ConfigurationSettings) => Promise<void>;
  updateMessageAnalytics: (recordId: string, messageId: string, analytics: ResponseTimeAnalytics | undefined) => void;
  stopGeneration: (conversationId: string, config: ConfigurationSettings) => void;
  shareTokens: Record<string, string>;
  setConversationShareToken: (conversationId: string, token: string) => void;
  getConversationShareToken: (conversationId: string) => string | undefined;
}

const conversationModelPatch = (
  participantKeys: string[],
  previousModelName?: string,
) => {
  const modelIds = useModelStore.getState().models.map((model) => model.id);
  return {
    invitationParticipantIds: participantKeys,
    modelName: resolveConversationModelName(participantKeys, modelIds) ?? previousModelName,
  };
};

export const initialConversationState = (id: string, title = "", participants?: IamData[]): ConversationState => {
  // Ensure modelName is always set - use selectedModel or fallback to 'auto'



  return {
    id,
    title,
    messages: [],
    isLoading: false,
    isCurrentMessageLoading: false,
    isCurrentResponseGenerating: false,
    isAnalyzing: false,
    isAnalyzed: false,
    hasFetchedInitialMessages: false,
    error: null,
    modelName: "auto",
    allFileIds: [],
    streamingAssistantMessageId: null,
    streamingAssistantMessageIds: [], // Track multiple concurrent streaming messages
    toolsState: initialFeaturesState,
    isGroupChat: false,
    participants,
    removedParticipants: [],
    isRecentlyAdded: false,
    assistantResponseRemaining: 0,
    activeStreamsMap: new Map(),
    isCancelOnGoing: false,
    canCancelGeneration: false,
    customGptId: undefined,
    isScheduledLiveStub: false,
  };
};

const isConversationActivelyStreaming = (conv: ConversationState | undefined): boolean => {
  if (!conv) return false;
  return (
    conv.isCurrentResponseGenerating ||
    conv.isCurrentMessageLoading ||
    !!conv.streamingAssistantMessageId ||
    (conv.streamingAssistantMessageIds?.length ?? 0) > 0 ||
    (conv.activeStreamsMap?.size ?? 0) > 0
  );
};

const firstCatalogueAgentId = (
  ...candidates: Array<string | null | undefined>
): string | undefined => {
  for (const id of candidates) {
    if (typeof id === 'string' && isCatalogueAgentId(id)) return id;
  }
  return undefined;
};

const catalogueAgentIdFromSidebarItem = (
  item: ApiConversationResponse,
  existingAgentId?: string,
): string | undefined => {
  const fromPayload = firstCatalogueAgentId(item.agentId, ...(item.agentIds ?? []));
  if (fromPayload) return fromPayload;
  return isCatalogueAgentId(existingAgentId) ? existingAgentId : undefined;
};

const catalogueAgentIdFromMentions = (mentions: unknown): string | undefined => {
  if (!Array.isArray(mentions)) return undefined;
  return mentions.find((id): id is string => typeof id === 'string' && isCatalogueAgentId(id));
};

const catalogueAgentIdFromConversationPayload = (freshData: {
  agentId?: string;
  agent_id?: string;
  participants?: Record<string, { type?: string }>;
} | null | undefined): string | undefined => {
  if (!freshData) return undefined;
  const direct = firstCatalogueAgentId(freshData.agentId, freshData.agent_id);
  if (direct) return direct;

  const participants = freshData.participants;
  if (!participants || typeof participants !== 'object') return undefined;
  return Object.keys(participants).find((key) => {
    const type = participants[key]?.type;
    return (type === 'agent' || type === 'Agent') && isCatalogueAgentId(key);
  });
};

const CHAT_ERROR_MESSAGE_KEY = 'SOMETHING_WENT_WRONG_ERROR_MESSAGE';
const CHAT_ERROR_MESSAGE =
  "I'm experiencing technical difficulties with the AI service. Please try again in a moment.";
export const UNABLE_TO_LOAD_MESSAGE = 'Unable to load message.';

export function finalizeGenerationOnStreamEnd<T extends MessageGeneration>(
  generation: T,
  options?: {
    wasSearchEnabled?: boolean;
    isSearchSourcesShow?: boolean;
    hasCodeExecutionSteps?: boolean;
    serverMessageId?: string;
  }
): T {
  const completed = completeGenerationInProgressTodos(
    clearStreamingStatusFields({
      ...generation,
      id: options?.serverMessageId ?? generation.id,
      isAnalyzing: false, // Stop analyzing when stream ends
      isAnalyzed: generation.isAnalyzed || !!options?.hasCodeExecutionSteps,
      isThinking: false,
      isThisGenerationOnGoing: false,
      isSkeletonShowForImages: (options?.wasSearchEnabled || options?.isSearchSourcesShow) ? true : false,
      isSkeletonShowForVideos: (options?.wasSearchEnabled || options?.isSearchSourcesShow) ? true : false,
      isSkeletonShowForQuestions: (options?.wasSearchEnabled || options?.isSearchSourcesShow) ? true : false,
    })
  );

  const hasMeaningfulContent =
    Boolean(completed.content?.trim()) &&
    completed.content !== 'SeliseAI is typing...';

  const hasOtherSurfaces =
    Boolean(completed.thinkContent?.trim()) ||
    Boolean(completed.a2uiSurfaces?.length) ||
    Boolean(completed.streamBlocks?.some((block) => block.type !== 'text')) ||
    Boolean(completed.toolCalls?.length) ||
    Boolean(completed.codeExecutionSteps?.length);

  if (!hasMeaningfulContent && !hasOtherSurfaces) {
    return {
      ...completed,
      content: UNABLE_TO_LOAD_MESSAGE,
      streamBlocks: [{ type: 'text', text: UNABLE_TO_LOAD_MESSAGE }],
    };
  }

  if (completed.content === 'SeliseAI is typing...') {
    return {
      ...completed,
      content: '',
    };
  }

  return completed;
}

const applyEmptyAssistantContentFallback = (messages: Message[]): Message[] =>
  messages.map((message) => {
    if (message.role !== MessageRole.ASSISTANT) return message;
    return {
      ...message,
      generations: message.generations.map((generation) =>
        generation?.content?.trim() ||
        generation.thinkContent?.trim() ||
        // Empty text is legitimate when the turn carries A2UI/approval surfaces.
        generation.a2uiSurfaces?.length ||
        generation.streamBlocks?.some((block) => block.type !== 'text')
          ? generation
          : { ...generation, content: UNABLE_TO_LOAD_MESSAGE },
      ),
    };
  });

const assistantStreamTimeoutByConversationId = new Map<
  string,
  ReturnType<typeof setTimeout>
>();

const clearAssistantStreamTimeout = (conversationId: string) => {
  const existing = assistantStreamTimeoutByConversationId.get(conversationId);
  if (existing) {
    clearTimeout(existing);
    assistantStreamTimeoutByConversationId.delete(conversationId);
  }
};

// Stall watchdog: an *inactivity* timer (re-armed on every streamed frame in
// _handleWebSocketMessage), not a wall-clock cap — long healthy streams never
// trip it. On trip it reconciles against the server before erroring:
// a lost terminal chunk hydrates the real answer instead of showing a failure.
const assistantStreamReconcileAttemptsByConversationId = new Map<string, number>();
const MAX_STREAM_RECONCILE_ATTEMPTS = 5;

const armAssistantStreamWatchdog = (conversationId: string) => {
  clearAssistantStreamTimeout(conversationId);
  const timeoutId = setTimeout(() => {
    assistantStreamTimeoutByConversationId.delete(conversationId);
    void reconcileStalledStream(conversationId);
  }, ASSISTANT_STREAM_TIMEOUT_MS);
  assistantStreamTimeoutByConversationId.set(conversationId, timeoutId);
};

const reconcileStalledStream = async (conversationId: string) => {
  const store = useChatStore.getState();
  if (!store.conversations[conversationId]?.isCurrentResponseGenerating) {
    assistantStreamReconcileAttemptsByConversationId.delete(conversationId);
    return;
  }

  const config = useCollaborationSocketStore.getState().configurationSettings;
  if (!config) {
    armAssistantStreamWatchdog(conversationId);
    return;
  }

  // Heal the transport first (refreshes the token and reconnects if unhealthy).
  try {
    await useCollaborationSocketStore.getState().ensureConnected();
  } catch (err) {
    console.error('Stream watchdog: ensureConnected failed:', err);
  }

  // Did the run finish server-side while we were deaf?
  let finishedRemotely = false;
  try {
    const fresh = await getConversationById(conversationId, config);
    const local = useChatStore.getState().conversations[conversationId];
    const knownIds = new Set<string>();
    for (const msg of local?.messages ?? []) {
      if (msg.recordId) knownIds.add(msg.recordId);
      for (const gen of msg.generations ?? []) {
        if (gen.id) knownIds.add(gen.id);
      }
    }
    finishedRemotely = (fresh?.messages ?? []).some(
      (m: any) => m?.id && m.type !== 'user' && !knownIds.has(m.id)
    );
  } catch (err) {
    console.error('Stream watchdog: conversation fetch failed:', err);
  }

  const state = useChatStore.getState();
  if (finishedRemotely) {
    assistantStreamReconcileAttemptsByConversationId.delete(conversationId);
    const participants = state.conversations[conversationId]?.participants;
    await state.fetchAndSetConversationMessages(conversationId, config, participants, { silent: true });
    // The hydrator replaces messages but leaves streaming flags alone.
    useChatStore.setState((s) => {
      const conv = s.conversations[conversationId];
      if (!conv) return s;
      return {
        conversations: {
          ...s.conversations,
          [conversationId]: {
            ...conv,
            isCurrentResponseGenerating: false,
            isCurrentMessageLoading: false,
            isAnalyzing: false,
            streamingAssistantMessageId: null,
            streamingAssistantMessageIds: [],
            assistantResponseRemaining: 0,
            activeStreamsMap: new Map(),
          },
        },
      };
    });
    return;
  }

  // Nothing new server-side: still generating (long tool run) or truly dead.
  // Keep waiting a bounded number of cycles before surfacing an error.
  const attempts = (assistantStreamReconcileAttemptsByConversationId.get(conversationId) ?? 0) + 1;
  if (attempts < MAX_STREAM_RECONCILE_ATTEMPTS) {
    assistantStreamReconcileAttemptsByConversationId.set(conversationId, attempts);
    armAssistantStreamWatchdog(conversationId);
    return;
  }
  assistantStreamReconcileAttemptsByConversationId.delete(conversationId);
  state.setChatErrorMessage(conversationId);
};

const scheduleAssistantStreamTimeout = (
  conversationId: string,
  _setChatErrorMessage: (conversationId: string) => void,
  _getConversationState: (conversationId: string) => { isCurrentResponseGenerating?: boolean } | undefined
) => {
  assistantStreamReconcileAttemptsByConversationId.delete(conversationId);
  armAssistantStreamWatchdog(conversationId);
};

const createChatErrorAssistantMessage = (userRecordId?: string): Message => {
  const assistantMessageId = uuidv4();

  return {
    recordId: assistantMessageId,
    userRecordId,
    role: MessageRole.ASSISTANT,
    generations: [
      {
        id: assistantMessageId,
        content: CHAT_ERROR_MESSAGE,
        createdDate: Date.now(),
        showMessageTime: formatShowMessageTimeForSend(),
        isThinking: false,
        isAnalyzing: false,
        isThisGenerationOnGoing: false,
      },
    ],
  };
};

const getLastUserGenerationId = (messages: Message[]): string | undefined => {
  const lastUserMessage = [...messages]
    .reverse()
    .find((message) => message.role === MessageRole.USER);

  if (!lastUserMessage) return undefined;

  const activeGenerationIndex = lastUserMessage.activeGenerationIndex ?? 0;
  return lastUserMessage.generations?.[activeGenerationIndex]?.id || lastUserMessage.recordId;
};

const isAssistantMessageEmpty = (message: Message): boolean => {
  if (message.role !== MessageRole.ASSISTANT) return false;
  return (
    message.generations?.every(
      (generation) =>
        !generation?.content?.trim() &&
        !generation.isThinking &&
        !generation.isAnalyzing &&
        !(generation.codeExecutionSteps?.length ?? 0),
    ) ?? true
  );
};

const hasChatErrorContent = (message: Message): boolean =>
  message.role === MessageRole.ASSISTANT &&
  message.generations?.some((generation) => generation.content === CHAT_ERROR_MESSAGE);

const removeEmptyAssistantDuplicatesForLastUserTurn = (messages: Message[]): Message[] => {
  const lastUserMessage = [...messages]
    .reverse()
    .find((message) => message.role === MessageRole.USER);
  if (!lastUserMessage || !messages.some(hasChatErrorContent)) return messages;

  const userTurnIds = buildUserTurnIdSet(messages, lastUserMessage);

  return messages.filter((message) => {
    if (!isAssistantMessageEmpty(message)) return true;
    if (!assistantBelongsToUserTurn(message, userTurnIds)) return true;
    return false;
  });
};

const withChatErrorMessage = (conversation: ConversationState): Message[] => {
  const messages = conversation.messages || [];
  const streamingIds = new Set([
    conversation.streamingAssistantMessageId,
    ...(conversation.streamingAssistantMessageIds || []),
  ].filter(Boolean));

  let targetAssistantIndex = messages.findIndex((message) => {
    if (message.role !== MessageRole.ASSISTANT) return false;
    if (streamingIds.size > 0) {
      if (streamingIds.has(message.recordId)) return true;
      return message.generations?.some((generation) => streamingIds.has(generation.id));
    }
    return message.generations?.some((generation) => generation.isThisGenerationOnGoing);
  });

  if (targetAssistantIndex === -1) {
    const lastUserMessage = [...messages]
      .reverse()
      .find((message) => message.role === MessageRole.USER);
    if (lastUserMessage) {
      const userTurnIds = buildUserTurnIdSet(messages, lastUserMessage);
      targetAssistantIndex = messages.findIndex(
        (message) =>
          message.role === MessageRole.ASSISTANT &&
          assistantBelongsToUserTurn(message, userTurnIds) &&
          isAssistantMessageEmpty(message),
      );
    }
  }

  if (targetAssistantIndex !== -1) {
    const updatedMessages = messages.map((message, index) => {
      if (index !== targetAssistantIndex) return message;

      const targetGenerationId =
        message.generations?.find((generation) => streamingIds.has(generation.id))?.id ||
        conversation.streamingAssistantMessageId ||
        message.generations?.[0]?.id;
      const generations = message.generations?.length
        ? message.generations.map((generation) => ({
            ...generation,
            content: generation.id === targetGenerationId ? CHAT_ERROR_MESSAGE : generation.content,
            isThinking: false,
            isAnalyzing: false,
            isThisGenerationOnGoing: false,
          }))
        : createChatErrorAssistantMessage(message.userRecordId).generations;

      return {
        ...message,
        generations,
      };
    });

    return removeEmptyAssistantDuplicatesForLastUserTurn(updatedMessages);
  }

  const existingErrorIndex = messages.findIndex((message) => hasChatErrorContent(message));

  if (existingErrorIndex !== -1) {
    return removeEmptyAssistantDuplicatesForLastUserTurn(messages);
  }

  return removeEmptyAssistantDuplicatesForLastUserTurn([
    ...messages,
    createChatErrorAssistantMessage(getLastUserGenerationId(messages)),
  ]);
};

export const useChatStore = create<ChatStoreState>((set, get) => ({
  pendingStreamingNotifications: {},
  enqueuePendingStreamingNotification: (conversationId: string, notification: any) => {
    set((state) => ({
      pendingStreamingNotifications: {
        ...state.pendingStreamingNotifications,
        [conversationId]: [
          ...(state.pendingStreamingNotifications[conversationId] || []),
          notification,
        ],
      },
    }));
  },
  flushPendingStreamingNotifications: (conversationId: string, config: ConfigurationSettings) => {
    const pending = get().pendingStreamingNotifications[conversationId];
    if (!pending?.length) return;
    set((state) => {
      const { [conversationId]: _removed, ...rest } = state.pendingStreamingNotifications;
      return { pendingStreamingNotifications: rest };
    });
    get().handleStreamingMessageBatch(pending, config);
  },
  hasGroupChatParticipantsArray: {},
  isTitleLoading: false,
  setIsTitleLoading: (value: boolean) => set({ isTitleLoading: value }),
  messageQueue: [],
  isProcessingMessageQueue: false,
  isIncognito: false,
  setIsIncognitoChat: (value: boolean) => {
    set({ isIncognito: value });
  },
  shareTokens: {},
  setConversationShareToken: (conversationId: string, token: string) => {
    set((state) => ({
      shareTokens: {
        ...state.shareTokens,
        [conversationId]: token,
      },
    }));
  },
  getConversationShareToken: (conversationId: string) => {
    return get().shareTokens[conversationId];
  },
  setIsProcessingMessageQueue: (value: boolean) => {
    set({ isProcessingMessageQueue: value });
  },
  addToMessageQueue: (item: MessageQueueItem) => {
    set((state) => ({
      messageQueue: [...state.messageQueue, item],
    }));
  },
  removeFromMessageQueue: (key: string) => {
    set((state) => {
      const updatedQueue = state.messageQueue.filter((item) => item.key !== key);
      return { messageQueue: updatedQueue };
    });
  },
  clearMessageQueue: () => {
    set({ messageQueue: [] });
  },
  processNextMessageFromQueue: async (conversationId: string, config: ConfigurationSettings) => {
    const state = get();

    // Don't process if already processing or queue is empty
    if (state.isProcessingMessageQueue || state.messageQueue.length === 0) {
      return;
    }

    // Get the first message from queue (FIFO)
    const nextMessage = state.messageQueue[0];
    if (!nextMessage) {
      return;
    }

    // Mark as processing
    get().setIsProcessingMessageQueue(true);

    try {
      // Extract message data
      const {
        key: dummyUserMessageId,
        content: messageBody,
        attachmentsMetadata,
        parentIds = [],
        mentions,
        isRegenerate = false,
        imageGeneration = false,
        integrationSources,
        searchOnline = false,
        selectedTools,
        selectedSkillIds,
      } = nextMessage;

      // Make HTTP call
      const response: any = await getGroupChatStream(
        messageBody,
        config,
        conversationId,
        attachmentsMetadata,
        parentIds,
        mentions,
        isRegenerate,
        imageGeneration,
        integrationSources,
        searchOnline,
        selectedTools,
        selectedSkillIds
      );

      // console.log('response in processNextMessageFromQueue', response);

      if (response?.id) {
        set((state) => {
          const current = state.conversations[conversationId];
          const newId = response?.id;

          if (!current || !newId || !dummyUserMessageId) return state;

          const messages = current.messages;

          // Check if dummy exists anywhere
          const dummyFound = messages.some(
            (m) =>
              m.recordId === dummyUserMessageId ||
              m.userRecordId === dummyUserMessageId ||
              m.generations?.some((g) => g.id === dummyUserMessageId)
          );

          if (!dummyFound) return state;

          const finalMessages = messages.map((msg) => {
            const isDummyUserMsg = msg.recordId === dummyUserMessageId;
            const isDummyAssistantLink =
              msg.role === MessageRole.ASSISTANT &&
              msg.userRecordId === dummyUserMessageId;

            // Update dummy USER message → replace ID
            if (isDummyUserMsg) {
              return {
                ...msg,
                recordId: newId,
                deliveryStatus: MessageDeliveryStatus.DELIVERED,
                generations: msg.generations?.map((g) =>
                  g.id === dummyUserMessageId ? { ...g, id: newId } : g
                ),
              };
            }

            if (isDummyAssistantLink) {
              return {
                ...msg,
                userRecordId: newId,

              };
            }

            return msg;
          });

          return {
            conversations: {
              ...state.conversations,
              [conversationId]: {
                ...current,
                messages: finalMessages,
              },
            },
          };
        });
      }

      // Remove message from queue after response
      // get().removeFromMessageQueue(dummyUserMessageId);

      // Mark as not processing
      // get().setIsProcessingMessageQueue(false);

      // // Process next message from queue
      // get().processNextMessageFromQueue(conversationId, config);

    } catch (error) {
      clearAssistantStreamTimeout(conversationId);
      get().setChatErrorMessage(conversationId);

      // Mark as not processing on error
      get().setIsProcessingMessageQueue(false);

      // Remove message from queue on error
      get().removeFromMessageQueue(nextMessage.key);

      // Try to process next message
      get().processNextMessageFromQueue(conversationId, config);

      throw error;
    }
  },
  setHasGroupChatParticipantsArray: (conversationId: string, value: IamData[]) => {
    set((state) => {
      const isActiveConversation = state.activeConversationId === conversationId;

      return {
        hasGroupChatParticipantsArray: {
          ...state.hasGroupChatParticipantsArray,
          [conversationId]: value,
        },
        ...(isActiveConversation && {
          hasParticipant: value.length > 1,
          hasAddedAssistantAsParticipant: value.length <= 1,
          isLoadingParticipants: false,
          hasParticipantsArray: value,
        }),
        conversations: {
          ...state.conversations,
          [conversationId]: {
            ...(state.conversations[conversationId] || initialConversationState(conversationId)),
            participants: value,
            isGroupChat: value.length > 1,
            modelName:
              state.conversations[conversationId]?.modelName ||
              useModelStore.getState().selectedModel?.id ||
              'auto',
          },
        },
      };
    });

    if (get().activeConversationId === conversationId) {
      get().syncGroupChatTitleFromParticipants(conversationId, value);
    }
  },
  activeConversationId: null,
  activeAudioMessageId: null,
  conversations: {},
  invitedUsersData: [],
  setInvitedUsersData: (users) => set({ invitedUsersData: users }),
  sidebarConversationsList: [],
  searchConversationsList: [],
  searchConversationsTotalCount: 0,
  totalConversationsCount: 0,
  totalIndividualConversationsCount: 0,
  totalGroupConversationsCount: 0,
  sidebarProjectsList: [],
  setSidebarProjectsList: (projects) => set({ sidebarProjectsList: projects }),
  removeProjectFromSidebar: (projectId) =>
    set((state) => ({
      sidebarProjectsList: state.sidebarProjectsList.filter((project) => project.id !== projectId),
    })),
  updateProjectItems: (projectId, conversations) => set((state) => ({
    sidebarProjectsList: state.sidebarProjectsList.map((project) =>
      project.id === projectId ? { ...project, items: conversations } : project
    ),
  })),
  isSidebarLoading: false,
  isSidebarIndividualLoading: false,
  isSidebarGroupLoading: false,
  sidebarError: null,
  isNewChat: false,
  hasParticipant: false,
  hasParticipantsArray: [],
  isLoadingParticipants: false,
  hasAddedAssistantAsParticipant: false,
  setHasAddedAssistantAsParticipant: (value: boolean) => set({ hasAddedAssistantAsParticipant: value }),
  isGroupChat: false,
  setGroupChat: (value: boolean) => set({ isGroupChat: value }),
  conversationTitleRefetched: false,
  setConversationTitleRefetched: (value: boolean) => set({ conversationTitleRefetched: value }),
  replyMessage: null,
  setReplyMessage: (value: ReplyMessageData | null) => set({ replyMessage: value }),
  commentMessage: null,
  setCommentMessage: (value: Message | null) => set({ commentMessage: value }),
  itemIdFilePathMap: new Map<string, string>(),
  headerTitle: "",
  setHeaderTitle: (value: string) => set({ headerTitle: value }),


  fetchAttachmentMetadata: async (attachmentIds: string[], sender: MessageRole, config: ConfigurationSettings): Promise<FileMetadata[]> => {
    if (!attachmentIds || attachmentIds.length === 0) return [];

    const fileMetadata: FileMetadata[] = [];

    for (const fileId of attachmentIds) {
      try {
        const response = await getFile(fileId, config || {} as ConfigurationSettings);
        if (response.isSuccess) {
          // Extract filename and extension from the response
          const fileName = response.name || `file_${fileId}`;
          const extension = fileName.includes('.') ? fileName.split('.').pop()?.toLowerCase() || 'unknown' : 'unknown';

          // Use itemId from response as fileId, fallback to provided fileId
          const responseFileId = response.itemId || fileId;
          const createdDate = response.createDate || new Date().toISOString();
          const lastUpdatedDate = response.createDate || new Date().toISOString(); // Use createDate if lastUpdatedDate not available
          const size = response.sizeInBytes || 0;

          fileMetadata.push({
            fileId: responseFileId,
            fileName: fileName,
            extension: extension,
            createdDate: createdDate,
            lastUpdatedDate: lastUpdatedDate,
            size: size,
            sender: sender,
          });
        } else {

          fileMetadata.push({
            fileId: fileId,
            fileName: `file_${fileId}`,
            extension: 'unknown',
            createdDate: new Date().toISOString(),
            lastUpdatedDate: new Date().toISOString(),
            size: 0,
            sender: sender,
          });
        }
      } catch (error) {

        fileMetadata.push({
          fileId: fileId,
          fileName: `file_${fileId}`,
          extension: 'unknown',
          createdDate: new Date().toISOString(),
          lastUpdatedDate: new Date().toISOString(),
          size: 0,
          sender: sender,
        });
      }
    }

    return fileMetadata;
  },
  sendSSEMessage: async (messageBody: string, config: ConfigurationSettings, conversationId: string) => {
    let response;
    try {
      response = await getSSEStream(messageBody, config);
    } catch (error) {
      clearAssistantStreamTimeout(conversationId);
      trackEvent(EVENTS.ERROR(error, {
        endpoint: config.paths.seliseAi.chatStream
      }));
      set((state) => ({
        conversations: {
          ...state.conversations,
          [conversationId]: {
            ...(state.conversations[conversationId] || initialConversationState(conversationId)),
            messages: withChatErrorMessage(
              state.conversations[conversationId] || initialConversationState(conversationId)
            ),
            streamingAssistantMessageId: null,
            streamingAssistantMessageIds: [],
            assistantResponseRemaining: 0,
            isCurrentResponseGenerating: false,
            isCurrentMessageLoading: false,
            isAnalyzing: false,
            error: CHAT_ERROR_MESSAGE_KEY
          },
        },
      }));
      return;
    }

    const reader = response?.body?.getReader();
    const decoder = new TextDecoder('utf-8');
    let buffer = "";

    while (reader) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      const parts = buffer.split("\n\n");
      buffer = parts.pop() ?? "";

      for (const part of parts) {
        const lines = part.split('\n');
        for (let line of lines) {
          line = line.trim();
          if (line.startsWith('data: ')) {
            const payload = line.replace('data: ', '').trim();
            if (payload === '[DONE]') return;

            try {
              const parsedResponse = JSON.parse(payload);
              console.log('parsedResponse in sendSSEMessage', parsedResponse);
              get()._handleWebSocketMessage(parsedResponse, config);
            } catch (error) {
              console.error('Partial or invalid JSON, waiting for next chunk:', payload);
            }
          }
        }
      }
    }
  },

  sendGroupChatStream: async (
    messageBody: string,
    config: ConfigurationSettings,
    conversationId: string,
    attachmentsMetadata?: FileMetadata[],
    replyMessage?: ReplyMessageData | null,
    mentions?: string[],
    isRegenerate?: boolean,
    originalRegenerationMessageId?: string,
    imageGeneration?: boolean,
    integrationSources?: IntegrationSource[],
    searchOnline?: boolean,
    retryMessageId?: string, // ID of message to retry (prevents creating new message)
    dummyUserMessageId?: string, // Dummy ID generated for the user message that needs to be replaced
    selectedTools?: string[],
    selectedSkillIds?: string[],
    a2uiMetadata?: Record<string, unknown>,
  ) => {

    // Keep ordinary chat connectivity handling unchanged; picker failures must reject.
    if (!a2uiMetadata) {
      const connected = await useCollaborationSocketStore.getState().ensureConnected();
      if (!connected) {
        clearAssistantStreamTimeout(conversationId);
        get().setChatErrorMessage(conversationId);
        return;
      }
    }

    let parentIds = replyMessage?.generation ? [replyMessage?.generation.id] : [];
    if (isRegenerate && originalRegenerationMessageId) {
      parentIds = [originalRegenerationMessageId ?? ''];
    }
    let response: any;
    try {
      if (a2uiMetadata) {
        const connected = await useCollaborationSocketStore.getState().ensureConnected();
        if (!connected) throw new Error("Connection unavailable");
      }
      response = await getGroupChatStream(
        messageBody,
        config,
        conversationId,
        attachmentsMetadata,
        parentIds,
        mentions,
        isRegenerate,
        imageGeneration,
        integrationSources,
        searchOnline,
        selectedTools,
        selectedSkillIds,
        a2uiMetadata
      );
      set((state) => {
        const current = state.conversations[conversationId];
        if (!current) return state;

        const newId = response?.id;
        let messages = current.messages;
        if (newId && dummyUserMessageId) {
          const dummyFound = messages.some(
            (m) =>
              m.recordId === dummyUserMessageId ||
              m.userRecordId === dummyUserMessageId
          );
          if (dummyFound) {
            messages = messages.map((msg) => {
              const isDummyUserMsg = msg.recordId === dummyUserMessageId;
              const isDummyAssistantLink =
                msg.role === MessageRole.ASSISTANT &&
                msg.userRecordId === dummyUserMessageId;

              if (isDummyUserMsg) {
                return {
                  ...msg,
                  recordId: newId,
                  deliveryStatus: MessageDeliveryStatus.DELIVERED,
                  generations: msg.generations?.map((g) =>
                    g.id === dummyUserMessageId ? { ...g, id: newId } : g
                  ),
                };
              }

              if (isDummyAssistantLink) {
                return {
                  ...msg,
                  userRecordId: newId,
                };
              }

              return msg;
            });
          }
        }

        const hasServerUserIdForCancel =
          !!newId ||
          (!!dummyUserMessageId &&
            messages.some(
              (message) =>
                isUserMessageRole(message.role) &&
                message.generations.some(
                  (generation) =>
                    generation.id === dummyUserMessageId &&
                    message.recordId !== dummyUserMessageId
                )
            ));

        return {
          conversations: {
            ...state.conversations,
            [conversationId]: {
              ...current,
              messages,
              canCancelGeneration: hasServerUserIdForCancel,
            },
          },
        };
      });


    } catch (error) {
      clearAssistantStreamTimeout(conversationId);

      trackEvent(EVENTS.ERROR(error, {
        endpoint: config.paths.seliseAi.collaboration
      }));

      // Reset streaming locks so UI (e.g. model selector) is not stuck "streaming" after HTTP failure (403, etc.).
      const streamingReset = {
        streamingAssistantMessageId: null as string | null,
        streamingAssistantMessageIds: [] as string[],
        assistantResponseRemaining: 0,
        activeStreamsMap: new Map<string, boolean>(),
        isAnalyzing: false,
        canCancelGeneration: false,
      };

      if (a2uiMetadata) {
        // The picker owns retry. Remove only this optimistic submission (and the
        // assistant placeholder 1:1 chats create for it), avoiding an ordinary-text
        // Retry button and duplicate error row alongside its toast.
        const optimisticId = retryMessageId ?? dummyUserMessageId;
        set((state) => {
          const conv = state.conversations[conversationId];
          if (!conv) return state;
          return {
            conversations: {
              ...state.conversations,
              [conversationId]: {
                ...conv,
                ...streamingReset,
                messages: conv.messages.filter(
                  (message) => message.recordId !== optimisticId && message.userRecordId !== optimisticId
                ),
                isCurrentResponseGenerating: false,
                isCurrentMessageLoading: false,
                error: null,
              },
            },
          };
        });
        throw error;
      }

      // Mark the last user message as failed for group chats
      set((state) => {
        const conv = state.conversations[conversationId];
        if (!conv) return state;
        if (!conv || !conv.messages) return {
          conversations: {
            ...state.conversations,
            [conversationId]: {
              ...conv,
              ...streamingReset,
              isCurrentResponseGenerating: false,
              isCurrentMessageLoading: false,
              error: CHAT_ERROR_MESSAGE_KEY
            },
          },
        };
        const messages = [...conv.messages];


        // Mark the message as failed - use retryMessageId if provided, otherwise find last PENDING message
        const updatedMessages = messages.map((msg) => {
          // If retryMessageId is provided, update that specific message
          if (retryMessageId && msg.recordId === retryMessageId) {
            return {
              ...msg,
              deliveryStatus: MessageDeliveryStatus.FAILED,
            };
          }
          // Otherwise, find the last USER_CHAT message with PENDING status
          if (!retryMessageId && msg.role === MessageRole.USER_CHAT &&
            (msg.deliveryStatus === MessageDeliveryStatus.PENDING || !msg.deliveryStatus)) {
            // Check if this is the last PENDING message
            const userChatMessages = messages.filter(m => m.role === MessageRole.USER_CHAT);
            const lastPendingMessage = userChatMessages[userChatMessages.length - 1];
            if (msg.recordId === lastPendingMessage?.recordId) {
              return {
                ...msg,
                deliveryStatus: MessageDeliveryStatus.FAILED,
              };
            }
          }
          return msg;
        });

        return {
          conversations: {
            ...state.conversations,
            [conversationId]: {
              ...conv,
              ...streamingReset,
              messages: withChatErrorMessage({
                ...conv,
                messages: updatedMessages,
              }),
              isCurrentResponseGenerating: false,
              isCurrentMessageLoading: false,
              error: CHAT_ERROR_MESSAGE_KEY
            },
          },
        };
      });
      return;
    }

  },

  sendMessageToProject: async (
    content: string,
    config: ConfigurationSettings,
    projectId: string,
    newConversationId?: string,
    attachments?: Attachment[],
    isFeatureSelected?: Record<ButtonIds, boolean>,
    sources?: IntegrationSource[],
    selectedTools?: string[],
  ) => {
    // console.log('isGroupChatParticipants in sendMessageToProject', isGroupChatParticipants);
    const { activeConversationId, replyMessage } = get();
    const selectedModel = useModelStore.getState().selectedModel;
    const newFileIds = attachments?.map((item) => item.fileId) ?? [];
    let updatedAllFileIds = newFileIds;

    // Extract parentMessage data from replyMessage
    const parentMessage = replyMessage?.generation && replyMessage?.repliedUser ? {
      senderName: replyMessage.repliedUser.firstName + (replyMessage.repliedUser.lastName ? ` ${replyMessage.repliedUser.lastName}` : ''),
      content: replyMessage.generation.content,
      time: messageGenerationTimeMs(replyMessage.generation)
    } : undefined;

    const userGenerationId = uuidv4();
    const userSendShowTime = formatShowMessageTimeForSend();
    const userMessage: Message = {
      recordId: userGenerationId,
      role: MessageRole.USER,
      senderId: useCommonStore.getState().user?.id,
      generations: [
        {
          id: userGenerationId,
          content: content,
          createdDate: Date.now(),
          showMessageTime: userSendShowTime,
          attachments: attachments,
          ...(parentMessage && { parentMessage }),
        },
      ],
    };
    const assistantMessageId = uuidv4();
    let conversationIdToUse = activeConversationId;

    if (!conversationIdToUse) {
      conversationIdToUse = newConversationId ?? uuidv4();
      const newConvState = initialConversationState(conversationIdToUse, ""); // Temporary title
      newConvState.messages = [userMessage];
      newConvState.isCurrentMessageLoading = true;
      newConvState.isCurrentResponseGenerating = true;
      newConvState.modelName = selectedModel?.id;
      newConvState.allFileIds = newFileIds;
      newConvState.canCancelGeneration = false;
      newConvState.toolsState = isFeatureSelected ?? initialFeaturesState;

      set((state) => ({
        activeConversationId: conversationIdToUse,
        conversations: {
          ...state.conversations,
          [conversationIdToUse!]: newConvState,
        },
      }));

      const isoDate = new Date().toISOString();
      const newSidebarEntry: ProjectItem = {
        ProjectId: projectId,
        ConversationId: conversationIdToUse!,
        ConversationTitle: newConvState.title,
        CreatedBy: '',
        CreatedDate: isoDate,
        LastUpdatedDate: isoDate,
        TenantId: '',
        Conversations: [
          {
            id: conversationIdToUse!,
            title: newConvState.title,
            createdDate: isoDate,
            lastUpdatedDate: isoDate,
            type: MessageRole.USER,
            content: userMessage.generations[0].content
          }
        ],
      };
      get().appendProjectItemToSidebarProjectsList(newSidebarEntry, projectId);

      // Recent Chats reads sidebarConversationsList, and the conversations list API returns
      // project chats too — without this row the chat only shows under its project until refetch.
      get().appendToSidebarConversationsList({
        ConversationId: conversationIdToUse!,
        ConversationTitle: newConvState.title,
        Conversations: [],
        CreatedBy: useCommonStore.getState().user?.id ?? '',
        CreatedDate: isoDate,
        LastUpdatedDate: isoDate,
        TenantId: '',
        IsGroup: false,
        ProjectId: projectId,
        color: get().sidebarProjectsList.find((project) => project.id === projectId)?.color,
      });
    } else {
      get()._addMessageToActiveConversation(userMessage);

      // if (conversationIdToUse) {
      //   get()._moveConversationToToday(conversationIdToUse);
      // }

      const currentConv = get().conversations[conversationIdToUse!];
      const prevAllFileIds = currentConv.allFileIds ?? [];
      updatedAllFileIds = [...prevAllFileIds, ...newFileIds];

      set((state) => ({
        conversations: {
          ...state.conversations,
          [conversationIdToUse!]: {
            ...state.conversations[conversationIdToUse!],
            isCurrentMessageLoading: true,
            isCurrentResponseGenerating: true,
            modelName: selectedModel?.id,
            allFileIds: updatedAllFileIds,
            canCancelGeneration: false,
            toolsState: isFeatureSelected ?? initialFeaturesState,
            isAnalyzing: !!attachments && attachments.length > 0, // Set analyzing to true when files are uploaded

          },
        },
      }));
    }

    const newAssistantMessage: Message = {
      recordId: assistantMessageId,
      userRecordId: userMessage.generations[0].id,
      role: MessageRole.ASSISTANT,
      toolsStatus: isFeatureSelected?.[ButtonIds.SEARCH]
        ? {
          [Tools.SEARCH]: true,
          [Tools.CREATE_IMAGE]: false,
          [Tools.VOICE]: false,
        }
        : undefined,
      generations: [
        {
          id: assistantMessageId,
          content: '',
          createdDate: Date.now(),
          showMessageTime: formatShowMessageTimeForSend(),
          HasFileGeneratedResponse: false,
          isThinking: !!selectedModel?.isThinkingModel && !selectedModel?.isHarmonicModel && !selectedModel?.hasDynamicThinking,
          isAnalyzing: !!attachments && attachments.length > 0, // Set analyzing to true when files are uploaded
          usedModelId: selectedModel?.id,
          isThisGenerationOnGoing: true,
        },
      ],
    };
    get()._addMessageToActiveConversation(newAssistantMessage, userMessage.generations[0].id);

    trackEvent(EVENTS.MESSAGE_SENT(conversationIdToUse, userMessage?.generations[0].content.length ?? 0));
    if (isFeatureSelected?.[ButtonIds.SEARCH]) {
      trackEvent(EVENTS.FEATURE_USED(FeatureName.SEARCH));
    }
    if (isFeatureSelected?.[ButtonIds.CREATE_IMAGE]) {
      trackEvent(EVENTS.FEATURE_USED(FeatureName.CREATE_IMAGE));
    }

    // if (isGroupChatParticipants) {
    //   const replyMessage = get().replyMessage;


    const nowIso = new Date().toISOString();
    const attachmentsMetadata: FileMetadata[] = (userMessage.generations[0].attachments ?? []).map((att) => ({
      fileId: att.fileId,
      fileName: att.fileName,
      extension: att.extension,
      sender: att.sender,
      size: att.size ?? 0,
      createdDate: nowIso,
      lastUpdatedDate: nowIso,
    }));
    // Match normal solo chat: invite uses DEFAULT_MAIN_CHAT_AGENT_ID; stream mentions stay empty.
    const mentions: string[] = [];
    const imageGeneration = isFeatureSelected?.[ButtonIds.CREATE_IMAGE] ?? false;
    const searchOnline = isFeatureSelected?.[ButtonIds.SEARCH] ?? false;
    const integrationSources = sources ? sources : [];

    get().updateProjectConversation(conversationIdToUse, config);
    scheduleAssistantStreamTimeout(
      conversationIdToUse!,
      get().setChatErrorMessage,
      (id) => get().conversations[id]
    );
    get().sendGroupChatStream(
      content,
      config,
      conversationIdToUse,
      attachmentsMetadata,
      replyMessage,
      mentions,
      false,
      undefined,
      imageGeneration,
      integrationSources ?? [],
      searchOnline,
      undefined,
      userGenerationId,
      selectedTools ?? [],
    );

    // get().sendGroupChatStream(content, config, conversationIdToUse, userMessage.generations[0].attachments as unknown as FileMetadata[], replyMessage);
    return;
    // } else {
    //   get().sendSSEMessage(
    //     JSON.stringify({
    //       question: userMessage.generations[0].content,
    //       conversation: [],
    //       file_ids: isFeatureSelected?.[ButtonIds.SEARCH] ? [] : updatedAllFileIds,
    //       filter_key_value_pair: [],
    //       model_name: selectedModel?.id,
    //       is_regenerate: false,
    //       conversation_id: conversationIdToUse,
    //       curr_file_ids: userMessage.generations[0].attachments?.map((file) => file.fileId),
    //       search_online_v2: isFeatureSelected?.[ButtonIds.SEARCH] ?? false,
    //       image_generation: isFeatureSelected?.[ButtonIds.CREATE_IMAGE] ?? false,
    //       record_id: recordId,
    //       msg_id: uuidv4(),
    //       assistant_message_id: assistantMessageId,
    //     }),
    //     config,
    //     conversationIdToUse
    //   );
    // }
  },


  setItemIdFilePathMap: (key: string, value: string) => {
    set((state) => ({
      itemIdFilePathMap: state.itemIdFilePathMap.set(key, value),
    }));
  },
  clearItemIdFilePathMap: () => {
    set(() => ({
      itemIdFilePathMap: new Map<string, string>(),
    }));
  },

  _addMessageToActiveConversation: (message, recordIdToUpdate?: string, conversationId?: string) => {
    const targetConversationId = conversationId ?? get().activeConversationId;
    if (!targetConversationId) return;

    set((state) => {
      const currentConv = state.conversations[targetConversationId];
      if (!currentConv) return {};
 
      const newMessages = currentConv.messages?.length ? [...currentConv.messages] : [];
      // console.log('newMessages in _addMessageToActiveConversation', newMessages)
 
      // Check for duplicate system messages by recordId
      if (message.role === MessageRole.CONVERSATION_TITLE_UPDATED) {
        const existingSystemMessage = newMessages.find(
          (m) => m.role === MessageRole.CONVERSATION_TITLE_UPDATED && m.recordId === message.recordId
        );
        if (existingSystemMessage) {
          return state;
        }
      }
      if (message.role === MessageRole.CONVERSATION_CREATED) {
        const existingSystemMessage = newMessages.find(
          (m) => m.role === MessageRole.CONVERSATION_CREATED && m.recordId === message.recordId
        );
        if (existingSystemMessage) {
          return state;
        }
        // Add CONVERSATION_CREATED message immediately after duplicate check
        newMessages.push(message);
      }
      if (message.role === MessageRole.ADD_PARTICIPANT) {
        const existingSystemMessage = newMessages.find(
          (m) => m.role === MessageRole.ADD_PARTICIPANT && m.recordId === message.recordId
        );
        if (existingSystemMessage) {
          return state;
        }
 
      }
 
      // Skip processing if message was already added (CONVERSATION_CREATED)
   
 
      if (message.role === MessageRole.ASSISTANT && recordIdToUpdate) {
        const targetUserMessage = findUserMessageForLinkedId(newMessages, recordIdToUpdate);
        const targetUserMessageRecordId = targetUserMessage?.recordId;

        const userTurnIds = buildUserTurnIdSet(newMessages, {
          role: MessageRole.ASSISTANT,
          recordId: recordIdToUpdate,
          userRecordId: recordIdToUpdate,
          generations: [{ id: recordIdToUpdate, content: '' }],
        });
        const existingIndex = newMessages.findIndex(
          (m) => {
            if (m.role !== MessageRole.ASSISTANT) return false;
            if (targetUserMessageRecordId) {
              const assistantUserMessageRecordId = resolveUserMessageRecordIdForAssistant(
                newMessages,
                m
              );
              if (assistantUserMessageRecordId !== targetUserMessageRecordId) {
                return false;
              }
            }
            return (
              assistantBelongsToUserTurn(m, userTurnIds) ||
              message.generations.some((msgGen) =>
                m.generations.some((g) => g.id === msgGen.id)
              )
            );
          }
        );
        if (existingIndex !== -1) {
          const prevMessage = newMessages[existingIndex];
          const totalGenerations = prevMessage.generations.length + message.generations.length;
          const updatedMessage: Message = {
            ...prevMessage,
            generations: [...prevMessage.generations, ...message.generations],
            activeGenerationIndex: totalGenerations - 1,
          };
          newMessages[existingIndex] = updatedMessage;
        } else {
          // Check for duplicate by recordId or generation ID before adding
          const isDuplicate = newMessages.some((m) => {
            if (m.recordId === message.recordId) return true;
            if (message.generations.length > 0) {
              return m.generations.some(gen =>
                message.generations.some(msgGen => gen.id === msgGen.id)
              );
            }
            return false;
          });
          if (!isDuplicate) {
            newMessages.push(message);
          }
        }
      } else {
        // Check for duplicate by recordId and generation IDs before adding
        // This prevents duplicates when multiple users send messages simultaneously
        const isDuplicate = newMessages.some((m) => {
          if (m.recordId === message.recordId) return true;
          // Also check generation IDs to catch duplicates from different sources
          if (message.generations && message.generations.length > 0 && m.generations && m.generations.length > 0) {
            return message.generations.some(msgGen =>
              m.generations.some(gen => gen.id === msgGen.id)
            );
          }
          return false;
        });
        if (!isDuplicate) {
          if (isUserMessageRole(message.role)) {
            const expired = expireActiveApprovals(newMessages);
            newMessages.splice(0, newMessages.length, ...expired);
          }
          newMessages.push(message);
        }
      }

      return {
        conversations: {
          ...state.conversations,
          [targetConversationId]: {
            ...currentConv,
            messages: insertUserBeforeStreamingAssistant(
              consolidateAssistantMessages(newMessages)
            ),
          },
        },
      };
    });
  },
 
 
 
 

  updateProjectNameInStore: (projectId: string, newName: string, color?: string) => {
    set((state) => {
      const updatedProjectsList = state.sidebarProjectsList.map((project) =>
        project.id === projectId
          ? {
            ...project,
            name: newName,
            ...(color !== undefined ? { color } : {}),
          }
          : project
      );
      return { sidebarProjectsList: updatedProjectsList };
    });
  },

  fetchAndUpdateProjectAllConversations: async (configurationSettings: ConfigurationSettings, projectId: string) => {
    const { sidebarProjectsList } = get();
    const project = sidebarProjectsList.find((p) => p.id === projectId);
    if (!project) return;

    try {
      const response = await fetchProjectConversations(configurationSettings, projectId);
      const updatedProject = {
        ...project,
        items: response,
      };

      set((state) => {
        const updatedProjectsList = state.sidebarProjectsList.map((p) =>
          p.id === projectId ? updatedProject : p
        );
        return { sidebarProjectsList: updatedProjectsList };
      });
    } catch (error) {
      console.log(error);
      throw error;
    }
  },

  syncGroupChatTitleFromParticipants: (conversationId: string, participants: IamData[]) => {
    if (!conversationId || participants.length <= 1) {
      return;
    }

    const conversation = get().conversations[conversationId];
    if (conversation?.hasManualTitle) {
      return;
    }

    const currentUser = useCommonStore.getState().user;
    const title = deriveGroupChatTitleFromParticipants(participants, currentUser);
    get().updateConversationTitleInStore(conversationId, title);
  },

  setAgentAvailability: (conversationId: string, unavailable: boolean) => {
    set((state) => {
      const conversation = state.conversations[conversationId];
      if (!conversation || !!conversation.agentUnavailable === unavailable) return state;
      return {
        conversations: {
          ...state.conversations,
          [conversationId]: { ...conversation, agentUnavailable: unavailable },
        },
      };
    });
  },
  updateConversationTitleInStore: (
    conversationId: string,
    newTitle: string,
    options?: { isManual?: boolean }
  ) => {
    set((state) => {
      const existingConversation = state.conversations[conversationId];
      const updatedConversations = {
        ...state.conversations,
        [conversationId]: {
          ...(existingConversation ?? initialConversationState(conversationId, newTitle)),
          title: newTitle,
          ...(options?.isManual ? { hasManualTitle: true } : {}),
        },
      };

      const updatedSidebarList = state.sidebarConversationsList.map((item) => {
        if (item.ConversationId === conversationId) {
          return {
            ...item,
            ConversationTitle: newTitle,
          };
        }
        return item;
      });

      const updatedSearchList = state.searchConversationsList.map((item) => {
        if (item.ConversationId === conversationId) {
          return {
            ...item,
            ConversationTitle: newTitle,
          };
        }
        return item;
      });

      const updatedProjectsList = state.sidebarProjectsList.map((project) => ({
        ...project,
        items: project.items?.map((conv) => {
          if (conv.ConversationId === conversationId) {
            return { ...conv, ConversationTitle: newTitle };
          }
          return conv;
        }),
      }));

      const isHeaderTitleUpdateNeeded = state.activeConversationId === conversationId;

      return {
        conversations: updatedConversations,
        sidebarConversationsList: updatedSidebarList,
        searchConversationsList: updatedSearchList,
        sidebarProjectsList: updatedProjectsList,
        headerTitle: isHeaderTitleUpdateNeeded ? newTitle : state.headerTitle,
      };
    });
  },

  generateConversationTitle: async (conversationId: string, config: ConfigurationSettings) => {
    const conversation = get().conversations[conversationId];
    const sidebarTitle = get().sidebarConversationsList.find(
      (item) => item.ConversationId === conversationId
    )?.ConversationTitle;

    if (
      !shouldAutoGenerateConversationTitle({
        conversation,
        storedTitle: sidebarTitle ?? conversation?.title,
        isIncognito: get().isIncognito,
        hasParticipant: get().hasParticipant,
      })
    ) {
      return;
    }

    const shouldShowTitleLoading = get().activeConversationId === conversationId;

    try {
      if (shouldShowTitleLoading) {
        get().setIsTitleLoading(true);
      }
      const response = await getTitle2(conversationId, config);
      const title = response.conversationTitle;
      if (get().activeConversationId === conversationId) {
        get().setHeaderTitle(title as string);
      }
      get().updateConversationTitleInStore(conversationId, title as string);
      if (shouldShowTitleLoading) {
        get().setIsTitleLoading(false);
      }
    } catch (error) {
      console.log(error);
      if (shouldShowTitleLoading) {
        get().setIsTitleLoading(false);
      }
      trackEvent(EVENTS.ERROR(error, {
        endpoint: `${config.paths.seliseAi.collaborationConversation}/${conversationId}/title/generate`,
      }));
    }
  },

  syncConversationTitleFromApi: (
    conversationId: string,
    apiTitle: string | null | undefined,
    config: ConfigurationSettings
  ) => {
    const strippedTitle = stripConversationDisplayQuotes(apiTitle ?? '');

    if (isMeaningfulConversationTitle(strippedTitle)) {
      get().updateConversationTitleInStore(conversationId, strippedTitle);
      return;
    }

    const conversation = get().conversations[conversationId];
    const sidebarTitle = get().sidebarConversationsList.find(
      (item) => item.ConversationId === conversationId
    )?.ConversationTitle;

    if (
      shouldAutoGenerateConversationTitle({
        conversation,
        storedTitle: sidebarTitle ?? conversation?.title ?? apiTitle,
        isIncognito: get().isIncognito,
        hasParticipant: get().hasParticipant,
      })
    ) {
      void get().generateConversationTitle(conversationId, config);
    }
  },


  updateProjectConversation: async (conversationId: string, configurationSettings: ConfigurationSettings) => {
    const sidebarProjectsList = get().sidebarProjectsList;
    const project = sidebarProjectsList.find((project) => project.items?.some((item) => item.ConversationId === conversationId))
    if (project) {
      // const payload = {
      //   Id: project.id,
      //   Name: project.name,
      //   Exttra: {},
      //   Conversations: [
      //     ...(project.items?.map((item) => item.ConversationId) || []),
      //     conversationId,
      //   ],
      // };
      const payload = {
        projectId: project.id
      };
      try {
        const baseUrl = `${configurationSettings.paths.seliseAiBaseUrl}`;
        const url = `${baseUrl}${configurationSettings.paths.seliseAi.collaborationConversation}/${conversationId}`;
        await clients.put(url, configurationSettings.blocksKey, JSON.stringify(payload));
      } catch (error) {
        console.log(error);
      }
    }
  },

  setrelatedSearcheQuestionsList: (conversationId: string, relatedSearcheQuestionsList: RelativeSearchQuestion[], messageId?: string) => {
    set((state) => {
      const updatedConversations = { ...state.conversations };
      if (updatedConversations[conversationId]) {
        const messages = updatedConversations[conversationId].messages;
        // Use messageId (recordId) if provided, otherwise fall back to streamingAssistantMessageId
        const targetMessageId = messageId || updatedConversations[conversationId].streamingAssistantMessageId;

        const updatedMessages = messages.map((message) => {
          if (message.role === 'assistant') {
            // Check if this message's recordId matches the target messageId
            const isTargetMessage = message.recordId === targetMessageId;

            return {
              ...message,
              generations: message.generations.map((generation) => {
                // Set relatedSearcheQuestionsList only for the target message
                // Match by message recordId or generation id (for backward compatibility)
                if (isTargetMessage || generation.id === targetMessageId) {
                  return {
                    ...generation,
                    relatedSearcheQuestionsList,
                    isSkeletonShowForQuestions: false,
                  };
                }
                return generation;
              }),
            };
          }
          return message;
        });

        return {
          conversations: {
            ...updatedConversations,
            [conversationId]: {
              ...updatedConversations[conversationId],
              messages: updatedMessages,
              isCurrentResponseGenerating: false,
            },
          },
        };
      }
      return state;
    });
  },

  setrelatedSearcheImageList: (conversationId: string, relatedSearcheImageList: RelativeSearchQuestion[], messageId?: string) => {
    // console.log('relatedSearcheImageList in setrelatedSearcheImageList', relatedSearcheImageList);
    set((state) => {
      const updatedConversations = { ...state.conversations };
      if (updatedConversations[conversationId]) {
        const messages = updatedConversations[conversationId].messages;
        // Use messageId (recordId) if provided, otherwise fall back to streamingAssistantMessageId
        const targetMessageId = messageId || updatedConversations[conversationId].streamingAssistantMessageId;
        // console.log('targetMessageId in setrelatedSearcheImageList', targetMessageId);


        const updatedMessages = messages.map((message) => {
          if (message.role === 'assistant') {
            // Check if this message's recordId matches the target messageId
            const isTargetMessage = message.recordId === targetMessageId;

            return {
              ...message,
              generations: message.generations.map((generation) => {
                // Set relatedSearcheImageList only for the target message
                // Match by message recordId or generation id (for backward compatibility)
                if (isTargetMessage || generation.id === targetMessageId) {
                  return {
                    ...generation,
                    relatedSearcheImageList,
                    isSkeletonShowForImages: false,
                  };
                }
                return generation;
              }),
            };
          }
          return message;
        });

        return {
          conversations: {
            ...updatedConversations,
            [conversationId]: {
              ...updatedConversations[conversationId],
              messages: updatedMessages,
            },
          },
        };
      }
      return state;
    });
  },

  setrelatedSearcheVideoList: (conversationId: string, relatedSearcheVideoList: RelativeSearchQuestion[], messageId?: string) => {
    set((state) => {
      const updatedConversations = { ...state.conversations };
      if (updatedConversations[conversationId]) {
        const messages = updatedConversations[conversationId].messages;
        // Use messageId (recordId) if provided, otherwise fall back to streamingAssistantMessageId
        const targetMessageId = messageId || updatedConversations[conversationId].streamingAssistantMessageId;

        const updatedMessages = messages.map((message) => {
          if (message.role === 'assistant') {
            // Check if this message's recordId matches the target messageId
            const isTargetMessage = message.recordId === targetMessageId;

            return {
              ...message,
              generations: message.generations.map((generation) => {
                // Set relatedSearcheVideoList only for the target message
                // Match by message recordId or generation id (for backward compatibility)
                if (isTargetMessage || generation.id === targetMessageId) {
                  return {
                    ...generation,
                    relatedSearcheVideoList,
                    isSkeletonShowForVideos: false,
                  };
                }
                return generation;
              }),
            };
          }
          return message;
        });

        return {
          conversations: {
            ...updatedConversations,
            [conversationId]: {
              ...updatedConversations[conversationId],
              messages: updatedMessages,
            },
          },
        };
      }
      return state;
    });
  },

  _handleWebSocketMessage: (parsedResponse: any, config: ConfigurationSettings) => {
    let SSEResponse;
    try {
      SSEResponse = WebsocketServices.SSEResponseParser(parsedResponse);
    } catch (error) {
      console.error('Failed to parse websocket stream event:', error, parsedResponse);
      return;
    }
    const receivedConversationId = SSEResponse.conversation_id;

    if (!receivedConversationId || !get().conversations[receivedConversationId]) {
      console.warn('Received WS message but no conversation exist with this ID.');
      return;
    }

    // Any stream event is proof of life: re-arm the watchdog so it measures
    // inactivity, not total turn duration (long multi-tool runs exceed 90s while
    // streaming perfectly fine). Terminal events clear it in their own handlers.
    if (get().conversations[receivedConversationId]?.isCurrentResponseGenerating) {
      scheduleAssistantStreamTimeout(
        receivedConversationId,
        get().setChatErrorMessage,
        (id) => get().conversations[id]
      );
    }

    posthogSSEEvent(SSEResponse.type, { conversationId: receivedConversationId });

    // Reconnect replay: the burst's first frame wipes this message's streamed state, then every
    // replayed frame is reduced exactly like a live one.
    set((state) => {
      const conv = state.conversations[receivedConversationId];
      if (!conv) return state;
      const patch = applyStreamReplayBurst(conv, {
        assistantMessageId: SSEResponse.assistant_message_id,
        userMessageId: SSEResponse.message_sender_id,
        isReplay: SSEResponse.stream_reason === NEW_CONNECTION_WHILE_STREAMING,
        isEndOfStream: SSEResponse.type === SSEResponseTypes.END_OF_STREAM,
      });
      if (!patch) return state;
      return {
        conversations: {
          ...state.conversations,
          [receivedConversationId]: { ...conv, ...patch },
        },
      };
    });

    const updateConversation = (
      updater: (conv: ConversationState) => Partial<ConversationState> | null
    ) => {
      set((state) => {
        const conv = state.conversations[receivedConversationId];
        if (!conv) return state;
        const patch = updater(conv);
        if (!patch) return state;
        return {
          conversations: {
            ...state.conversations,
            [receivedConversationId]: {
              ...conv,
              error: null,
              ...patch,
            },
          },
        };
      });
    };

    const updateConversationMessages = (
      updater: (conv: ConversationState, updatedMessages: Message[]) => Partial<ConversationState>,
      options?: { allowEmptyMessages?: boolean }
    ) => {
      set((state) => {
        const conv = state.conversations[receivedConversationId];
        if (!conv) return state;
        const updatedMessages = getUpdatedMessages(SSEResponse, conv);
        if (!options?.allowEmptyMessages && updatedMessages.length === 0) {
          return state;
        }
        const patch = updater(conv, updatedMessages);
        return {
          conversations: {
            ...state.conversations,
            [receivedConversationId]: {
              ...conv,
              error: null,
              ...patch,
              messages:
                updatedMessages.length > 0
                  ? updatedMessages
                  : patch.messages ?? conv.messages,
            },
          },
        };
      });
    };

    switch (SSEResponse.type) {

      case SSEResponseTypes.PLATFORM_RESOURCE_CHANGED: {
        const changeData = SSEResponse.data as PlatformResourceChangedData;
        if (!changeData) break;
        set((state) => {
          const conv = state.conversations[receivedConversationId];
          if (!conv) return state;

          const serverAssistantId =
            SSEResponse.assistant_message_id || conv.streamingAssistantMessageId || '';
          let target = resolveStreamGenerationTarget(
            conv.messages,
            serverAssistantId,
            conv.streamingAssistantMessageId,
            SSEResponse.message_sender_id,
            conv.isGroupChat
          );
          if (!target) {
            for (let i = conv.messages.length - 1; i >= 0; i--) {
              const m = conv.messages[i];
              if (
                (m.role === MessageRole.ASSISTANT || m.role === MessageRole.ASSISTANT_CHAT) &&
                m.generations.length > 0
              ) {
                target = {
                  msgIndex: i,
                  genIndex: m.activeGenerationIndex ?? m.generations.length - 1,
                };
                break;
              }
            }
          }
          if (!target) return state;

          const updatedMessages = conv.messages.map((message, msgIdx) => {
            if (msgIdx !== target.msgIndex) return message;
            const updatedGenerations = message.generations.map((gen, genIdx) => {
              if (genIdx !== target.genIndex) return gen;
              const existing = gen.platformResourceChanges || [];
              return {
                ...gen,
                platformResourceChanges: [...existing, changeData],
              };
            });
            return {
              ...message,
              generations: updatedGenerations,
            };
          });

          return {
            conversations: {
              ...state.conversations,
              [receivedConversationId]: {
                ...conv,
                messages: updatedMessages,
              },
            },
          };
        });
        break;
      }

      case SSEResponseTypes.AUTH_REQUIRED: {
        const data = SSEResponse.data as McpServiceAuthRequiredData;
        if (data?.mcpServiceId && !isConnectableAuthMethod(data.authMethod)) {
          // Credentials only the embedding site can supply: a connect prompt here
          // would collect the wrong thing, so end the turn with the server's copy.
          clearAssistantStreamTimeout(receivedConversationId);
          const notice =
            data.message ||
            i18n.t('TOOL_AUTH_UNAVAILABLE_HERE') ||
            'This tool needs a sign-in that is not available here.';
          set((state) => {
            const conv = state.conversations[receivedConversationId];
            if (!conv) return state;
            const targetId =
              conv.streamingAssistantMessageId ??
              [...conv.messages]
                .reverse()
                .find((m) => m.role === MessageRole.ASSISTANT)
                ?.generations?.slice(-1)[0]?.id;
            if (!targetId) return state;
            return {
              conversations: {
                ...state.conversations,
                [receivedConversationId]: {
                  ...conv,
                  streamingAssistantMessageId: null,
                  streamingAssistantMessageIds: [],
                  assistantResponseRemaining: 0,
                  isCurrentResponseGenerating: false,
                  isCurrentMessageLoading: false,
                  isAnalyzing: false,
                  messages: conv.messages.map((message) => ({
                    ...message,
                    generations: message.generations.map((generation) =>
                      generation.id === targetId
                        ? {
                            ...generation,
                            content: notice,
                            isThinking: false,
                            thinkContent: '',
                            isThisGenerationOnGoing: false,
                          }
                        : generation
                    ),
                  })),
                },
              },
            };
          });
          break;
        }
        if (data?.mcpServiceId) {
          useMcpServiceChatStore.getState().setPendingAuth(data);
          // Mark the paused generation so the row shows "Authenticating…" and resume can target
          // it. Fall back to the latest assistant generation if the stream already ended (the
          // auth_required/END_OF_STREAM ordering can null out streamingAssistantMessageId).
          set((state) => {
            const conv = state.conversations[receivedConversationId];
            if (!conv) return state;
            let targetId: string | undefined = conv.streamingAssistantMessageId ?? undefined;
            if (!targetId) {
              const lastAssistant = [...conv.messages]
                .reverse()
                .find((m) => m.role === MessageRole.ASSISTANT);
              targetId = lastAssistant?.generations?.[lastAssistant.generations.length - 1]?.id;
            }
            console.log('[MCP_RESUME_DEBUG] auth_required — marking pausedForAuth', {
              conversationId: receivedConversationId,
              streamingAssistantMessageId: conv.streamingAssistantMessageId,
              targetId,
            });
            if (!targetId) return state;
            return {
              conversations: {
                ...state.conversations,
                [receivedConversationId]: {
                  ...conv,
                  messages: conv.messages.map((message) => ({
                    ...message,
                    generations: message.generations.map((generation) =>
                      generation.id === targetId ? { ...generation, pausedForAuth: true } : generation
                    ),
                  })),
                },
              },
            };
          });
        }
        break;
      }

      case SSEResponseTypes.STREAM_CANCELLED: {
        clearAssistantStreamTimeout(receivedConversationId);
        set((state) => {
          const conv = state.conversations[receivedConversationId];
          if (!conv) return state;

          const target = SSEResponse.assistant_message_id
            ? resolveStreamGenerationTarget(
                conv.messages,
                SSEResponse.assistant_message_id,
                conv.streamingAssistantMessageId,
                SSEResponse.message_sender_id,
                conv.isGroupChat
              )
            : null;

          const isCancelTarget = (
            generation: MessageGeneration,
            msgIndex: number,
            genIndex: number
          ) =>
            generation.id === SSEResponse.assistant_message_id ||
            (target?.msgIndex === msgIndex && target.genIndex === genIndex) ||
            (!target && (generation.isThisGenerationOnGoing ?? false));

          const messages = conv.messages.map((message, msgIndex) => {
            if (message.role !== MessageRole.ASSISTANT) return message;
            return {
              ...message,
              generations: message.generations.map((generation, genIndex) => {
                if (!isCancelTarget(generation, msgIndex, genIndex)) return generation;
                return clearStreamingStatusFields({
                  ...generation,
                  id: SSEResponse.assistant_message_id ?? generation.id,
                  isThisGenerationOnGoing: false,
                  isThinking: false,
                  isAnalyzing: false,
                  isCancelled: true,
                });
              }),
            };
          });

          const hasOtherStreamingMessages = messages.some(
            (msg) =>
              msg.role === MessageRole.ASSISTANT &&
              msg.generations?.some((g) => g?.isThisGenerationOnGoing ?? false)
          );

          return {
            conversations: {
              ...state.conversations,
              [receivedConversationId]: {
                ...conv,
                error: null,
                isCancelOnGoing: false,
                isCurrentMessageLoading: false,
                isAnalyzing: false,
                isCurrentResponseGenerating: hasOtherStreamingMessages,
                streamingAssistantMessageId: hasOtherStreamingMessages
                  ? conv.streamingAssistantMessageId
                  : null,
                messages,
              },
            },
          };
        });
        notifyStreamCancelled(receivedConversationId);
        break;
      }

      case SSEResponseTypes.ASSISTANT_REQUEST_QUEUED: {
        const data: AssistantPositionRemainingType = SSEResponse.data as AssistantPositionRemainingType;

        updateConversation(() => ({
          assistantResponseRemaining: data.remaining,
        }));
        break;
      }
      case SSEResponseTypes.ASSISTANT_REQUEST_PROCESSING_STARTED: {
        // No longer creating MODEL_THINKING messages - skip this event
        break;
      }
      case SSEResponseTypes.CODE_EXECUTION_STARTED: {
        updateConversationMessages((conv, updatedMessages) => ({
          isAnalyzing: true,
          messages: updatedMessages.length > 0 ? updatedMessages : conv.messages,
        }));
        break;
      }
      case SSEResponseTypes.CODE_EXECUTION_STEP: {
        updateConversationMessages((conv, updatedMessages) => {
          const shouldUpdateAnalyzingState =
            conv.isAnalyzed === true && conv.isAnalyzing === false;

          return {
            isAnalyzing: shouldUpdateAnalyzingState ? false : conv.isAnalyzing,
            messages: updatedMessages.length > 0 ? updatedMessages : conv.messages,
          };
        });
        break;
      }
      case SSEResponseTypes.END_OF_STREAM: {
        clearAssistantStreamTimeout(receivedConversationId);

        // An approval-paused (or tool-only) turn can end with no text chunk, so the
        // optimistic placeholder was never rekeyed to the server id. Resolve the
        // target generation like the TOOL_*/A2UI handlers do instead of matching on
        // id alone, otherwise its isThisGenerationOnGoing flag never clears and the
        // composer stays stuck on the stop button.
        const currentConvBeforeEnd = get().conversations[receivedConversationId];
        const assistantIdToResolve =
          SSEResponse.assistant_message_id ||
          currentConvBeforeEnd?.streamingAssistantMessageId;

        const resolveEndTarget = (conv: ConversationState) =>
          assistantIdToResolve
            ? resolveStreamGenerationTarget(
                conv.messages,
                assistantIdToResolve,
                conv.streamingAssistantMessageId,
                SSEResponse.message_sender_id,
                conv.isGroupChat
              )
            : null;
        const isEndTarget = (
          generation: MessageGeneration,
          msgIndex: number,
          genIndex: number,
          target: { msgIndex: number; genIndex: number } | null
        ) =>
          (assistantIdToResolve && generation.id === assistantIdToResolve) ||
          (target?.msgIndex === msgIndex &&
            target.genIndex === genIndex &&
            ((generation.isThisGenerationOnGoing ?? false) || !generation?.content?.trim())) ||
          (!target && !SSEResponse.assistant_message_id && !!generation.isThisGenerationOnGoing);

        set((state) => {
          const conv = state.conversations[receivedConversationId];
          if (!conv) return state;

          let updatedActiveStreamsMap: Map<string, boolean> | undefined;
          const endAssistantId = SSEResponse.assistant_message_id || conv.streamingAssistantMessageId;
          const assistantMessage = conv.messages.find(
            (msg) =>
              msg.role === MessageRole.ASSISTANT &&
              (msg.recordId === endAssistantId ||
                msg.generations.some(
                  (gen) => gen.id === endAssistantId || gen.isThisGenerationOnGoing
                ))
          );
          if (assistantMessage?.userRecordId && conv.activeStreamsMap) {
            updatedActiveStreamsMap = new Map(conv.activeStreamsMap);
            updatedActiveStreamsMap.delete(assistantMessage.userRecordId);
          }

          return {
            conversations: {
              ...state.conversations,
              [receivedConversationId]: {
                ...conv,
                error: null,
                isCurrentMessageLoading: false,
                isAnalyzing: false,
                assistantResponseRemaining: 0,
                ...(updatedActiveStreamsMap && { activeStreamsMap: updatedActiveStreamsMap }),
                isCancelOnGoing: false,
              },
            },
          };
        });

        const latestConv = get().conversations[receivedConversationId];
        const sidebarTitle = get().sidebarConversationsList.find(
          (item) => item.ConversationId === receivedConversationId
        )?.ConversationTitle;
        const isGroupChat = latestConv?.isGroupChat === true;
        const isIncognito = get().isIncognito;
        const isIndividualChat = !isGroupChat && !get().hasParticipant;

        if (
          shouldAutoGenerateConversationTitle({
            conversation: latestConv,
            storedTitle: sidebarTitle ?? latestConv?.title,
            isIncognito,
            hasParticipant: get().hasParticipant,
          })
        ) {
          void get().generateConversationTitle(receivedConversationId, config);
        }

        if (isIndividualChat) {
          const wasSearchEnabled =
            latestConv?.toolsState?.[ButtonIds.SEARCH] ?? false;

          const currentStreamingMessage = latestConv?.messages?.find(
            (m) =>
              m.role === 'assistant' &&
              m.generations.some((g) => g.id === SSEResponse.assistant_message_id)
          );
          const currentGeneration = currentStreamingMessage?.generations?.find(
            (g) => g.id === SSEResponse.assistant_message_id
          );
          const isSearchSourcesShow = currentGeneration?.isSearchSourcesShow ?? false;

          const messageIdToUse =
            currentStreamingMessage?.recordId ?? SSEResponse.assistant_message_id ?? '';

          set((state) => {
            const conv = state.conversations[receivedConversationId];
            if (!conv) return state;

            const target = resolveEndTarget(conv);
            return {
            conversations: {
              ...state.conversations,
              [receivedConversationId]: {
                ...conv,
                isCurrentResponseGenerating: (wasSearchEnabled || isSearchSourcesShow) ? true : false,
                streamingAssistantMessageId: null,
                isCancelOnGoing: false,
                messages: conv.messages.map((message, msgIndex) => {
                  if (message.role === 'assistant') {
                    return {
                      ...message,
                      generations: message.generations.map((generation, genIndex) => {
                        if (isEndTarget(generation, msgIndex, genIndex, target)) {
                          const hasCodeExecutionSteps =
                            Array.isArray(generation.codeExecutionSteps) &&
                            generation.codeExecutionSteps.length > 0;
                          return finalizeGenerationOnStreamEnd(generation, {
                            wasSearchEnabled,
                            isSearchSourcesShow,
                            hasCodeExecutionSteps,
                            serverMessageId: SSEResponse.assistant_message_id,
                          });
                        }
                        return generation;
                      }),
                    };
                  }
                  return message;
                }),
              },
            },
          };
          });

          if (wasSearchEnabled || isSearchSourcesShow) {
            const modelName = latestConv?.modelName ?? '';

            get().getSearchRelatedImageList({
              conversationId: receivedConversationId,
              messageId: messageIdToUse,
              type: 'IMAGE_SUGGESTIONS', modelName
            },
              config);

            get().getSearchRelatedVideoList({
              conversationId: receivedConversationId,
              messageId: messageIdToUse,
              type: 'VIDEO_SUGGESTIONS',
              modelName
            },
              config);

            get().getSearchRelatedList({
              conversationId: receivedConversationId,
              messageId: messageIdToUse,
              type: 'SEARCH_SUGGESTIONS',
              modelName
            },
              config);
          }

        } else {
          // For group chats, only set isCurrentResponseGenerating to false if no other messages are still streaming
          set((state) => {
            const conv = state.conversations[receivedConversationId];
            if (!conv) return state;

            const target = resolveEndTarget(conv);
            const messages = conv.messages.map((message, msgIndex) => {
              if (message.role === 'assistant') {
                return {
                  ...message,
                  generations: message.generations.map((generation, genIndex) => {
                    if (isEndTarget(generation, msgIndex, genIndex, target)) {
                      const hasCodeExecutionSteps =
                        Array.isArray(generation.codeExecutionSteps) &&
                        generation.codeExecutionSteps.length > 0;
                      return finalizeGenerationOnStreamEnd(generation, {
                        hasCodeExecutionSteps,
                        serverMessageId: SSEResponse.assistant_message_id,
                      });
                    }
                    return generation;
                  }),
                };
              }
              return message;
            });

            // Any assistant generation still streaming after this stream's target cleared?
            const hasOtherStreamingMessages = messages.some(
              (msg) =>
                msg.role === MessageRole.ASSISTANT &&
                msg.generations?.some((g) => g?.isThisGenerationOnGoing ?? false)
            );

            const endedId = SSEResponse.assistant_message_id || conv.streamingAssistantMessageId;
            const streamingAssistantMessageIds = conv.streamingAssistantMessageIds?.filter(
              (id) => id !== endedId
            );

            return {
              conversations: {
                ...state.conversations,
                [receivedConversationId]: {
                  ...conv,
                  isCancelOnGoing: false,
                  isCurrentMessageLoading: false,
                  isCurrentResponseGenerating: hasOtherStreamingMessages,
                  streamingAssistantMessageId: hasOtherStreamingMessages ? conv.streamingAssistantMessageId : null,
                  ...(streamingAssistantMessageIds && { streamingAssistantMessageIds }),
                  messages,
                },
              },
            };
          });
        }


        // get().updateProjectConversation(receivedConversationId, config);
        break;
      }

      case SSEResponseTypes.CONTEXT_WINDOW_EXCEEDED: {
        updateConversationMessages(() => ({
          isCurrentMessageLoading: false,
          isCurrentResponseGenerating: false,
          isAnalyzing: false,
          error: 'CONTEXT_WINDOW_EXCEEDED_ERROR_MESSAGE',
        }));
        break;
      }

      case SSEResponseTypes.IMAGE_GENERATION_LIMIT_EXCEEDED: {
        updateConversationMessages(() => ({
          isCurrentMessageLoading: false,
          isCurrentResponseGenerating: false,
          isAnalyzing: false,
          error: 'IMAGE_GENERATION_LIMIT_EXCEEDED_ERROR_MESSAGE',
        }));
        break;
      }

      case SSEResponseTypes.RESPONSE_SOURCES:
      case SSEResponseTypes.SEARCH_SOURCES: {
        updateConversationMessages(() => ({
          streamingAssistantMessageId: SSEResponse.assistant_message_id,
        }));
        break;
      }

      case SSEResponseTypes.SERVER_ERROR: {
        // Show the server error text as a normal assistant message (not conversation.error).
        clearAssistantStreamTimeout(receivedConversationId);
        const agentUnavailable = SSEResponse.category === 'agent_unavailable';
        if (agentUnavailable) {
          toast({
            variant: 'destructive',
            title: i18n.t('AGENT_UNAVAILABLE_TITLE') || 'Agent unavailable',
            description:
              i18n.t('AGENT_UNAVAILABLE_DESCRIPTION') ||
              'This agent is no longer available to you. Its visibility or sharing may have changed.',
          });
        } else {
          // Categorized failures (provider_rate_limited, provider_auth_failed,
          // unknown_error, ...) also surface as a toast — the inline bubble alone
          // is easy to miss when the user has scrolled away.
          const errorText =
            (typeof SSEResponse.error === 'string' && SSEResponse.error.trim()) ||
            (typeof SSEResponse.data === 'string' && SSEResponse.data.trim()) ||
            '';
          if (SSEResponse.category && errorText) {
            toast({
              variant: 'destructive',
              title: i18n.t('RESPONSE_FAILED_TITLE') || 'Response failed',
              description: errorText,
            });
          }
        }
        updateConversationMessages((conv, updatedMessages) => ({
          messages: updatedMessages.length > 0 ? updatedMessages : conv.messages,
          streamingAssistantMessageId: null,
          streamingAssistantMessageIds: [],
          assistantResponseRemaining: 0,
          isCurrentResponseGenerating: false,
          isCurrentMessageLoading: false,
          isAnalyzing: false,
          ...(agentUnavailable && { agentUnavailable: true }),
        }));
        break;
      }
      case SSEResponseTypes.CONVERSATION_TITLE:
        get().updateConversationTitleInStore(receivedConversationId, SSEResponse.data as string);
        break;
      case SSEResponseTypes.FILE_GENERATION_SUCCESS:
      case SSEResponseTypes.IMAGE_GENERATION_SUCCESS:
      case SSEResponseTypes.CODE_EXECUTION_ARTIFACT: {
        updateConversationMessages(() => ({}));
        break;
      }
      case SSEResponseTypes.FILE_GENERATION_FAILED: {
        updateConversationMessages(
          () => ({
            isCurrentMessageLoading: false,
            isCurrentResponseGenerating: false,
            isAnalyzing: false,
            error: 'FILE_GENERATION_FAILED_ERROR_MESSAGE',
          }),
          { allowEmptyMessages: true }
        );
        break;
      }
      case SSEResponseTypes.IMAGE_GENERATION_FAILED: {
        updateConversationMessages(
          (conv) => ({
            isCurrentMessageLoading: false,
            isCurrentResponseGenerating: false,
            isAnalyzing: false,
            error: conv.error,
          }),
          { allowEmptyMessages: true }
        );
        break;
      }
      case SSEResponseTypes.IMAGE_GENERATION_STARTED:
      case SSEResponseTypes.FILE_GENERATION_STARTED:
      case SSEResponseTypes.FILE_GENERATION_ON_PROGRESS:
      case SSEResponseTypes.LLM_ANSWER:
      case SSEResponseTypes.SEARCH_RESPONSE: {
        if (SSEResponseTypes.FILE_GENERATION_STARTED && Array.isArray(SSEResponse.data)) {
          return;
        }

        updateConversationMessages((conv) => ({
          isAnalyzing: false,
          isAnalyzed: conv.isAnalyzing,
          isCurrentMessageLoading: false,
          streamingAssistantMessageId: SSEResponse.assistant_message_id,
        }));
        break;
      }

      case SSEResponseTypes.CONTENT_FILTER_VIOLATION: {
        clearAssistantStreamTimeout(receivedConversationId);
        updateConversationMessages((conv, updatedMessages) => ({
          messages: updatedMessages.length > 0 ? updatedMessages : conv.messages,
          isCurrentMessageLoading: false,
          isCurrentResponseGenerating: false,
          isAnalyzing: false,
          canCancelGeneration: false,
          streamingAssistantMessageId: null,
          hasFetchedInitialMessages: true,
        }));
        break;
      }

      case SSEResponseTypes.LLM_THINKING: {
        updateConversationMessages(() => ({
          isAnalyzing: false,
          isCurrentMessageLoading: false,
          streamingAssistantMessageId: SSEResponse.assistant_message_id,
        }));
        break;
      }

      case SSEResponseTypes.RESPONSE_TIME_ANALYTICS: {
        updateConversationMessages(() => ({}));
        break;
      }

      case SSEResponseTypes.MODEL_SELECTED: {
        updateConversationMessages(() => ({}));
        break;
      }

      case SSEResponseTypes.TOKEN_ANALYTICS: {
        updateConversationMessages(() => ({}));
        const tokenModel = (SSEResponse.data as TokenAnalytics)?.model;
        const usedSmart = isSyntheticAutoModel(useModelStore.getState().selectedModel?.id);
        if (usedSmart && tokenModel && !isSyntheticAutoModel(tokenModel)) {
          useModelStore.getState().setLastUsedFromSmart(tokenModel);
        } else {
          useModelStore.getState().setLastUsedFromSmart(null);
        }
        break;
      }

      case SSEResponseTypes.TOOL_CALL_STARTED:
      case SSEResponseTypes.TOOL_CALL_COMPLETED: {
        const relatedUserMessageId = SSEResponse.message_sender_id;
        const isStarted = SSEResponse.type === SSEResponseTypes.TOOL_CALL_STARTED;
        const toolEntry = isStarted
          ? parseToolCallFromStreamStartedPayload(SSEResponse.data)
          : parseToolCallFromStreamCompletedPayload(SSEResponse.data);
        if (!toolEntry) {
          break;
        }

        set((state) => {
          const conv = state.conversations[receivedConversationId];
          if (!conv) return state;

          // STARTED events sometimes omit assistant_message_id — fall back to the live stream target.
          const serverAssistantId =
            SSEResponse.assistant_message_id || conv.streamingAssistantMessageId || '';
          if (!serverAssistantId && !relatedUserMessageId) {
            return state;
          }

          const target = resolveStreamGenerationTarget(
            conv.messages,
            serverAssistantId,
            conv.streamingAssistantMessageId,
            relatedUserMessageId,
            conv.isGroupChat
          );
          if (!target) return state;

          const toolCallStreamStatus: ToolCallStreamStatus = isStarted ? 'started' : 'completed';
          const messages = conv.messages.map((message, msgIndex) => {
            if (msgIndex !== target.msgIndex) return message;
            return {
              ...message,
              generations: message.generations.map((gen, genIndex): MessageGeneration => {
                if (genIndex !== target.genIndex) return gen;
                const contentOffset = nextStatusOffset(
                  gen.streamStatusEntries,
                  gen.content?.length ?? 0,
                  isStarted ? undefined : 'tool_started'
                );
                const prevLen = gen.streamStatusEntries?.length ?? 0;
                const streamStatusEntries = upsertTodosStreamStatusEntry(
                  gen.streamStatusEntries,
                  toolEntry,
                  toolCallStreamStatus,
                  contentOffset,
                );
                const pairIcon = isStarted ? undefined : 'tool_started';
                let nextGen: MessageGeneration = {
                  ...gen,
                  toolCalls: upsertMessageToolCall(gen.toolCalls, toolEntry),
                  toolCallStreamStatus,
                  activeToolCallName: toolEntry.toolName,
                  isThisGenerationOnGoing: true,
                  streamStatusEntries,
                };
                for (const entry of streamStatusEntries.slice(prevLen)) {
                  nextGen = applyStreamStatusEntry(nextGen, entry, pairIcon);
                }
                for (const entry of streamStatusEntries) {
                  if (entry.todos?.length) {
                    nextGen = applyStreamStatusEntry(nextGen, entry, pairIcon);
                  }
                }
                return nextGen;
              }),
            };
          });

          return {
            conversations: {
              ...state.conversations,
              [receivedConversationId]: {
                ...conv,
                messages,
                isCurrentMessageLoading: false,
                isCurrentResponseGenerating: true,
                streamingAssistantMessageId: serverAssistantId || conv.streamingAssistantMessageId,
              },
            },
          };
        });
        break;
      }

      case SSEResponseTypes.SUBAGENT_STARTED:
      case SSEResponseTypes.SUBAGENT_COMPLETED: {
        const relatedUserMessageId = SSEResponse.message_sender_id;
        const isStarted = SSEResponse.type === SSEResponseTypes.SUBAGENT_STARTED;
        const subagentRun = isStarted
          ? parseSubagentRunFromStreamStartedPayload(SSEResponse.data)
          : parseSubagentRunFromStreamCompletedPayload(SSEResponse.data);
        if (!subagentRun) {
          break;
        }

        set((state) => {
          const conv = state.conversations[receivedConversationId];
          if (!conv) return state;

          const serverAssistantId =
            SSEResponse.assistant_message_id || conv.streamingAssistantMessageId || '';
          if (!serverAssistantId && !relatedUserMessageId) {
            return state;
          }

          const target = resolveStreamGenerationTarget(
            conv.messages,
            serverAssistantId,
            conv.streamingAssistantMessageId,
            relatedUserMessageId,
            conv.isGroupChat
          );
          if (!target) return state;

          const subagentStreamStatus: SubagentStreamStatus = isStarted ? 'started' : 'completed';
          const messages = conv.messages.map((message, msgIndex) => {
            if (msgIndex !== target.msgIndex) return message;
            return {
              ...message,
              generations: message.generations.map((gen, genIndex): MessageGeneration => {
                if (genIndex !== target.genIndex) return gen;
                const contentOffset = nextStatusOffset(
                  gen.streamStatusEntries,
                  gen.content?.length ?? 0,
                  isStarted ? undefined : 'subagent_started'
                );
                const statusEntry = createSubagentStreamStatusEntry(
                  subagentRun,
                  subagentStreamStatus,
                  contentOffset
                );
                return applyStreamStatusEntry(
                  {
                    ...gen,
                    subagentRuns: upsertMessageSubagentRun(gen.subagentRuns, subagentRun),
                    subagentStreamStatus,
                    activeSubagentName: subagentRun.subagentName,
                    isThisGenerationOnGoing: true,
                    streamStatusEntries: appendStreamStatusEntry(gen.streamStatusEntries, statusEntry),
                  },
                  statusEntry,
                  isStarted ? undefined : 'subagent_started'
                );
              }),
            };
          });

          return {
            conversations: {
              ...state.conversations,
              [receivedConversationId]: {
                ...conv,
                messages,
                isCurrentMessageLoading: false,
                isCurrentResponseGenerating: true,
                streamingAssistantMessageId: serverAssistantId || conv.streamingAssistantMessageId,
              },
            },
          };
        });
        break;
      }

      case SSEResponseTypes.A2UI_MESSAGE: {
        const serverAssistantId = SSEResponse.assistant_message_id;
        const relatedUserMessageId = SSEResponse.message_sender_id;
        if (!serverAssistantId) {
          break;
        }

        set((state) => {
          const conv = state.conversations[receivedConversationId];
          if (!conv) return state;

          const target = resolveStreamGenerationTarget(
            conv.messages,
            serverAssistantId,
            conv.streamingAssistantMessageId,
            relatedUserMessageId,
            conv.isGroupChat
          );
          if (!target) return state;

          const messages = conv.messages.map((message, msgIndex) => {
            if (msgIndex !== target.msgIndex) return message;
            return {
              ...message,
              generations: message.generations.map((gen, genIndex): MessageGeneration => {
                if (genIndex !== target.genIndex) return gen;
                const parsed = parseA2uiSurfaceFromStreamPayload(SSEResponse.data);
                if (!parsed) return gen;
                return applyStreamSurface(gen, parsed.surface, parsed.kind);
              }),
            };
          });

          return {
            conversations: {
              ...state.conversations,
              [receivedConversationId]: {
                ...conv,
                messages,
                isCurrentMessageLoading: false,
                streamingAssistantMessageId: serverAssistantId,
              },
            },
          };
        });
        break;
      }

      case SSEResponseTypes.APPROVAL_REQUIRED: {
        const serverAssistantId = SSEResponse.assistant_message_id;
        const relatedUserMessageId = SSEResponse.message_sender_id;
        const surface = parseApprovalRequiredFromStreamPayload(SSEResponse.data);
        if (!serverAssistantId || !surface) {
          break;
        }

        set((state) => {
          const conv = state.conversations[receivedConversationId];
          if (!conv) return state;

          const target = resolveStreamGenerationTarget(
            conv.messages,
            serverAssistantId,
            conv.streamingAssistantMessageId,
            relatedUserMessageId,
            conv.isGroupChat
          );
          if (!target) return state;

          const messages = conv.messages.map((message, msgIndex) => {
            if (msgIndex !== target.msgIndex) return message;
            return {
              ...message,
              generations: message.generations.map((gen, genIndex): MessageGeneration => {
                if (genIndex !== target.genIndex) return gen;
                return applyStreamSurface(gen, surface);
              }),
            };
          });

          return {
            conversations: {
              ...state.conversations,
              [receivedConversationId]: {
                ...conv,
                messages,
                isCurrentMessageLoading: false,
                isCurrentResponseGenerating: true,
                streamingAssistantMessageId: serverAssistantId || conv.streamingAssistantMessageId,
              },
            },
          };
        });
        break;
      }

      case SSEResponseTypes.APPROVAL_RESOLVED: {
        const resolved = parseApprovalResolvedFromStreamPayload(SSEResponse.data);
        if (!resolved) {
          break;
        }

        set((state) => {
          const conv = state.conversations[receivedConversationId];
          if (!conv) return state;

          return {
            conversations: {
              ...state.conversations,
              [receivedConversationId]: {
                ...conv,
                messages: conv.messages.map((message) => ({
                  ...message,
                  generations: message.generations.map((generation) =>
                    applyStreamApprovalResolved(generation, resolved)
                  ),
                })),
                isCurrentMessageLoading: false,
              },
            },
          };
        });
        break;
      }

      case SSEResponseTypes.CONVERSATION_COMPACTION_STARTED:
      case SSEResponseTypes.CONVERSATION_COMPACTION_COMPLETED:
      case SSEResponseTypes.CONVERSATION_COMPACTION_FAILED: {
        const compactionStreamStatus = compactionStatusFromEventType(SSEResponse.type);
        if (!compactionStreamStatus) {
          break;
        }

        const relatedUserMessageId = SSEResponse.message_sender_id;
        set((state) => {
          const conv = state.conversations[receivedConversationId];
          if (!conv) return state;

          const serverAssistantId =
            SSEResponse.assistant_message_id || conv.streamingAssistantMessageId || '';
          let target =
            serverAssistantId || relatedUserMessageId
              ? resolveStreamGenerationTarget(
                  conv.messages,
                  serverAssistantId,
                  conv.streamingAssistantMessageId,
                  relatedUserMessageId,
                  conv.isGroupChat
                )
              : null;

          if (!target) {
            for (let msgIndex = conv.messages.length - 1; msgIndex >= 0; msgIndex -= 1) {
              const message = conv.messages[msgIndex];
              if (message.role !== MessageRole.ASSISTANT) continue;
              const genIndex = message.activeGenerationIndex ?? message.generations.length - 1;
              if (message.generations[genIndex]) {
                target = { msgIndex, genIndex };
                break;
              }
            }
          }
          if (!target) return state;

          const messages = conv.messages.map((message, msgIndex) => {
            if (msgIndex !== target.msgIndex) return message;
            return {
              ...message,
              generations: message.generations.map((gen, genIndex): MessageGeneration => {
                if (genIndex !== target.genIndex) return gen;
                return {
                  ...gen,
                  compactionStreamStatus,
                  isThisGenerationOnGoing: true,
                };
              }),
            };
          });

          return {
            conversations: {
              ...state.conversations,
              [receivedConversationId]: {
                ...conv,
                messages,
                isCurrentMessageLoading: false,
                isCurrentResponseGenerating: true,
                streamingAssistantMessageId: serverAssistantId || conv.streamingAssistantMessageId,
              },
            },
          };
        });
        break;
      }

      case SSEResponseTypes.SKILL_USED:
      case SSEResponseTypes.SKILL_SKIPPED:
      case SSEResponseTypes.SKILL_RESOURCE_LOADED: {
        const relatedUserMessageId = SSEResponse.message_sender_id;
        const skillEntry = parseSkillEventFromStreamPayload(SSEResponse.type, SSEResponse.data);
        const skillStreamStatus = streamEventTypeToSkillStreamStatus(SSEResponse.type);
        if (!skillEntry || !skillStreamStatus) {
          break;
        }

        const skillDisplayName = formatSkillDisplayName(skillEntry.skillName);

        set((state) => {
          const conv = state.conversations[receivedConversationId];
          if (!conv) return state;

          const serverAssistantId =
            SSEResponse.assistant_message_id || conv.streamingAssistantMessageId || '';
          if (!serverAssistantId && !relatedUserMessageId) {
            return state;
          }

          const target = resolveStreamGenerationTarget(
            conv.messages,
            serverAssistantId,
            conv.streamingAssistantMessageId,
            relatedUserMessageId,
            conv.isGroupChat
          );
          if (!target) return state;

          const messages = conv.messages.map((message, msgIndex) => {
            if (msgIndex !== target.msgIndex) return message;
            return {
              ...message,
              generations: message.generations.map((gen, genIndex): MessageGeneration => {
                if (genIndex !== target.genIndex) return gen;
                const statusEntry = createSkillStreamStatusEntry(
                  skillEntry,
                  skillStreamStatus,
                  nextStatusOffset(gen.streamStatusEntries, gen.content?.length ?? 0)
                );
                return applyStreamStatusEntry(
                  {
                    ...gen,
                    skillEvents: upsertMessageSkillEvent(gen.skillEvents, skillEntry),
                    skillStreamStatus,
                    activeSkillName: skillDisplayName,
                    activeSkillResourceName: skillEntry.resourceName,
                    isThisGenerationOnGoing: true,
                    streamStatusEntries: appendStreamStatusEntry(gen.streamStatusEntries, statusEntry),
                  },
                  statusEntry
                );
              }),
            };
          });

          return {
            conversations: {
              ...state.conversations,
              [receivedConversationId]: {
                ...conv,
                messages,
                isCurrentMessageLoading: false,
                isCurrentResponseGenerating: true,
                streamingAssistantMessageId: serverAssistantId || conv.streamingAssistantMessageId,
              },
            },
          };
        });
        break;
      }
    }

    // Keep the stall watchdog aligned with the post-frame streaming state.
    // Stream-scoped frames are liveness: re-arm while anything is still
    // generating (covers group chats where one agent's END_OF_STREAM must not
    // leave the remaining streams unwatched), clear once nothing is.
    if (SSEResponse.assistant_message_id) {
      if (get().conversations[receivedConversationId]?.isCurrentResponseGenerating) {
        armAssistantStreamWatchdog(receivedConversationId);
      } else {
        clearAssistantStreamTimeout(receivedConversationId);
      }
    }
  },

  regenerateResponse: async (recordId: string, config: ConfigurationSettings) => {
    const { activeConversationId } = get();
    const selectedModel = useModelStore.getState().selectedModel;

    if (!activeConversationId) return;

    const conversationMessages = get().conversations[activeConversationId].messages;
    const userMessage = findUserMessageForLinkedId(conversationMessages, recordId);

    const activeUserGenerationIndex = userMessage?.activeGenerationIndex ?? 0;
    const activeUserGeneration = userMessage?.generations[activeUserGenerationIndex];

    if (!userMessage || !activeUserGeneration?.content) {
      console.error('Failed to find user message for regeneration with recordId:', recordId);
      set((state) => ({
        conversations: {
          ...state.conversations,
          [activeConversationId!]: {
            ...state.conversations[activeConversationId!],
            isCurrentMessageLoading: false,
            isCurrentResponseGenerating: false,
            error: 'Cannot regenerate: User message not found.',
          },
        },
      }));
      return;
    }

    const prevAssistantMessage = conversationMessages.find(
      (msg) =>
        msg.role === MessageRole.ASSISTANT &&
        resolveUserMessageRecordIdForAssistant(conversationMessages, msg) === userMessage.recordId
    );

    if (!prevAssistantMessage) {
      console.error('Failed to find assistant message for regeneration with recordId:', recordId);
      set((state) => ({
        conversations: {
          ...state.conversations,
          [activeConversationId!]: {
            ...state.conversations[activeConversationId!],
            isCurrentMessageLoading: false,
            isCurrentResponseGenerating: false,
            error: 'Cannot regenerate: Assistant message not found.',
          },
        },
      }));
      return;
    }

    const assistantMessageId = uuidv4();
    const activeAssistantGenerationIndex =
      prevAssistantMessage.activeGenerationIndex ?? prevAssistantMessage.generations.length - 1;
    const activeAssistantGeneration =
      prevAssistantMessage.generations[activeAssistantGenerationIndex];

    set((state) => {
      const conv = state.conversations[activeConversationId!];
      if (!conv) return state;

      const updatedMessages = expireActiveApprovals(conv.messages).map((msg) => {
        if (msg.recordId !== prevAssistantMessage.recordId) {
          if (msg.role !== MessageRole.ASSISTANT) return msg;
          if (!msg.generations.some((generation) => generation.isThisGenerationOnGoing)) return msg;
          return {
            ...msg,
            generations: msg.generations.map((generation) =>
              generation.isThisGenerationOnGoing
                ? { ...generation, isThisGenerationOnGoing: false }
                : generation
            ),
          };
        }

        const lastGeneration = msg.generations[msg.generations.length - 1];
        const canReuseLast =
          !!lastGeneration &&
          !lastGeneration.content?.trim() &&
          (lastGeneration.isThisGenerationOnGoing || !lastGeneration.streamBlocks?.length);

        const nextGeneration = {
          id: assistantMessageId,
          content: '',
          createdDate: Date.now(),
          showMessageTime: formatShowMessageTimeForSend(),
          isThinking:
            !!selectedModel?.isThinkingModel &&
            !selectedModel?.isHarmonicModel &&
            !selectedModel?.hasDynamicThinking,
          hasSearchQuery: msg.toolsStatus?.[Tools.SEARCH] ?? false,
          usedModelId: selectedModel?.id,
          parentUserGenerationId: activeUserGeneration.id,
          isThisGenerationOnGoing: true,
        };

        const newGenerations = canReuseLast
          ? msg.generations.map((generation, index) =>
              index === msg.generations.length - 1 ? { ...generation, ...nextGeneration } : generation
            )
          : [...msg.generations, nextGeneration];

        return {
          ...msg,
          generations: newGenerations,
          activeGenerationIndex: newGenerations.length - 1,
        };
      });

      return {
        conversations: {
          ...state.conversations,
          [activeConversationId!]: {
            ...conv,
            messages: consolidateAssistantMessages(updatedMessages),
            isCurrentMessageLoading: true,
            isCurrentResponseGenerating: true,
            canCancelGeneration: false,
          },
        },
      };
    });

    trackEvent(EVENTS.MESSAGE_SENT(activeConversationId, activeUserGeneration.content.length ?? 0));
    if (prevAssistantMessage.toolsStatus?.[Tools.SEARCH]) {
      trackEvent(EVENTS.FEATURE_USED(FeatureName.SEARCH));
    }
    if (prevAssistantMessage.toolsStatus?.[Tools.CREATE_IMAGE]) {
      trackEvent(EVENTS.FEATURE_USED(FeatureName.CREATE_IMAGE));
    }

    const nowIso = new Date().toISOString();
    const attachmentsMetadata: FileMetadata[] = (activeUserGeneration.attachments ?? []).map((att) => ({
      fileId: att.fileId,
      fileName: att.fileName,
      extension: att.extension,
      sender: att.sender,
      size: att.size ?? 0,
      createdDate: nowIso,
      lastUpdatedDate: nowIso,
    }));
    const imageGeneration = prevAssistantMessage.toolsStatus?.[Tools.CREATE_IMAGE] ?? false;
    const searchOnline = prevAssistantMessage.toolsStatus?.[Tools.SEARCH] ?? false;
    const integrationSources: IntegrationSource[] = [];

    const mentions = [selectedModel?.id];

    scheduleAssistantStreamTimeout(
      activeConversationId,
      get().setChatErrorMessage,
      (id) => get().conversations[id]
    );
    get().sendGroupChatStream(
      activeUserGeneration.content,
      config,
      activeConversationId,
      attachmentsMetadata,
      undefined,
      mentions as string[],
      true,
      activeAssistantGeneration?.id,
      imageGeneration,
      integrationSources,
      searchOnline,
      undefined,
      activeUserGeneration.id,
      []
    );


    // get().sendSSEMessage(
    //   JSON.stringify({
    //     question: userMessage.generations[0].content,
    //     conversation: [],
    //     file_ids: get().conversations[activeConversationId].allFileIds,
    //     filter_key_value_pair: [],
    //     model_name: selectedModel?.id,
    //     is_regenerate: true,
    //     conversation_id: activeConversationId,
    //     curr_file_ids: userMessage?.generations[0].attachments?.map((file) => file.fileId),
    //     search_online_v2: prevAssistantMessage?.toolsStatus?.[Tools.SEARCH] ?? false,
    //     image_generation: prevAssistantMessage?.toolsStatus?.[Tools.CREATE_IMAGE] ?? false,
    //     record_id: recordId,
    //     msg_id: uuidv4(),
    //     assistant_message_id: assistantMessageId,
    //     original_regeneration_message_id: prevAssistantMessage?.generations[0]?.id,
    //   }),
    //   config,
    //   activeConversationId
    // );
  },

  editUserMessage: async (
    userRecordId: string,
    content: string,
    config: ConfigurationSettings,
    options: { attachments?: Attachment[]; selectedModelId?: string } = {}
  ): Promise<boolean> => {
    const trimmedContent = content.trim();
    if (!trimmedContent) return false;

    const { activeConversationId } = get();
    if (!activeConversationId) return false;

    const conversation = get().conversations[activeConversationId];
    if (!conversation) return false;

    const userMessage = conversation.messages.find((msg) => msg.recordId === userRecordId);
    if (!userMessage) return false;

    const prevAssistantMessage = conversation.messages.find(
      (msg) =>
        msg.role === MessageRole.ASSISTANT &&
        resolveUserMessageRecordIdForAssistant(conversation.messages, msg) === userMessage.recordId
    );

    const activeGeneration = userMessage.generations[userMessage.activeGenerationIndex ?? 0];
    const activeGenerationId = activeGeneration?.id || userMessage.recordId;
    const attachments =
      options.attachments ??
      activeGeneration?.attachments?.filter((attachment) => attachment.sender === 'user') ??
      [];
    const selectedModelId = options.selectedModelId ?? useModelStore.getState().selectedModel?.id ?? 'auto';

    if (conversation.isCurrentResponseGenerating) {
      await get().stopGeneration(activeConversationId, config);

      const afterCancelAttempt = get().conversations[activeConversationId];
      if (afterCancelAttempt?.error === 'COUDNT_STOP_GENERATION') {
        return false;
      }

      await waitForStreamCancelled(activeConversationId);

      const afterCancelWait = get().conversations[activeConversationId];
      if (afterCancelWait?.isCurrentResponseGenerating) {
        set((state) => {
          const conv = state.conversations[activeConversationId];
          if (!conv) return state;

          const messages = finalizeTurnInFlightAssistant(conv.messages, userRecordId);
          const stillStreaming = messages.some(
            (msg) =>
              msg.role === MessageRole.ASSISTANT &&
              msg.generations.some((generation) => generation.isThisGenerationOnGoing ?? false)
          );

          return {
            conversations: {
              ...state.conversations,
              [activeConversationId]: {
                ...conv,
                messages,
                isCurrentMessageLoading: false,
                isCurrentResponseGenerating: stillStreaming,
                streamingAssistantMessageId: stillStreaming
                  ? conv.streamingAssistantMessageId
                  : null,
                isCancelOnGoing: false,
              },
            },
          };
        });
      }
    }

    let newUserGenerationId: string | undefined;
    try {
      const response = await updateMessage(
        {
          conversationId: activeConversationId,
          messageId: activeGenerationId,
          content: trimmedContent,
          attachments: attachments.map(({ fileId, fileName, extension }) => ({
            fileId,
            fileName,
            extension,
          })),
          mentions: [selectedModelId],
          queryMetadata: {
            isRegenerate: true,
          },
        },
        config
      );
      newUserGenerationId = response?.id;
    } catch (error) {
      console.error('Failed to update message:', error);
      trackEvent(EVENTS.ERROR(error, { endpoint: 'collaboration/messages', method: 'PUT' }));
      get().setChatErrorMessage(activeConversationId);
      return false;
    }

    if (!newUserGenerationId) return false;

    const selectedModel = useModelStore.getState().selectedModel;
    const assistantGenerationId = uuidv4();

    set((state) => {
      const conv = state.conversations[activeConversationId];
      if (!conv) return state;

      const assistantPlaceholder = {
        id: assistantGenerationId,
        content: '',
        createdDate: Date.now(),
        isThinking:
          !!selectedModel?.isThinkingModel &&
          !selectedModel?.isHarmonicModel &&
          !selectedModel?.hasDynamicThinking,
        usedModelId: selectedModel?.id,
        parentUserGenerationId: newUserGenerationId,
        isThisGenerationOnGoing: true,
      };

      const updatedMessages = conv.messages.map((msg) => {
        if (msg.recordId === userMessage.recordId) {
          const lastGeneration = msg.generations[msg.generations.length - 1];
          const alreadyHasGeneration =
            msg.generations.some((generation) => generation.id === newUserGenerationId) ||
            lastGeneration?.content?.trim() === trimmedContent;
          const generations = alreadyHasGeneration
            ? msg.generations
            : [
                ...msg.generations,
                {
                  id: newUserGenerationId,
                  content: trimmedContent,
                  createdDate: Date.now(),
                  attachments: activeGeneration?.attachments,
                },
              ];
          return {
            ...msg,
            generations,
            activeGenerationIndex: generations.length - 1,
          };
        }
        if (prevAssistantMessage && msg.recordId === prevAssistantMessage.recordId) {
          const alreadyHasPlaceholder = msg.generations.some(
            (generation) => generation.id === assistantGenerationId
          );
          if (alreadyHasPlaceholder) return msg;
          return {
            ...msg,
            generations: [...msg.generations, assistantPlaceholder],
            activeGenerationIndex: msg.generations.length,
          };
        }
        return msg;
      });

      return {
        conversations: {
          ...state.conversations,
          [activeConversationId]: {
            ...conv,
            messages: removeEchoUserMessageRows(updatedMessages),
            isCurrentMessageLoading: true,
            isCurrentResponseGenerating: true,
            canCancelGeneration: true,
            streamingAssistantMessageId: assistantGenerationId,
            error: null,
          },
        },
      };
    });

    return true;
  },

  sendMessage: async (
    content: string,
    config: ConfigurationSettings,
    newConversationId?: string,
    attachments?: Attachment[],
    isFeatureSelected?: Record<ButtonIds, boolean>,
    isGroupChatParticipants?: boolean,
    parentIds?: string[],
    sources?: IntegrationSource[],
    isRegenerate?: boolean,
    originalRegenerationMessageId?: string,
    mentions?: Mention[],
    parentUserGenerationId?: string,
    isIncognitoChat?: boolean,
    customGptId?: string,
    selectedTools?: string[],
    selectedSkillIds?: string[],
    agentId?: string,
    isAgentPreviewChat?: boolean,
    a2uiMetadata?: Record<string, unknown>,
  ) => {
    // console.log('mentions in sendMessage', mentions)
    const { activeConversationId, replyMessage } = get();

    const selectedModel = useModelStore.getState().selectedModel;
    const newFileIds = attachments?.map((item) => item.fileId) ?? [];
    let updatedAllFileIds = newFileIds;

    const parentMessage = replyMessage?.generation && replyMessage?.repliedUser ? {
      senderName: replyMessage.repliedUser.firstName + (replyMessage.repliedUser.lastName ? ` ${replyMessage.repliedUser.lastName}` : ''),
      content: replyMessage.generation.content,
      time: messageGenerationTimeMs(replyMessage.generation)
    } : undefined;
    const messageRole = isGroupChatParticipants ? MessageRole.USER_CHAT : MessageRole.USER;
    // console.log('messageRole in sendMessage', messageRole, "isGroupChatParticipants", isGroupChatParticipants)
    const userGenerationId = uuidv4();
    const userSendShowTime = formatShowMessageTimeForSend();
    const userMessage: Message = {
      recordId: userGenerationId,
      role: messageRole,
      senderId: useCommonStore.getState().user?.id,
      // Set delivery status to PENDING for group chat messages
      ...(isGroupChatParticipants && { deliveryStatus: MessageDeliveryStatus.PENDING }),
      generations: [
        {
          id: userGenerationId,
          content: content,
          createdDate: Date.now(),
          showMessageTime: userSendShowTime,
          attachments: attachments,
          ...(parentIds && parentIds.length > 0 && { parentIds: parentIds }),
          ...(parentMessage && { parentMessage }),
          ...(mentions && mentions.length > 0 && { mentions: mentions }),
        },
      ],
    };
    const assistantMessageId = uuidv4();
    let conversationIdToUse = newConversationId ?? activeConversationId;
    const conversationExists = Boolean(
      conversationIdToUse && get().conversations[conversationIdToUse],
    );

    if (!conversationIdToUse || !conversationExists) {
      conversationIdToUse = newConversationId ?? conversationIdToUse ?? uuidv4();
      const newConvState = initialConversationState(conversationIdToUse, ""); // Temporary title
      newConvState.messages = [userMessage];
      newConvState.isCurrentMessageLoading = isGroupChatParticipants ? false : true;
      newConvState.isCurrentResponseGenerating = true;
      newConvState.hasFetchedInitialMessages = true;
      newConvState.modelName = selectedModel?.id;
      if (selectedModel?.id) {
        newConvState.invitationParticipantIds = [
          ...(agentId ? [agentId] : []),
          selectedModel.id,
        ];
      }
      newConvState.allFileIds = newFileIds;
      newConvState.canCancelGeneration = false;
      newConvState.toolsState = isFeatureSelected ?? initialFeaturesState;
      if (customGptId) {
        newConvState.customGptId = customGptId;
      }
      if (agentId) {
        newConvState.agentId = agentId;
      }
      if (isAgentPreviewChat) {
        newConvState.hasManualTitle = true;
      }

      set((state) => ({
        ...(agentId || isAgentPreviewChat ? {} : { activeConversationId: conversationIdToUse }),
        conversations: {
          ...state.conversations,
          [conversationIdToUse!]: newConvState,
        },
      }));

      // Incognito / preview: keep the message+stream path; only skip the sidebar entry.
      if (!shouldSkipSidebarConversationEntry({ isIncognitoChat, isAgentPreviewChat })) {
      const isoDate = new Date().toISOString();
      const currentUser = useCommonStore.getState().user;
      // Tag agent conversations so they nest under the agent in the sidebar, not Recent Chats.
      const agentIdForSidebar =
        !isGroupChatParticipants
          ? agentId ??
            get().sidebarConversationsList.find(
              (item) => item.ConversationId === conversationIdToUse,
            )?.AgentId
          : undefined;
      // Reuse the agent logo another chat already carries so the Recent Chats row matches
      // the Agents section right away instead of after the next conversations list fetch.
      const agentLogoUrl = agentIdForSidebar
        ? get().sidebarConversationsList.find(
            (item) => item.AgentId === agentIdForSidebar && item.logo_url,
          )?.logo_url
        : undefined;
      const newSidebarEntry: SidebarItem = {
        ConversationId: conversationIdToUse!,
        ConversationTitle: '',
        Conversations: [], // Assuming this is for potential sub-items, adjust if not used
        CreatedBy: currentUser?.id || '',
        CreatedDate: isoDate,
        LastUpdatedDate: isoDate,
        TenantId: '', // Populate if available
        IsGroup: isGroupChatParticipants ?? false,
        CustomGptId: customGptId,
        AgentId: agentIdForSidebar,
        ...(agentLogoUrl ? { logo_url: agentLogoUrl } : {}),
      };
      get().appendToSidebarConversationsList(newSidebarEntry);
      }
    } else {
      get()._addMessageToActiveConversation(userMessage, undefined, conversationIdToUse!);

      if (conversationIdToUse) {
        get()._moveConversationToToday(conversationIdToUse);
      }

      const currentConv = get().conversations[conversationIdToUse!];
      const prevAllFileIds = currentConv.allFileIds ?? [];
      updatedAllFileIds = [...prevAllFileIds, ...newFileIds];

      set((state) => ({
        conversations: {
          ...state.conversations,
          [conversationIdToUse!]: {
            ...state.conversations[conversationIdToUse!],
            isCurrentMessageLoading: isGroupChatParticipants ? false : true,
            isCurrentResponseGenerating: true,
            modelName: selectedModel?.id,
            allFileIds: updatedAllFileIds,
            canCancelGeneration: false,
            toolsState: isFeatureSelected ?? initialFeaturesState,
            isAnalyzing: !!attachments && attachments.length > 0, // Set analyzing to true when files are uploaded

          },
        },
      }));

      scheduleAssistantStreamTimeout(
        conversationIdToUse!,
        get().setChatErrorMessage,
        (id) => get().conversations[id]
      );
    }
    if (!isGroupChatParticipants) {
      get().setHasAddedAssistantAsParticipant(true);
      // }

      const newAssistantMessage: Message = {
        recordId: assistantMessageId,
        userRecordId: userMessage.generations[0].id,
        role: MessageRole.ASSISTANT,
        toolsStatus: isFeatureSelected?.[ButtonIds.SEARCH]
          ? {
            [Tools.SEARCH]: true,
            [Tools.CREATE_IMAGE]: false,
            [Tools.VOICE]: false,
          }
          : undefined,
        generations: [
          {
            id: assistantMessageId,
            content: '',
            createdDate: Date.now(),
            showMessageTime: formatShowMessageTimeForSend(),
            HasFileGeneratedResponse: false,
            isThinking: !!selectedModel?.isThinkingModel && !selectedModel?.isHarmonicModel && !selectedModel?.hasDynamicThinking,
            isAnalyzing: !!attachments && attachments.length > 0, // Set analyzing to true when files are uploaded
            isAnalyzed: false,
            usedModelId: selectedModel?.id,
            parentUserGenerationId: parentUserGenerationId ?? userGenerationId,
            isThisGenerationOnGoing: true,
          },
        ],
      };
      // get().appendToSidebarConversationsList(newAssistantMessage);
      get()._addMessageToActiveConversation(
        newAssistantMessage,
        userMessage.generations[0].id,
        conversationIdToUse!,
      );
    }

    trackEvent(EVENTS.MESSAGE_SENT(conversationIdToUse, userMessage?.generations[0].content.length ?? 0));
    if (isFeatureSelected?.[ButtonIds.SEARCH]) {
      trackEvent(EVENTS.FEATURE_USED(FeatureName.SEARCH));
    }
    if (isFeatureSelected?.[ButtonIds.CREATE_IMAGE]) {
      trackEvent(EVENTS.FEATURE_USED(FeatureName.CREATE_IMAGE));
    }
    // if (isGroupChatParticipants) {
    const nowIso = new Date().toISOString();

    const resolvedAgentId =
      agentId ??
      (conversationIdToUse
        ? get().conversations[conversationIdToUse]?.agentId ??
          get().sidebarConversationsList.find(
            (item) => item.ConversationId === conversationIdToUse,
          )?.AgentId
        : undefined);

    const attachmentsMetadata: FileMetadata[] = (userMessage.generations[0].attachments ?? []).map(
      (att) => {
        const normalizedExtension = att.extension.replace(/^\./, '');
        if (resolvedAgentId) {
          return {
            fileId: att.fileId,
            fileName: att.fileName,
            extension: normalizedExtension,
            sender: att.sender,
            size: att.size ?? 0,
          } as FileMetadata;
        }
        return {
          fileId: att.fileId,
          fileName: att.fileName,
          extension: normalizedExtension,
          sender: att.sender,
          size: att.size ?? 0,
          createdDate: nowIso,
          lastUpdatedDate: nowIso,
        };
      },
    );
    // const replyMessage = get().replyMessage;


    let mentionUserIds: string[];

    if (!isGroupChatParticipants) {
      if (isCatalogueAgentId(resolvedAgentId)) {
        mentionUserIds = [];
      } else if (mentions && mentions.length > 0) {
        mentionUserIds = Array.from(new Set(mentions.map((m) => m.userId)));
      } else {
        mentionUserIds = [];
      }
    } else if (isGroupChatParticipants && mentions && mentions.length > 0) {
      const userIds = new Set(mentions.map((m) => m.userId));
      mentionUserIds = Array.from(userIds);
    } else if (isGroupChatParticipants && !mentions) {
      mentionUserIds = isCatalogueAgentId(resolvedAgentId)
        ? []
        : [DEFAULT_MAIN_CHAT_AGENT_ID];
    } else {
      mentionUserIds = [];
    }
    const imageGeneration = isFeatureSelected?.[ButtonIds.CREATE_IMAGE] ?? false;
    const searchOnline = isFeatureSelected?.[ButtonIds.SEARCH] ?? false;
    const integrationSources = sources ? sources : [];

    scheduleAssistantStreamTimeout(
      conversationIdToUse!,
      get().setChatErrorMessage,
      (id) => get().conversations[id]
    );

    await get().sendGroupChatStream(
      content,
      config,
      conversationIdToUse,
      attachmentsMetadata,
      replyMessage,
      mentionUserIds,
      isRegenerate ?? false,
      originalRegenerationMessageId ?? undefined,
      imageGeneration,
      integrationSources ?? [],
      searchOnline,
      undefined,
      userGenerationId,
      selectedTools,
      selectedSkillIds,
      a2uiMetadata
    );
    return;


  },

  markA2uiSurfaceStatus: (conversationId, surfaceId, status) => {
    set((state) => {
      const conv = state.conversations[conversationId];
      if (!conv) return state;
      return {
        conversations: {
          ...state.conversations,
          [conversationId]: {
            ...conv,
            messages: conv.messages.map((message) => ({
              ...message,
              generations: message.generations.map((generation) => {
                const hasSurface = generation.a2uiSurfaces?.some(
                  (surface) => surface.surfaceId === surfaceId
                );
                const hasBlock = generation.streamBlocks?.some(
                  (block) =>
                    (block.type === 'approval' || block.type === 'a2ui') &&
                    block.surface.surfaceId === surfaceId
                );
                if (!hasSurface && !hasBlock) return generation;
                return {
                  ...generation,
                  streamBlocks: markStreamSurfaceStatus(generation.streamBlocks, surfaceId, status),
                  a2uiSurfaces: generation.a2uiSurfaces?.map((surface) =>
                    surface.surfaceId === surfaceId ? { ...surface, status } : surface
                  ),
                };
              }),
            })),
          },
        },
      };
    });
  },

  clearConversationGenerating: (conversationId) => {
    clearAssistantStreamTimeout(conversationId);
    set((state) => {
      const conv = state.conversations[conversationId];
      if (!conv || (!conv.isCurrentResponseGenerating && !conv.isCurrentMessageLoading)) {
        return state;
      }
      return {
        conversations: {
          ...state.conversations,
          [conversationId]: {
            ...conv,
            isCurrentResponseGenerating: false,
            isCurrentMessageLoading: false,
            streamingAssistantMessageId: null,
          },
        },
      };
    });
  },

  resumePendingAuthTurn: async (config, queryMetadata) => {
    const { activeConversationId } = get();
    if (!activeConversationId) {
      console.log('[MCP_RESUME_DEBUG] resume aborted — no activeConversationId');
      return;
    }
    const conv = get().conversations[activeConversationId];
    if (!conv) {
      console.log('[MCP_RESUME_DEBUG] resume aborted — no conversation', activeConversationId);
      return;
    }

    // Locate the paused generation (by flag, then streaming id). Fall back to the latest
    // assistant generation so a cleared placeholder never blocks resume.
    let pausedGenId: string | undefined;
    let userMessageId: string | undefined;
    let content = '';
    for (const message of conv.messages) {
      if (message.role !== MessageRole.ASSISTANT) continue;
      const gen =
        message.generations.find((g) => g.pausedForAuth) ??
        (conv.streamingAssistantMessageId
          ? message.generations.find((g) => g.id === conv.streamingAssistantMessageId)
          : undefined);
      if (gen) {
        pausedGenId = gen.id;
        userMessageId = message.userRecordId;
        break;
      }
    }
    if (!pausedGenId) {
      const lastAssistant = [...conv.messages].reverse().find((m) => m.role === MessageRole.ASSISTANT);
      pausedGenId = lastAssistant?.generations?.[lastAssistant.generations.length - 1]?.id;
      userMessageId = lastAssistant?.userRecordId;
    }
    if (!pausedGenId) {
      console.log('[MCP_RESUME_DEBUG] resume aborted — no assistant generation to reuse');
      return;
    }

    // Resolve the originating user message text (Q1); fall back to the last user message.
    if (userMessageId) {
      for (const message of conv.messages) {
        if (message.role !== MessageRole.USER) continue;
        const userGen = message.generations.find((g) => g.id === userMessageId);
        if (userGen) {
          content = userGen.content ?? '';
          break;
        }
      }
    }
    if (!content) {
      const lastUser = [...conv.messages].reverse().find((m) => m.role === MessageRole.USER);
      if (lastUser) {
        if (!userMessageId) userMessageId = lastUser.generations?.[0]?.id ?? lastUser.recordId;
        content = lastUser.generations?.[lastUser.generations.length - 1]?.content ?? '';
      }
    }
    if (!content) content = queryMetadata.content ?? '';
    console.log('[MCP_RESUME_DEBUG] resume firing', {
      conversationId: activeConversationId,
      messageId: userMessageId,
      assistantMessageId: pausedGenId,
      contentLen: content.length,
      tool_ids: queryMetadata.tool_ids,
    });

    // Clear the paused flag and re-mark the SAME generation ongoing (no new user/assistant bubble).
    set((state) => {
      const current = state.conversations[activeConversationId];
      if (!current) return state;
      return {
        conversations: {
          ...state.conversations,
          [activeConversationId]: {
            ...current,
            isCurrentResponseGenerating: true,
            streamingAssistantMessageId: pausedGenId,
            messages: current.messages.map((message) => ({
              ...message,
              generations: message.generations.map((generation) =>
                generation.id === pausedGenId
                  ? {
                      // Drop the paused turn's text + tool-calls panel so the resumed run starts
                      // clean; keep pausedForAuth so the row shows "Authenticating…" until the
                      // first resume chunk arrives (clearStreamingStatusFields strips pausedForAuth,
                      // so re-set it after).
                      ...clearStreamingStatusFields({
                        ...generation,
                        content: '',
                        toolCalls: [],
                      }),
                      pausedForAuth: true,
                      isThisGenerationOnGoing: true,
                    }
                  : generation
              ),
            })),
          },
        },
      };
    });

    scheduleAssistantStreamTimeout(
      activeConversationId,
      get().setChatErrorMessage,
      (id) => get().conversations[id]
    );

    try {
      await getResumeChatStream(
        {
          conversationId: activeConversationId,
          messageId: userMessageId ?? '',
          assistantMessageId: pausedGenId,
          content,
          queryMetadata,
        },
        config
      );
    } catch (error) {
      console.error('Failed to resume paused auth turn:', error);
      get().setChatErrorMessage(activeConversationId);
    }
  },

  retryFailedMessage: async (
    messageId: string,
    config: ConfigurationSettings,
    conversationId: string,
  ) => {
    const { conversations } = get();
    const conv = conversations[conversationId];
    if (!conv || !conv.messages) return;

    const isGroupChatParticipants = conv?.participants && conv.participants.length > 0;

    // Find the failed message
    const failedMessage = conv.messages.find(
      (msg) => msg.recordId === messageId && msg.deliveryStatus === MessageDeliveryStatus.FAILED
    );

    if (!failedMessage || failedMessage.role !== MessageRole.USER_CHAT) return;

    const generation = failedMessage.generations[0];
    if (!generation || !generation.content) return;

    // Update message status to PENDING
    set((state) => {
      const currentConv = state.conversations[conversationId];
      if (!currentConv) return state;

      const updatedMessages = currentConv.messages.map((msg) => {
        if (msg.recordId === messageId) {
          return {
            ...msg,
            deliveryStatus: MessageDeliveryStatus.PENDING,
          };
        }
        return msg;
      });

      return {
        conversations: {
          ...state.conversations,
          [conversationId]: {
            ...currentConv,
            messages: updatedMessages,
            isCurrentMessageLoading: isGroupChatParticipants ? false : true,
            isCurrentResponseGenerating: true,
          },
        },
      };
    });

    // Extract message data
    const content = generation.content;
    const attachments = generation.attachments || [];
    const mentions = generation.mentions || [];
    // const parentIds = generation.parentIds || [];

    const attachmentsMetadata: FileMetadata[] = attachments.map((att) => ({
      fileId: att.fileId,
      fileName: att.fileName,
      extension: att.extension,
      createdDate: new Date().toISOString(),
      lastUpdatedDate: new Date().toISOString(),
      size: att.size ?? 0,
      sender: att.sender,
    }));

    // Extract mention user IDs
    const mentionUserIds = mentions.map((m) => m.userId);

    scheduleAssistantStreamTimeout(
      conversationId,
      get().setChatErrorMessage,
      (id) => get().conversations[id]
    );

    // Retry sending the message - pass retryMessageId to prevent creating a new message
    try {
      await get().sendGroupChatStream(
        content,
        config,
        conversationId,
        attachmentsMetadata,
        null, // replyMessage
        mentionUserIds,
        false, // isRegenerate
        undefined, // originalRegenerationMessageId
        false, // imageGeneration
        [], // integrationSources
        false, // searchOnline
        messageId, // retryMessageId - tells sendGroupChatStream to update existing message instead of creating new one
        messageId // dummyUserMessageId - the dummy ID that needs to be replaced with server-assigned ID
      );
    } catch (error) {
      // Error handling is already done in sendGroupChatStream
      console.error('Failed to retry message:', error);
    }
  },


  temporaryConversation: (
    content: string,
    attachments?: Attachment[],
    isFeatureSelected?: Record<ButtonIds, boolean>
  ) => {
    const selectedModel = useModelStore.getState().selectedModel;
    const newFileIds = attachments?.map((item) => item.fileId) ?? [];
    const temporaryConversationId = uuidv4();

    const userGenerationId = uuidv4();
    const userSendShowTime = formatShowMessageTimeForSend();
    const userMessage: Message = {
      recordId: userGenerationId,
      role: MessageRole.USER,
      senderId: useCommonStore.getState().user?.id,
      generations: [
        {
          id: userGenerationId,
          content: content,
          createdDate: Date.now(),
          showMessageTime: userSendShowTime,
          attachments: attachments,
        },
      ],
    };

    const assistantMessageId = uuidv4();
    const newConvState = initialConversationState(temporaryConversationId, DEFAULT_CONVERSATION_NAME);
    newConvState.messages = [userMessage];
    newConvState.isCurrentMessageLoading = true;
    newConvState.isCurrentResponseGenerating = true;
    newConvState.hasFetchedInitialMessages = true;
    newConvState.modelName = selectedModel?.id;
    newConvState.allFileIds = newFileIds;
    newConvState.canCancelGeneration = false;
    newConvState.toolsState = isFeatureSelected ?? initialFeaturesState;

    // Add assistant message placeholder
    const newAssistantMessage: Message = {
      recordId: assistantMessageId,
      userRecordId: userMessage.generations[0].id,
      role: MessageRole.ASSISTANT,
      toolsStatus: isFeatureSelected?.[ButtonIds.SEARCH]
        ? {
          [Tools.SEARCH]: true,
          [Tools.CREATE_IMAGE]: false,
          [Tools.VOICE]: false,
        }
        : undefined,
      generations: [
        {
          id: assistantMessageId,
          content: '',
          createdDate: Date.now(),
          showMessageTime: formatShowMessageTimeForSend(),
          HasFileGeneratedResponse: false,
          isThinking: !!selectedModel?.isThinkingModel && !selectedModel?.isHarmonicModel && !selectedModel?.hasDynamicThinking,
          isAnalyzing: !!attachments && attachments.length > 0,
          isAnalyzed: false,
          usedModelId: selectedModel?.id,
          isThisGenerationOnGoing: true,
        },
      ],
    };
    newConvState.messages.push(newAssistantMessage);

    set((state) => ({
      activeConversationId: temporaryConversationId,
      conversations: {
        ...state.conversations,
        [temporaryConversationId]: newConvState,
      },
    }));

    return { temporaryConversationId, recordId: userMessage.generations[0].id, assistantMessageId };
  },

  replaceConversationId: (
    oldConversationId: string,
    newConversationId: string,
    isIncognitoChat: boolean
  ) => {
    set((state) => {
      const oldConversation = state.conversations[oldConversationId];

      if (!oldConversation) {
        console.warn(`Conversation ${oldConversationId} not found for replacement`);
        return {};
      }

      const newConversation: ConversationState = {
        ...oldConversation,
        id: newConversationId,
      };

      const updatedActiveConversationId =
        state.activeConversationId === oldConversationId
          ? newConversationId
          : state.activeConversationId;

      const remainingConversations = { ...state.conversations };
      delete remainingConversations[oldConversationId];

      // Incognito chat - do not update sidebar
      if (isIncognitoChat) {
        return {
          activeConversationId: updatedActiveConversationId,
          conversations: {
            ...remainingConversations,
            [newConversationId]: newConversation,
          },
        };
      }

      // Normal chat - update sidebar
      const sidebarItemIndex = state.sidebarConversationsList.findIndex(
        (item) => item.ConversationId === oldConversationId
      );

      let updatedSidebarList = [...state.sidebarConversationsList];

      if (sidebarItemIndex !== -1) {
        // Update existing sidebar entry with new conversationId
        updatedSidebarList[sidebarItemIndex] = {
          ...updatedSidebarList[sidebarItemIndex],
          ConversationId: newConversationId,
        };
      } else {
        // If not found in sidebar, add new entry
        const isoDate = new Date().toISOString();
        const currentUser = useCommonStore.getState().user;
        const agentId =
          oldConversation.agentId ??
          state.sidebarConversationsList.find((item) => item.ConversationId === oldConversationId)
            ?.AgentId;
        const newSidebarEntry: SidebarItem = {
          ConversationId: newConversationId,
          ConversationTitle: oldConversation.title || DEFAULT_CONVERSATION_NAME,
          Conversations: [],
          CreatedBy: currentUser?.id || '',
          CreatedDate: isoDate,
          LastUpdatedDate: isoDate,
          TenantId: '',
          IsGroup: false,
          AgentId: agentId,
        };
        updatedSidebarList = [newSidebarEntry, ...updatedSidebarList];
      }

      return {
        activeConversationId: updatedActiveConversationId,
        conversations: {
          ...remainingConversations,
          [newConversationId]: newConversation,
        },
        sidebarConversationsList: updatedSidebarList,
      };
    });
  },




  setActiveConversationId: (conversationId) => {
    // Sync hasParticipantsArray from cache when switching conversations
    const cachedParticipants = conversationId
      ? get().hasGroupChatParticipantsArray[conversationId]
      : undefined;
    const sidebarTitle = conversationId
      ? get().sidebarConversationsList.find((item) => item.ConversationId === conversationId)
          ?.ConversationTitle
      : undefined;
    const storeConversation = conversationId ? get().conversations[conversationId] : undefined;
    const storeTitle = storeConversation?.title;
    const currentUser = useCommonStore.getState().user;
    const isGroupChat =
      storeConversation?.isGroupChat === true ||
      (cachedParticipants !== undefined && cachedParticipants.length > 1);
    const nextHeaderTitle = conversationId
      ? resolveConversationDisplayTitle({
          storedTitle: storeTitle || sidebarTitle,
          participants: cachedParticipants,
          currentUser,
          hasManualTitle: storeConversation?.hasManualTitle,
          isGroupChat,
          fallback: '',
        })
      : '';

    set((state) => ({
      activeConversationId: conversationId,
      hasParticipantsArray: cachedParticipants || [],
      hasParticipant: (cachedParticipants?.length ?? 0) > 1,
      headerTitle: nextHeaderTitle,
      ...(conversationId && {
        conversations: {
          ...state.conversations,
          [conversationId]: {
            ...(state.conversations[conversationId] || initialConversationState(conversationId)),
            ...(cachedParticipants !== undefined && {
              participants: cachedParticipants || [],
            }),
            // Ensure modelName is always set
            modelName: state.conversations[conversationId]?.modelName || useModelStore.getState().selectedModel?.id || 'auto',
            // Set isRecentlyAdded to false when user clicks on the conversation
            isRecentlyAdded: false,
          },
        },
      }),
      hasAddedAssistantAsParticipant: !cachedParticipants || cachedParticipants.length === 0,
    }));
  },
  setActiveConversationModelName: (modelName: string) => {
    const conversationId = get().activeConversationId;
    if (!conversationId) return;

    const modelIds = new Set(useModelStore.getState().models.map((model) => model.id));

    set((state) => {
      const conversation = state.conversations[conversationId] || initialConversationState(conversationId);
      const previousKeys = conversation.invitationParticipantIds ?? [];
      const invitationParticipantIds = [
        ...previousKeys.filter((key) => !modelIds.has(key)),
        modelName,
      ];

      return {
        conversations: {
          ...state.conversations,
          [conversationId]: {
            ...conversation,
            modelName,
            invitationParticipantIds,
          },
        },
      };
    });
  },
  setActiveAudioMessageId: (messageId) => {
    set({
      activeAudioMessageId: messageId,
    });
  },
  setActiveConversation: async (conversationId, config: ConfigurationSettings, participants?: IamData[]) => {
    // console.log('conversationId in: setActiveConversation', conversationId)
    if (conversationId) {
      if (get().activeConversationId && get().activeConversationId !== conversationId) {
        return;
      }

      // Sync participants from cache or provided participants
      const cachedParticipants = get().hasGroupChatParticipantsArray[conversationId];
      const participantsToUse = participants || cachedParticipants || [];

      // Update hasParticipantsArray if we have participants
      if (participantsToUse.length > 0 || cachedParticipants !== undefined) {
        set({
          hasParticipantsArray: participantsToUse,
          hasParticipant: participantsToUse.length > 1,
          hasAddedAssistantAsParticipant: participantsToUse.length <= 1,
        });
      }

      const existingConv = get().conversations[conversationId];
      // console.log('existingConv:', existingConv)
      if (!existingConv) {
        // First time seeing this ID, create a placeholder and fetch
        set((state) => ({
          conversations: {
            ...state.conversations,
            [conversationId]: {
              ...initialConversationState(conversationId, 'Loading...', participantsToUse),
              participants: participantsToUse,
              // Set isRecentlyAdded to false when user clicks on the conversation
              isRecentlyAdded: false,
            },
          },
        }));
        await get().fetchAndSetConversationMessages(conversationId, config, participantsToUse);
      } else if (
        !existingConv.hasFetchedInitialMessages &&
        !existingConv.messages.some((m) => isUserMessageRole(m.role)) &&
        !(existingConv.isScheduledLiveStub && isConversationActivelyStreaming(existingConv))
      ) {
        // Exists but never fetched (e.g. sidebar click, or a scheduled-job stub that is not live-streaming)
        set((state) => ({
          conversations: {
            ...state.conversations,
            [conversationId]: {
              ...state.conversations[conversationId],
              participants: participantsToUse,
              // Ensure modelName is always set
              modelName: state.conversations[conversationId]?.modelName || useModelStore.getState().selectedModel?.id || 'auto',
              // Set isRecentlyAdded to false when user clicks on the conversation
              isRecentlyAdded: false,
            },
          },
        }));
        await get().fetchAndSetConversationMessages(conversationId, config, participantsToUse);
      } else {
        // Update participants on existing conversation
        set((state) => ({
          conversations: {
            ...state.conversations,
            [conversationId]: {
              ...state.conversations[conversationId],
              participants: participantsToUse,
              // Ensure modelName is always set
              modelName: state.conversations[conversationId]?.modelName || useModelStore.getState().selectedModel?.id || 'auto',
              // Set isRecentlyAdded to false when user clicks on the conversation
              isRecentlyAdded: false,
            },
          },
        }));
      }
    }
  },

  fetchAndSetGroupChatParticipants: async (conversationId: string, config: ConfigurationSettings, refetch?: boolean, loading?: boolean) => {

    
    if (!conversationId) {
      return;
    }

    const isStillActiveConversation = () => get().activeConversationId === conversationId;

    const p = get().hasGroupChatParticipantsArray;
    const existingParticipants = p[conversationId];
    const cachedConversation = get().conversations[conversationId];
    const isGroupChat = cachedConversation?.isGroupChat;

    // Fast path only for individual chats that were actually loaded before.
    // Placeholders created by setActiveConversationId default to isGroupChat: false
    // but have not been fetched yet — those must take the full fetch path below.
    const hasUserTurn = cachedConversation?.messages?.some((m) => isUserMessageRole(m.role));
    const isLoadedIndividualChat =
      !refetch &&
      !!cachedConversation &&
      cachedConversation.isGroupChat === false &&
      (!cachedConversation.isScheduledLiveStub ||
        isConversationActivelyStreaming(cachedConversation)) &&
      (cachedConversation.hasFetchedInitialMessages || hasUserTurn);

    if (isLoadedIndividualChat){
      if (existingParticipants && existingParticipants.length > 1) {
  
        set((state) => ({
          ...(isStillActiveConversation() && {
            hasParticipantsArray: existingParticipants,
            hasParticipant: true,
            hasAddedAssistantAsParticipant: false,
            isLoadingParticipants: false,
          }),
          hasGroupChatParticipantsArray: {
            ...get().hasGroupChatParticipantsArray,
            [conversationId]: existingParticipants,
          },
          conversations: {
            ...state.conversations,
            [conversationId]: {
              ...(state.conversations[conversationId] || initialConversationState(conversationId)),
              participants: existingParticipants,
              // Ensure modelName is always set
              modelName: state.conversations[conversationId]?.modelName || useModelStore.getState().selectedModel?.id || 'auto',
            },
          },
        }));
  
        if (isStillActiveConversation()) {
          await get().setActiveConversation(conversationId, config, existingParticipants);
        }
  
        return;
      }

      const individualParticipants =
        existingParticipants ??
        get().conversations[conversationId]?.participants ??
        [];

      if (isStillActiveConversation()) {
        set({
          hasParticipantsArray: individualParticipants,
          hasParticipant: individualParticipants.length > 1,
          hasAddedAssistantAsParticipant: individualParticipants.length <= 1,
          isLoadingParticipants: false,
        });

        await get().setActiveConversation(conversationId, config, individualParticipants);
      }

      const storedTitle =
        get().sidebarConversationsList.find((item) => item.ConversationId === conversationId)
          ?.ConversationTitle ?? get().conversations[conversationId]?.title;
      get().syncConversationTitleFromApi(conversationId, storedTitle, config);
      return;
    }else{

      const setConversationLoading = (value: boolean) => {
        set((state) => ({
          conversations: {
            ...state.conversations,
            [conversationId]: {
              ...(state.conversations[conversationId] || initialConversationState(conversationId)),
              isLoading: value,
            },
          },
        }));
      };
  
      try {
        const hasLocalMessages =
          (get().conversations[conversationId]?.messages?.length ?? 0) > 0;

        // Only block the chat UI on first load — background refetches should not hide messages.
        if (loading && !refetch && !hasLocalMessages) {
          setConversationLoading(true);
        }

        const groupChatParticipants = await getGroupChatParticipants(conversationId, config);
        if (!isStillActiveConversation()) {
          return;
        }

        const models = useModelStore
          .getState()
          .models
  
  
        if (groupChatParticipants && Object.keys(groupChatParticipants).length > 0) {
          const modelIds = models.map(model => model.id);
          const participantKeys = Object.keys(groupChatParticipants);
          // An agent participant is the responder, not a human — it must not push the
          // conversation into group mode, and its id drives the agent selector UI.
          const agentParticipantId = participantKeys.find(
            (key) => (groupChatParticipants[key] as { type?: string })?.type === 'agent',
          );
          if (agentParticipantId && !isDefaultMainChatAgentId(agentParticipantId)) {
            set((state) => ({
              conversations: {
                ...state.conversations,
                [conversationId]: {
                  ...(state.conversations[conversationId] || initialConversationState(conversationId)),
                  agentId: agentParticipantId,
                },
              },
            }));
          }
          const nonModelParticipants = participantKeys.filter(
            (key) =>
              !modelIds.includes(key) &&
              (groupChatParticipants[key] as { type?: string })?.type !== 'assistant',
          );
  
          // && modelParticipantKeys.length > 0
          if (nonModelParticipants.length > 1) {
            const usersDataResponse = await getUsers({
              page: 0,
              pageSize: 50,
              filter: {
                userIds: nonModelParticipants,
              },
            }, config);
  
            // console.log('usersDataResponse.data', usersDataResponse.data)
            get().setHasGroupChatParticipantsArray(conversationId, usersDataResponse.data);
            set((state) => ({
              conversations: {
                ...state.conversations,
                [conversationId]: {
                  ...(state.conversations[conversationId] || initialConversationState(conversationId)),
                  ...conversationModelPatch(
                    participantKeys,
                    state.conversations[conversationId]?.modelName,
                  ),
                },
              },
            }));
  
            if (isStillActiveConversation()) {
              await get().setActiveConversation(conversationId, config, usersDataResponse.data);
            }
  
          } else {
            set((state) => ({
              hasGroupChatParticipantsArray: {
                ...get().hasGroupChatParticipantsArray,
                [conversationId]: [],
              },
              ...(isStillActiveConversation() && {
                hasParticipantsArray: [],
                hasParticipant: false,
                hasAddedAssistantAsParticipant: true,
              }),
              conversations: {
                ...state.conversations,
                [conversationId]: {
                  ...(state.conversations[conversationId] || initialConversationState(conversationId)),
                  participants: [],
                  ...conversationModelPatch(
                    participantKeys,
                    state.conversations[conversationId]?.modelName,
                  ),
                },
              },
            }));
  
            if (isStillActiveConversation()) {
              await get().setActiveConversation(conversationId, config, []);
            }
          }
        } else if (isStillActiveConversation()) {
          await get().setActiveConversation(conversationId, config, []);
        }
      } catch (error) {
        console.error('Failed to fetch group chat participants:', error);
        const accessError = getConversationAccessErrorMessage(error);

        set((state) => {
          const existingConversation = state.conversations[conversationId];
          const cachedParticipants =
            state.hasGroupChatParticipantsArray[conversationId] ??
            existingConversation?.participants ??
            [];

          return {
            conversations: {
              ...state.conversations,
              [conversationId]: {
                ...(existingConversation || initialConversationState(conversationId)),
                isLoading: false,
                hasFetchedInitialMessages: true,
                isScheduledLiveStub: false,
                error: accessError,
              },
            },
            ...(isStillActiveConversation() && {
              hasParticipantsArray: cachedParticipants,
              hasParticipant: cachedParticipants.length > 1,
              hasAddedAssistantAsParticipant: cachedParticipants.length <= 1,
              isLoadingParticipants: false,
            }),
          };
        });
      } finally {
        // If callers opted into showing a loading state here, always turn it off once we're done.
        if (loading && !refetch) {
          setConversationLoading(false);
        }
      }

    }



  },

  fetchAndSetConversationMessages: async (conversationId, config: ConfigurationSettings, participants?: IamData[], options?: { silent?: boolean }) => {
    if (!conversationId) return;

    // Live socket stream already owns this thread once history has loaded.
    // A reconnect user prompt must not skip history — older turns still need fetching.
    const convForHistory = get().conversations[conversationId];
    if (
      !options?.silent &&
      isConversationActivelyStreaming(convForHistory) &&
      convForHistory?.hasFetchedInitialMessages
    ) {
      set((state) => {
        const conv = state.conversations[conversationId];
        if (!conv || (conv.hasFetchedInitialMessages && !conv.isScheduledLiveStub)) {
          return state;
        }
        return {
          conversations: {
            ...state.conversations,
            [conversationId]: {
              ...conv,
              hasFetchedInitialMessages: true,
              isScheduledLiveStub: false,
            },
          },
        };
      });
      return;
    }



    if (!participants || participants.length === 0 || participants.length <= 1) {
      get().setHasAddedAssistantAsParticipant(true);

      set((state) => ({

        conversations: {
          ...state.conversations,
          [conversationId]: {
            ...(state.conversations[conversationId] || initialConversationState(conversationId)),
            // silent: background reconciliation must not flash the history skeleton
            isLoading: options?.silent ? (state.conversations[conversationId]?.isLoading ?? false) : true,
            error: null,
            participants: participants || [],
          },

        },
      }));
      try {
        const baseUrl = `${config.paths.seliseAiBaseUrl}`;
        const url = `${baseUrl}${config.paths.seliseAi.getCollaborationMessages}`;

        // Also fetch conversation events so we can populate removedParticipants
        const [res, conversation] = await Promise.all([
          clients.post<{ data: any }>(url, config.blocksKey, JSON.stringify({
            conversationId: conversationId,
            pageSize: 100
          })),
          getConversationById(conversationId, config),
        ]);

        const allEvents: ConversationEvent[] = conversation?.events || [];
        const hasManualTitle = allEvents.some(
          (event) => event.eventType === 'CONVERSATION_TITLE_UPDATED'
        );
        const removedEvents = allEvents.filter(
          (event: any) => event.eventType === 'USER_REMOVED'
        );
        const removedIds = new Set<string>();
        removedEvents.forEach((event: any) => {
          if (event?.targetIds && Array.isArray(event.targetIds)) {
            event.targetIds.forEach((id: string) => removedIds.add(id));
          }
        });

        if (removedIds.size > 0) {
          const removedUsersRes = await get().fetchUsersData(Array.from(removedIds), config);
          const fetchedRemovedUsers = removedUsersRes?.data || [];

          set((state) => {
            const existing = state.conversations[conversationId]?.removedParticipants || [];
            const mergedRemovedParticipants = [
              ...existing,
              ...fetchedRemovedUsers.filter(
                (u) => !existing.some((e) => e.itemId === u.itemId)
              ),
            ];
            return {
              conversations: {
                ...state.conversations,
                [conversationId]: {
                  ...(state.conversations[conversationId] || initialConversationState(conversationId)),
                  removedParticipants: mergedRemovedParticipants,
                },
              },
            };
          });
        }


        const fetchedMessages: Message[] = [];
        const conversationTitle = stripConversationDisplayQuotes(conversation?.conversationTitle ?? '');
        const allFileIds: string[] = [];
        const currentUser = useCommonStore.getState().user;
        const currentUserId = currentUser?.id;
        if (res && res.data && Array.isArray(res.data)) {
          const messages = res.data;

          // Sort messages by createdDate (oldest first)
          const sortedMessages = [...messages].sort((a, b) =>
            new Date(a.createdDate).getTime() - new Date(b.createdDate).getTime()
          );

          // const assistantMessage = sortedMessages.filter((msg: any) => msg.type === 'assistant');
          // // console.log('assistantMessage', assistantMessage) 
          // if (assistantMessage?.length > 0 && assistantMessage[assistantMessage.length - 1].senderId) {
          //   modelName = assistantMessage[assistantMessage.length - 1].senderId;
          //   isThinkingModel = useModelStore
          //     .getState()
          //     .models.some((model) => model.id === modelName && model.isThinkingModel);
          // }


          // Ensure modelName is always set - use fallback if not found in messages

          // if (!modelName || modelName === '') {
          //   const selectedModel = useModelStore.getState().selectedModel;
          //   modelName = selectedModel?.id || 'auto';
          // }



          const messageIdToRecordIdMap = new Map<string, string>();
          const recordIdToMessageIndexMap = new Map<string, number>();
          const a2uiSubmissions = collectA2uiSubmissions(sortedMessages);
          const a2uiApprovals = collectA2uiApprovals(sortedMessages);

          const latestUserMessageIndex = (): number => {
            for (let i = fetchedMessages.length - 1; i >= 0; i -= 1) {
              if (fetchedMessages[i].role === MessageRole.USER) return i;
            }
            return -1;
          };

          const resolveOriginalUserMessageIndex = (originalId: string): number => {
            let walkId = originalId;
            const seen = new Set<string>();

            while (walkId && !seen.has(walkId)) {
              seen.add(walkId);

              const directMatch = findUserMessageForLinkedId(fetchedMessages, walkId);
              if (directMatch) {
                return fetchedMessages.findIndex((m) => m.recordId === directMatch.recordId);
              }

              const mappedRecordId = messageIdToRecordIdMap.get(walkId);
              if (mappedRecordId) {
                const index = fetchedMessages.findIndex((m) => m.recordId === mappedRecordId);
                if (index !== -1) return index;
              }

              const linkedAssistant = fetchedMessages.find(
                (m) =>
                  m.role === MessageRole.ASSISTANT &&
                  (m.userRecordId === walkId ||
                    m.generations.some((g) => g.parentUserGenerationId === walkId))
              );
              if (linkedAssistant?.userRecordId) {
                const userMsg = findUserMessageForLinkedId(
                  fetchedMessages,
                  linkedAssistant.userRecordId
                );
                if (userMsg) {
                  return fetchedMessages.findIndex((m) => m.recordId === userMsg.recordId);
                }
              }

              const apiParent = sortedMessages.find((m: { id?: string }) => m.id === walkId);
              walkId = apiParent?.originalMessageId ? String(apiParent.originalMessageId) : '';
            }

            // Backend can store a client dummy id that never appears in history.
            // originalMessageId still means this is an edit of the current user thread.
            return latestUserMessageIndex();
          };

          const mapRelatedUserMessageToThread = (
            relatedUserMessageId: string | undefined,
            threadAnchorId: string | undefined,
            parentAssistantRecordId: string | null
          ) => {
            if (!relatedUserMessageId) return;

            const directUserMessage = findUserMessageForLinkedId(
              fetchedMessages,
              relatedUserMessageId
            );
            if (directUserMessage) {
              messageIdToRecordIdMap.set(relatedUserMessageId, directUserMessage.recordId);
              return;
            }

            const anchorId =
              threadAnchorId ||
              (parentAssistantRecordId
                ? fetchedMessages.find((m) => m.recordId === parentAssistantRecordId)?.userRecordId
                : undefined);

            if (!anchorId) return;

            const userThread = findUserMessageForLinkedId(fetchedMessages, anchorId);
            if (userThread) {
              messageIdToRecordIdMap.set(relatedUserMessageId, userThread.recordId);
            }
          };

          for (const msg of sortedMessages) {

            const isUserMessage = msg.type === 'user'
            const isAssistantMessage = msg.type === 'assistant' || msg.type === 'agent';

            const attachments: Attachment[] = [];
            const attachmentIds: string[] = [];

            if (msg.attachments && Array.isArray(msg.attachments) && msg.attachments.length > 0) {
              for (const attachment of msg.attachments) {
                if (typeof attachment === 'string') {
                  attachmentIds.push(attachment);
                } else if (attachment && typeof attachment === 'object') {
                  if (attachment.fileId) {
                    attachments.push({
                      fileId: attachment.fileId,
                      fileName: attachment.fileName || `file_${attachment.fileId}`,
                      extension: attachment.extension || 'unknown',
                      sender: isUserMessage ? MessageRole.USER : MessageRole.ASSISTANT,
                    });
                    allFileIds.push(attachment.fileId);
                  } else if (attachment.FileId) {
                    // Handle camelCase variant
                    attachments.push({
                      fileId: attachment.FileId,
                      fileName: attachment.FileName || `file_${attachment.FileId}`,
                      extension: attachment.Extension || attachment.extension || 'unknown',
                      sender: isUserMessage ? MessageRole.USER : MessageRole.ASSISTANT,
                    });
                    allFileIds.push(attachment.FileId);
                  }
                }
              }
            }

            if (msg.metadata?.files && Array.isArray(msg.metadata.files) && msg.metadata.files.length > 0) {
              msg.metadata.files.forEach((file: any) => {
                if (file.fileId || file.FileId) {
                  attachmentIds.push(file.fileId || file.FileId);
                }
              });
            }
            let sources: Source[] = [];
            let searchSources: SearchSource[] = [];
            const generatedImages: Attachment[] = [];
            let hasFileGeneratedResponse = false;
            let usedModelId = '';

            if (isAssistantMessage) {
              const modelFromMetadata =
                msg.metadata?.selected_model ??
                msg.metadata?.selectedModel ??
                msg.metadata?.autoSelectedModel ??
                msg.metadata?.model ??
                msg.metadata?.model_name ??
                msg.metadata?.modelName ??
                msg.metadata?.model_id ??
                msg.metadata?.modelId ??
                msg.metadata?.agentRun?.model ??
                msg.metadata?.agentRun?.Model ??
                msg.metadata?.AgentRun?.Model ??
                msg.metadata?.AgentRun?.model ??
                msg.metadata?.agent_run?.model ??
                msg.metadata?.agent_run?.Model;

              if (modelFromMetadata) {
                usedModelId = modelFromMetadata;
              } else if (msg.senderId && !isDefaultMainChatAgentId(msg.senderId)) {
                usedModelId = msg.senderId;
              }

              if (msg.metadata) {
                // Process sources
                if (msg.metadata.sources && Array.isArray(msg.metadata.sources) && msg.metadata.sources.length > 0) {
                  sources = msg.metadata.sources.map(normalizeSourceFromWire);
                }

                if (msg.metadata.searchSources && Array.isArray(msg.metadata.searchSources) && msg.metadata.searchSources.length > 0) {
                  searchSources = msg.metadata.searchSources.map((source: any) => ({
                    pageContent: source.PageContent || source.pageContent || '',
                    metadata: {
                      title: source.Metadata?.Title || source.metadata?.title || '',
                      url: source.Metadata?.Url || source.metadata?.url || '',
                    },
                  }));
                }

                if (msg.metadata.files && Array.isArray(msg.metadata.files) && msg.metadata.files.length > 0) {
                  for (const file of msg.metadata.files) {
                    if (file.fileId || file.FileId) {
                      const fileId = file.fileId || file.FileId;
                      generatedImages.push({
                        fileId: fileId,
                        fileName: file.fileName || file.FileName || `file_${fileId}`,
                        extension: file.extension || file.Extension || 'unknown',
                        sender: MessageRole.ASSISTANT,
                        title: file.title ?? file.Title ?? undefined,
                        version: file.version ?? file.Version ?? undefined,
                      });
                      allFileIds.push(fileId);
                    }
                  }
                }

                hasFileGeneratedResponse = msg.metadata.hasFileGeneratedResponse || false;
              }
            }

            let content = msg.content || '';
            let thinkContent = '';

            // const modelUsed = useModelStore.getState().models.find(model => model.id === usedModelId);
            // const isThinking = modelUsed ? modelUsed.isThinkingModel || modelUsed.hasDynamicThinking || modelUsed.isHarmonicModel : false;

            if (isAssistantMessage) {
              const parsedContent = parseThinkingContent(content);
              thinkContent = parsedContent.thinkContent;
              content = parsedContent.responseContent;
            }

            content = extractLastResponseContent(content);

            // Helper function to get parentMessage from parentIds
            const getParentMessage = (): { senderName: string; content: string; time: number } | undefined => {
              if (!msg.parentIds || !Array.isArray(msg.parentIds) || msg.parentIds.length === 0) {
                return undefined;
              }

              const parentId = msg.parentIds[0];
              const parentMsg = sortedMessages.find((m: any) => m.id === parentId);

              if (!parentMsg) return undefined;

              // Get sender name from participants, removedParticipants, or senderId
              let senderName = 'User';
              if (parentMsg.senderId) {
                // Check participants first
                if (participants && participants.length > 0) {
                  const user = participants.find(p => p.itemId === parentMsg.senderId);
                  if (user) {
                    senderName = `${user.firstName}${user.lastName ? ` ${user.lastName}` : ''}`;
                  }
                }

                // Check removedParticipants if not found in participants
                if (senderName === 'User') {
                  const currentConv = get().conversations[conversationId];
                  const removedParticipants = currentConv?.removedParticipants || [];
                  const removedUser = removedParticipants.find(p => p.itemId === parentMsg.senderId);
                  if (removedUser) {
                    senderName = `${removedUser.firstName}${removedUser.lastName ? ` ${removedUser.lastName}` : ''}`;
                  }
                }

                // Fallback to current user if senderId matches
                if (senderName === 'User' && currentUserId === parentMsg.senderId) {
                  senderName = `${currentUser?.firstName || ''}${currentUser?.lastName ? ` ${currentUser.lastName}` : ''}`.trim() || 'User';
                }
              }

              return {
                senderName: senderName,
                content: parentMsg.content || '',
                time: timestampFromBackendCreatedDate(parentMsg.createdDate),
              };
            };

            const parentMessage = getParentMessage();

            if (isUserMessage) {
              const backendShow = showMessageTimeFromBackend(msg.createdDate);
              const userGeneration = {
                id: msg.id,
                content: content,
                createdDate: new Date(msg.createdDate).getTime() || Date.now(),
                ...(backendShow && { showMessageTime: backendShow }),
                attachments: attachments,
                ...(msg.parentIds && Array.isArray(msg.parentIds) && msg.parentIds.length > 0 && { parentIds: msg.parentIds }),
                ...(parentMessage && { parentMessage }),
              };

              // Check if this message has an originalMessageId (edited message)
              if (msg.originalMessageId) {
                const originalMessageIndex = resolveOriginalUserMessageIndex(msg.originalMessageId);

                if (originalMessageIndex !== -1) {
                  // Add as a new generation to the original message
                  fetchedMessages[originalMessageIndex].generations.push(userGeneration);
                  // Update activeGenerationIndex to show the latest generation
                  fetchedMessages[originalMessageIndex].activeGenerationIndex = 
                    fetchedMessages[originalMessageIndex].generations.length - 1;
                  // Store mapping using the original message's recordId
                  messageIdToRecordIdMap.set(msg.id, fetchedMessages[originalMessageIndex].recordId);
                } else {
                  // Original message not found yet, create new message
                  // This shouldn't happen if messages are sorted by createdDate correctly
                  fetchedMessages.push({
                    recordId: msg.id,
                    role: MessageRole.USER,
                    senderId: msg.senderId,
                    generations: [userGeneration],
                  });
                  messageIdToRecordIdMap.set(msg.id, msg.id);
                  recordIdToMessageIndexMap.set(msg.id, fetchedMessages.length - 1);
                }
              } else {
                // No originalMessageId - create new user message
                fetchedMessages.push({
                  recordId: msg.id,
                  role: MessageRole.USER,
                  senderId: msg.senderId,
                  generations: [userGeneration],
                });
                // Store mapping for parent lookup
                messageIdToRecordIdMap.set(msg.id, msg.id);
                recordIdToMessageIndexMap.set(msg.id, fetchedMessages.length - 1);
              }
            } else if (isAssistantMessage) {
              // Extract feedback from reactions

              const feedback = extractFeedbackFromReactions(msg.reactions, currentUserId);

              // Find parentUserGenerationId
              let parentUserGenerationId: string | undefined;
              const hasParentIds = msg.parentIds && Array.isArray(msg.parentIds) && msg.parentIds.length > 0;

              if (hasParentIds) {
                // First check if parentIds points to a user message generation
                for (const parentId of msg.parentIds) {
                  const parentUserMessage = fetchedMessages.find(m =>
                    m.role === MessageRole.USER &&
                    m.generations.some(g => g.id === parentId)
                  );
                  if (parentUserMessage) {
                    parentUserGenerationId = parentId;
                    break;
                  }
                }

                // If not found in user messages, check if parentIds points to an assistant message
                // and inherit parentUserGenerationId from it (regeneration case)
                if (!parentUserGenerationId) {
                  for (const parentId of msg.parentIds) {
                    const parentAssistantMessage = fetchedMessages.find(m =>
                      m.role === MessageRole.ASSISTANT &&
                      m.generations.some(g => g.id === parentId)
                    );
                    if (parentAssistantMessage) {
                      // Find the generation and inherit its parentUserGenerationId
                      const parentGeneration = parentAssistantMessage.generations.find(g => g.id === parentId);
                      if (parentGeneration?.parentUserGenerationId) {
                        parentUserGenerationId = parentGeneration.parentUserGenerationId;
                      }
                      break; // Stop here, don't fall back to relatedUserMessageIds for regeneration
                    }
                  }
                }
              } else {
                // No parentIds - use relatedUserMessageIds from metadata
                if (msg.metadata?.relatedUserMessageIds?.[0]) {
                  const relatedUserId = msg.metadata.relatedUserMessageIds[0];
                  // Verify it exists in user messages
                  const relatedUserMessage = fetchedMessages.find(m =>
                    m.role === MessageRole.USER &&
                    m.generations.some(g => g.id === relatedUserId)
                  );
                  if (relatedUserMessage) {
                    parentUserGenerationId = relatedUserId;
                  }
                }
              }

              const backendShow = showMessageTimeFromBackend(msg.createdDate);
              const toolCalls = parseToolCallsFromMetadata(msg.metadata);
              const skillEvents = parseSkillEventsFromMetadata(msg.metadata);
              const subagentRuns = parseSubagentRunsFromMetadata(msg.metadata);
              const a2uiSurfaces = parseA2uiSurfacesFromMetadata(msg.metadata, a2uiSubmissions, a2uiApprovals);
              const generation: MessageGeneration = {
                id: msg.id,
                content: content,
                createdDate: new Date(msg.createdDate).getTime() || Date.now(),
                ...(backendShow && { showMessageTime: backendShow }),
                attachments: attachments,
                
                isThisGenerationOnGoing: false,
                ...(msg.metadata?.isCancelled && { isCancelled: true }),
                HasFileGeneratedResponse: hasFileGeneratedResponse,
                generatedImages: generatedImages.length > 0 ? generatedImages : undefined,
                sources: sources.length > 0 ? sources : undefined,
                searchSources: searchSources.length > 0 ? searchSources : undefined,
                feedback: feedback,
                ...(msg.parentIds && Array.isArray(msg.parentIds) && msg.parentIds.length > 0 && { parentIds: msg.parentIds }),
                ...(parentMessage && { parentMessage }),
                usedModelId: usedModelId,
                ...(msg.metadata && { metadata: msg.metadata }),
                ...(parentUserGenerationId && { parentUserGenerationId }),
                ...(toolCalls && { toolCalls }),
                ...(skillEvents && { skillEvents }),
                ...(subagentRuns && { subagentRuns }),
                ...(a2uiSurfaces && { a2uiSurfaces }),
              };

              if (thinkContent) {
                generation.thinkContent = thinkContent;
              }


              // const targetRecordId = messageRecordId;
              const userRecordIdForAssistant = msg.metadata?.relatedUserMessageIds?.[0];

              // Check if parentIds point to an assistant message (for grouping generations)
              let parentAssistantRecordId: string | null = null;
              if (msg.parentIds && Array.isArray(msg.parentIds) && msg.parentIds.length > 0) {
                for (const parentId of msg.parentIds) {
                  // First check if parentId is a message ID we've already mapped
                  if (messageIdToRecordIdMap.has(parentId)) {
                    const mappedRecordId = messageIdToRecordIdMap.get(parentId)!;
                    // Check if the parent is an assistant message
                    const parentMessage = fetchedMessages.find(m => m.recordId === mappedRecordId);
                    if (parentMessage && parentMessage.role === MessageRole.ASSISTANT) {
                      parentAssistantRecordId = mappedRecordId;
                      break;
                    }
                  } else {
                    // Check if parentId matches any generation ID in existing assistant messages
                    const parentMessage = fetchedMessages.find(m =>
                      m.role === MessageRole.ASSISTANT &&
                      m.generations.some(g => g.id === parentId)
                    );
                    if (parentMessage) {
                      parentAssistantRecordId = parentMessage.recordId;
                      // Store the mapping for future lookups
                      messageIdToRecordIdMap.set(parentId, parentMessage.recordId);
                      break;
                    }
                  }
                }
              }

              // If parentIds point to an assistant message (regeneration), add as generation to that assistant
              if (parentAssistantRecordId) {
                const parentAssistantIndex = fetchedMessages.findIndex(
                  (m) => m.recordId === parentAssistantRecordId
                );
                if (parentAssistantIndex !== -1) {
                  fetchedMessages[parentAssistantIndex].generations.push(generation);
                  fetchedMessages[parentAssistantIndex].activeGenerationIndex = 
                    fetchedMessages[parentAssistantIndex].generations.length - 1;
                  // Store mapping using the parent's recordId so future messages can find it
                  messageIdToRecordIdMap.set(msg.id, parentAssistantRecordId);
                } else {
                  // Parent not found yet, create new message (shouldn't happen if sorted correctly)
                  fetchedMessages.push({
                    role: MessageRole.ASSISTANT,
                    recordId: generation.id,
                    userRecordId: userRecordIdForAssistant || parentUserGenerationId,
                    activeGenerationIndex: 0,
                    generations: [generation],
                    senderId: msg.senderId,
                    ...(msg.parentIds && Array.isArray(msg.parentIds) && msg.parentIds.length > 0 && { parentIds: msg.parentIds }),
                  });
                  recordIdToMessageIndexMap.set(generation.id, fetchedMessages.length - 1);
                  messageIdToRecordIdMap.set(msg.id, generation.id);
                }
              } else {
                // Check if relatedUserMessageIds points to a user generation that's part of an edited message
                // If so, find the assistant that responds to that user Message and add as generation
                let existingAssistantForUserMessage: Message | undefined;
                
                if (userRecordIdForAssistant) {
                  let userMessageWithGeneration = findUserMessageForLinkedId(
                    fetchedMessages,
                    userRecordIdForAssistant
                  );

                  if (!userMessageWithGeneration) {
                    const mappedRecordId = messageIdToRecordIdMap.get(userRecordIdForAssistant);
                    if (mappedRecordId) {
                      userMessageWithGeneration = fetchedMessages.find(
                        (m) => m.recordId === mappedRecordId
                      );
                    }
                  }
                  
                  if (userMessageWithGeneration) {
                    const targetUserRecordId = userMessageWithGeneration.recordId;
                    existingAssistantForUserMessage = fetchedMessages.find(
                      (m) =>
                        m.role === MessageRole.ASSISTANT &&
                        resolveUserMessageRecordIdForAssistant(fetchedMessages, m) ===
                          targetUserRecordId
                    );
                  }
                }

                if (existingAssistantForUserMessage) {
                  // Add generation to existing assistant message (edit scenario)
                  const existingAssistantIndex = fetchedMessages.findIndex(
                    m => m.recordId === existingAssistantForUserMessage!.recordId
                  );
                  if (existingAssistantIndex !== -1) {
                    fetchedMessages[existingAssistantIndex].generations.push(generation);
                    fetchedMessages[existingAssistantIndex].activeGenerationIndex = 
                      fetchedMessages[existingAssistantIndex].generations.length - 1;
                    messageIdToRecordIdMap.set(msg.id, existingAssistantForUserMessage.recordId);
                  }
                } else {
                  // Regenerate responses can reference stale parent/related ids — attach to the
                  // assistant row for the latest user generation before this reply.
                  const assistantTime = new Date(msg.createdDate).getTime() || Date.now();
                  let anchorUserRecordId: string | undefined;
                  let latestUserGenTime = -1;
                  for (const userMessage of fetchedMessages) {
                    if (userMessage.role !== MessageRole.USER) continue;
                    for (const userGeneration of userMessage.generations) {
                      const generationTime = userGeneration.createdDate ?? 0;
                      if (generationTime <= assistantTime && generationTime >= latestUserGenTime) {
                        latestUserGenTime = generationTime;
                        anchorUserRecordId = userMessage.recordId;
                        if (!parentUserGenerationId) {
                          parentUserGenerationId = userGeneration.id;
                          generation.parentUserGenerationId = userGeneration.id;
                        }
                      }
                    }
                  }
                  if (anchorUserRecordId) {
                    const assistantForUserTurn = fetchedMessages.find(
                      (m) =>
                        m.role === MessageRole.ASSISTANT &&
                        resolveUserMessageRecordIdForAssistant(fetchedMessages, m) ===
                          anchorUserRecordId
                    );
                    if (assistantForUserTurn) {
                      const assistantIndex = fetchedMessages.findIndex(
                        (m) => m.recordId === assistantForUserTurn.recordId
                      );
                      if (assistantIndex !== -1) {
                        fetchedMessages[assistantIndex].generations.push(generation);
                        fetchedMessages[assistantIndex].activeGenerationIndex =
                          fetchedMessages[assistantIndex].generations.length - 1;
                        messageIdToRecordIdMap.set(msg.id, assistantForUserTurn.recordId);
                        mapRelatedUserMessageToThread(
                          userRecordIdForAssistant,
                          parentUserGenerationId,
                          parentAssistantRecordId
                        );
                        continue;
                      }
                    }
                  }

                  // Create new message entry
                  fetchedMessages.push({
                    role: MessageRole.ASSISTANT,
                    recordId: generation.id,
                    userRecordId: userRecordIdForAssistant || parentUserGenerationId,
                    activeGenerationIndex: 0,
                    generations: [generation],
                    senderId: msg.senderId,
                    ...(msg.parentIds && Array.isArray(msg.parentIds) && msg.parentIds.length > 0 && { parentIds: msg.parentIds }),
                  });
                  recordIdToMessageIndexMap.set(generation.id, fetchedMessages.length - 1);
                  messageIdToRecordIdMap.set(msg.id, generation.id);
                }
              }

              mapRelatedUserMessageToThread(
                userRecordIdForAssistant,
                parentUserGenerationId,
                parentAssistantRecordId
              );
            }
          }
        }

        // console.log('fetchedMessages in fetchCollaborationMessages', fetchedMessages)
        set((state) => {
          const existing = state.conversations[conversationId];
          const fetched = applyEmptyAssistantContentFallback(
            hydrateMessageStreamBlocks(consolidateAssistantMessages(fetchedMessages)),
          );
          const hasContentFilterViolation = existing?.messages.some(
            (msg) =>
              msg.role === MessageRole.ASSISTANT &&
              msg.generations.some((g) => g.isContentFilterViolation)
          );
          const hasStreamingAssistant = existing?.messages.some(
            (msg) =>
              msg.role === MessageRole.ASSISTANT &&
              msg.generations.some((g) => g.isThisGenerationOnGoing)
          );
          const keepLiveReconnect =
            (existing?.isCurrentResponseGenerating && hasStreamingAssistant) ||
            hasContentFilterViolation ||
            ((existing?.messages.length ?? 0) > 0 && fetched.length === 0);
          return {
          conversations: {
            ...state.conversations,
            [conversationId]: {
              ...(existing || initialConversationState(conversationId)),
              messages: keepLiveReconnect
                ? preferLiveReconnectMessages(fetched, existing.messages)
                : fetched,
              title: conversationTitle,
              isLoading: false, // Done loading history
              hasFetchedInitialMessages: true,
              isScheduledLiveStub: false,
              error: null,
              // modelName: modelName,
              allFileIds: allFileIds,
              isGroupChat: false,
              hasManualTitle,
              participants: participants || [],
              customGptId: conversation?.customGptId,
            },
          },
          };
        });
        get().syncConversationTitleFromApi(conversationId, conversation?.conversationTitle, config);
        get().flushPendingStreamingNotifications(conversationId, config);
      } catch (error) {
        console.error('Failed to fetch conversation:', error);

        set((state) => ({
          conversations: {
            ...state.conversations,
            [conversationId]: {
              ...(state.conversations[conversationId] || initialConversationState(conversationId)),
              isLoading: false,
              hasFetchedInitialMessages: true,
              isScheduledLiveStub: false,
              error: getConversationAccessErrorMessage(error),
              participants: participants || [],
            },
          },
        }));
        get().flushPendingStreamingNotifications(conversationId, config);
      }

    } else {
      // console.log('participants in fetchAndSetConversationMessages found', participants)

      get().setHasAddedAssistantAsParticipant(false);
      set((state) => ({
        conversations: {
          ...state.conversations,
          [conversationId]: {
            ...(state.conversations[conversationId] || initialConversationState(conversationId)),
            // silent: background reconciliation must not flash the history skeleton
            isLoading: options?.silent ? (state.conversations[conversationId]?.isLoading ?? false) : true,
            error: null,
            participants: participants || [],
            // Ensure modelName is always set
            // modelName: state.conversations[conversationId]?.modelName || useModelStore.getState().selectedModel?.id || 'auto',
          },
        },
      }));

      try {
        const baseUrl = `${config.paths.seliseAiBaseUrl}`;
        const url = `${baseUrl}${config.paths.seliseAi.getCollaborationMessages}`;

        const [res, events] = await Promise.all([
          clients.post<{ data: any }>(url, config.blocksKey, JSON.stringify({
            "conversationId": conversationId,
            "pageSize": 100
          })),
          getConversationById(conversationId, config)
        ]);
        const allEvents: ConversationEvent[] = events.events || [];
        const hasManualTitle = allEvents.some(
          (event) => event.eventType === 'CONVERSATION_TITLE_UPDATED'
        );
        const systemMessages: Message[] = [];
        let allRemovedParticipants: IamData[] = [];

        if (Array.isArray(allEvents) && allEvents.length > 0) {
          const userAddedEvent = allEvents.filter((event: any) => event.eventType === 'USER_ADDED');
          const userAddedByTokenEvent = allEvents.filter((event: any) => event.eventType === 'USER_ADDED_BY_TOKEN');
          const conversationTitleUpdatedEvent = allEvents.filter((event: any) => event.eventType === 'CONVERSATION_TITLE_UPDATED');
          const conversationCreatedEvent = allEvents.filter((event: any) => event.eventType === 'CONVERSATION_CREATED');

          const removedParticipantEvent = allEvents.filter((event: any) => event.eventType === 'USER_REMOVED');
          const participantIdsToFetch = new Set<string>();
          const existingParticipantIds = new Set(participants.map(p => p.itemId));

          if (conversationCreatedEvent.length > 0) {
            const conversationCreatedMessages = conversationCreatedEvent.map((event: any) => {
              const createdBy = participants.find((participant: any) => participant.itemId === event.actorId) || 
                (useCommonStore.getState().user?.id === event.actorId ? useCommonStore.getState().user : null);
              const createdByName = (() => {
                const firstName = createdBy?.firstName?.trim?.() ?? '';
                const lastName = createdBy?.lastName?.trim?.() ?? '';
                if (firstName || lastName) return `${firstName}${lastName ? ` ${lastName}` : ''}`;
                const emailPrefix =
                  typeof createdBy?.email === 'string' ? createdBy.email.split('@')[0] : '';
                return emailPrefix || 'Someone';
              })();
              const messageContent = `${createdByName} created the conversation`;
              const generationId = uuidv4();
              const evtShow = showMessageTimeFromBackend(event.createdDate);
              return {
                recordId: generationId,
                role: MessageRole.CONVERSATION_CREATED,
                generations: [
                  {
                    id: generationId,
                    content: messageContent,
                    createdDate: new Date(event.createdDate).getTime(),
                    ...(evtShow && { showMessageTime: evtShow }),
                  },
                ],
              };
            });
            systemMessages.push(...conversationCreatedMessages);
          }
          userAddedEvent.forEach((event: any) => {
            if (event.targetIds && Array.isArray(event.targetIds)) {
              event.targetIds.forEach((id: string) => {
                if (!existingParticipantIds.has(id)) {
                  participantIdsToFetch.add(id);
                }
              });
            }
          });
          userAddedByTokenEvent.forEach((event: any) => {
            if (event.targetIds && Array.isArray(event.targetIds)) {
              event.targetIds.forEach((id: string) => {
                if (!existingParticipantIds.has(id)) {
                  participantIdsToFetch.add(id);
                }
              });
            }
          });

          removedParticipantEvent.forEach((event: any) => {
            if (event.targetIds && Array.isArray(event.targetIds)) {
              event.targetIds.forEach((id: string) => participantIdsToFetch.add(id));
            }
          });

          const fetchedParticipantsMap = new Map<string, IamData>();
          if (participantIdsToFetch.size > 0) {
            const allFetchedParticipantsResponse = await get().fetchUsersData(Array.from(participantIdsToFetch), config);
            const allFetchedParticipants = allFetchedParticipantsResponse?.data || [];
            allFetchedParticipants.forEach((p: IamData) => {
              fetchedParticipantsMap.set(p.itemId, p);
            });
          }

          const addedParticipantIds = new Set<string>();
          userAddedEvent.forEach((event: any) => {
            if (event.targetIds && Array.isArray(event.targetIds)) {
              event.targetIds.forEach((id: string) => {
                if (!existingParticipantIds.has(id)) {
                  addedParticipantIds.add(id);
                }
              });
            }
          });

          userAddedByTokenEvent.forEach((event: any) => {
            if (event.targetIds && Array.isArray(event.targetIds)) {
              event.targetIds.forEach((id: string) => {
                if (!existingParticipantIds.has(id)) {
                  addedParticipantIds.add(id);
                }
              });
            }
          });

          const removedParticipantIds = new Set<string>();
          removedParticipantEvent.forEach((event: any) => {
            if (event.targetIds && Array.isArray(event.targetIds)) {
              event.targetIds.forEach((id: string) => removedParticipantIds.add(id));
            }
          });

          allRemovedParticipants = Array.from(removedParticipantIds)
            .map(id => fetchedParticipantsMap.get(id))
            .filter((p): p is IamData => p !== undefined);

          const addParticipantMessages = userAddedEvent.map((event: any) => {
            const addedBy = participants.find((participant: any) => participant.itemId === event.actorId);

            // Get participant data from existing participants or fetched data
            let participantData: any[] = participants.filter((participant: any) =>
              event.targetIds?.includes(participant.itemId)
            );

            // If not found in existing participants, get from fetched data
            if (participantData.length === 0) {
              participantData = event.targetIds
                ?.map((id: string) => fetchedParticipantsMap.get(id))
                .filter((p: IamData | undefined): p is IamData => p !== undefined) || [];
            }

            const addParticipantNames = participantData
              .map((participant: any) => participant.firstName)
              .filter((name: string) => name)
              .join(', ');

            const messageContent = `${getUserDisplayName(addedBy, 'Someone')} added ${addParticipantNames || 'users'}`;
            const generationId = uuidv4();
            const evtShow = showMessageTimeFromBackend(event.createdDate);
            return {
              recordId: generationId,
              role: MessageRole.ADD_PARTICIPANT,
              generations: [
                {
                  id: generationId,
                  content: messageContent,
                  createdDate: new Date(event.createdDate).getTime(),
                  ...(evtShow && { showMessageTime: evtShow }),
                },
              ],
            };
          });
          systemMessages.push(...addParticipantMessages);

          // Handle USER_ADDED_BY_TOKEN events - show "joined" messages
          if (userAddedByTokenEvent.length > 0) {
            const joinedByTokenMessages: Message[] = [];
            
            userAddedByTokenEvent.forEach((event: any) => {
              const eventDate = new Date(event.createdDate);
              const evtShow = showMessageTimeFromBackend(event.createdDate);
              
              // Get participant data from existing participants or fetched data
              let participantData: any[] = participants.filter((participant: any) =>
                event.targetIds?.includes(participant.itemId)
              );

              // If not found in existing participants, get from fetched data
              if (participantData.length === 0) {
                participantData = event.targetIds
                  ?.map((id: string) => fetchedParticipantsMap.get(id))
                  .filter((p: IamData | undefined): p is IamData => p !== undefined) || [];
              }

              const joinedParticipantNames = participantData
                .map((participant: any) => {
                  if (participant.firstName?.length > 0) return participant.firstName;
                  if (participant.lastName?.length > 0) return participant.lastName;
                  const emailPrefix = typeof participant.email === 'string'
                    ? participant.email.split('@')[0]
                    : '';
                  return emailPrefix || '';
                })
                .filter((name: string) => name)
                .join(', ');


              // Create time separator message
              const timeSeparatorId = uuidv4();
              const formatedTime = parseMessageTimeForDisplay(evtShow, eventDate.getTime());
              const time = formatTimeWithAMPM(formatedTime);
              const timeSeparatorMessage: Message = {
                recordId: timeSeparatorId,
                role: MessageRole.CONVERSATION_TITLE_UPDATED, // Using this role for time separator
                generations: [
                  {
                    id: timeSeparatorId,
                    content: time,
                    createdDate: eventDate.getTime(),
                    ...(evtShow && { showMessageTime: evtShow }),
                  },
                ],
              };
              joinedByTokenMessages.push(timeSeparatorMessage);

              // Create "joined" message
              const messageContent = joinedParticipantNames 
                ? `${joinedParticipantNames} joined the chat`
                : 'Someone joined the chat';
              
              const generationId = uuidv4();
              const joinedMessage: Message = {
                recordId: generationId,
                role: MessageRole.ADD_PARTICIPANT,
                generations: [
                  {
                    id: generationId,
                    content: messageContent,
                    createdDate: eventDate.getTime(),
                    ...(evtShow && { showMessageTime: evtShow }),
                  },
                ],
              };
              joinedByTokenMessages.push(joinedMessage);
            });
            
            systemMessages.push(...joinedByTokenMessages);
          }


          if (conversationTitleUpdatedEvent.length > 0) {
            const titleUpdatedMessages = conversationTitleUpdatedEvent.map((event: any) => {
              const updatedBy = participants.find((participant: any) => participant.itemId === event.actorId);
              const messageContent = `${getUserDisplayName(updatedBy, 'Someone')} updated the conversation title`;
              const generationId = uuidv4();
              const evtShow = showMessageTimeFromBackend(event.createdDate);
              return {
                recordId: generationId,
                role: MessageRole.CONVERSATION_TITLE_UPDATED,
                generations: [
                  {
                    id: generationId,
                    content: messageContent,
                    createdDate: new Date(event.createdDate).getTime(),
                    ...(evtShow && { showMessageTime: evtShow }),
                  },
                ],
              };
            });
            // console.log('systemMessage in conversation title updated', titleUpdatedMessages)
            systemMessages.push(...titleUpdatedMessages);
          }

          if (removedParticipantEvent.length > 0) {
            const removedParticipantMessages = removedParticipantEvent.map((event: any) => {
              const removedBy = participants.find((participant: any) => participant.itemId === event.actorId) || allRemovedParticipants.find((participant: any) => participant.itemId === event.actorId);

              // Check if the actor is removing themselves
              const isSelfRemoval = event.targetIds?.includes(event.actorId);

              // Get removed participants for this specific event
              const eventRemovedParticipants = allRemovedParticipants.filter((p: IamData) =>
                event.targetIds?.includes(p.itemId)
              );

     
              let messageContent: string;
              if (isSelfRemoval) {
                // If actor removed themselves, show "left the conversation"
                const leavingUser = eventRemovedParticipants.find(
                  (participant: any) => participant.itemId === event.actorId
                ) || removedBy;
                const leavingUserName = getUserDisplayName(leavingUser, 'Someone');
                messageContent = `${leavingUserName} left the conversation`;
              } else {
                // Otherwise, show "removed {users}"
                const removedParticipantNames = eventRemovedParticipants
                  .map((participant: any) => getUserDisplayName(participant, ''))
                  .filter((name: string) => name.length > 0)
                  .join(', ') || 'a user';
                messageContent = `${getUserDisplayName(removedBy, 'Someone')} removed ${removedParticipantNames}`;
              }

              const generationId = uuidv4();
              const evtShow = showMessageTimeFromBackend(event.createdDate);
              return {
                recordId: generationId,
                role: MessageRole.USER_REMOVED,
                generations: [
                  {
                    id: generationId,
                    content: messageContent,
                    createdDate: new Date(event.createdDate).getTime(),
                    ...(evtShow && { showMessageTime: evtShow }),
                  },
                ],
              };
            });
            systemMessages.push(...removedParticipantMessages);
          }
        }

        const fetchedMessages: Message[] = [];
        const conversationTitle = stripConversationDisplayQuotes(events?.conversationTitle ?? '');
        const currentUser = useCommonStore.getState().user;
        const currentUserId = currentUser?.id;

        if (res && res.data && Array.isArray(res.data)) {
          const messages = res.data;

          // Sort messages by createdDate (oldest first)
          const sortedMessages = [...messages].sort((a, b) =>
            new Date(a.createdDate).getTime() - new Date(b.createdDate).getTime()
          );

          // console.log('sortedMessages in fetchCollaborationMessages', sortedMessages)
          const addUserToAttachments = (attachments: Attachment[], sender: MessageRole): FileMetadata[] => {
            if (!attachments || attachments.length === 0) {
              return [];
            }

            return attachments.map((attachment) => {
              return {
                fileId: attachment.fileId,
                fileName: attachment.fileName,
                extension: attachment.extension,
                sender: sender,
                createdDate: new Date().toISOString(),
                lastUpdatedDate: new Date().toISOString(),
                size: 0,
              };
            });
          };

          // Get participants from conversation state
          const conv = get().conversations[conversationId];
          const convParticipants = conv?.participants || [];

          // Get selected model to add as SeliseAI user
          const selectedModel = useModelStore.getState().selectedModel;

          // Create a map of user IDs to user data for quick lookup
          // Use participants parameter first, then fall back to conversation participants
          const userMap = new Map<string, IamData>();
          const allParticipants = participants && participants.length > 0 ? participants : convParticipants;
          allParticipants.forEach((participant) => {
            userMap.set(participant.itemId, participant);
          });

          // Add removed participants to userMap for lookup
          const convRemovedParticipants = conv?.removedParticipants || [];
          convRemovedParticipants.forEach((participant) => {
            // Only add if not already in map (participants take priority)
            if (!userMap.has(participant.itemId)) {
              userMap.set(participant.itemId, participant);
            }
          });

          // Add selected model (SeliseAI) to userMap if it exists
          if (selectedModel) {
            const modelAsUser: IamData = {
              itemId: selectedModel.id,
              createdDate: '',
              lastUpdatedDate: '',
              lastLoggedInTime: '',
              language: '',
              salutation: '',
              firstName: 'SeliseAI',
              lastName: null,
              email: selectedModel.name || selectedModel.id,
              userName: selectedModel.name || selectedModel.id,
              phoneNumber: null,
              roles: [],
              permissions: [],
              active: true,
              isVarified: false,
              profileImageUrl: null,
              mfaEnabled: false,
              organizations: [],
              plan: { itemId: '', name: '' },
              planItem: { itemId: '', title: null },
              groups: [],
            };
            userMap.set(selectedModel.id, modelAsUser);
          }

          const convertBackendMentionsToMentionObjects = (
            mentionUserIds: string[],
            content: string
          ): Mention[] => {
            if (!mentionUserIds || mentionUserIds.length === 0 || !content) {
              return [];
            }

            const mentions: Mention[] = [];

            // For each mentioned user ID, find their name in the content
            mentionUserIds.forEach((userId) => {
              const user = userMap.get(userId);
              if (!user) {
                // If user still not found, skip it (we already tried to fetch)
                console.warn('User not found for mention:', userId);
                return;
              }

              const userName = `${user.firstName}${user.lastName ? ` ${user.lastName}` : ''}`;

              // Find all occurrences of the user name in the content
              let searchStart = 0;
              let index = content.indexOf(userName, searchStart);
              while (index !== -1) {

                // Check if this is a word boundary (not part of another word)
                const beforeChar = index > 0 ? content[index - 1] : ' ';
                const afterChar = index + userName.length < content.length
                  ? content[index + userName.length]
                  : ' ';

                // Only consider it a mention if it's at word boundaries
                if ((beforeChar === ' ' || beforeChar === '\n' || index === 0) &&
                  (afterChar === ' ' || afterChar === '\n' || index + userName.length === content.length)) {
                  mentions.push({
                    userId: userId,
                    trigger: `@${userName}`,
                    start: index,
                    end: index + userName.length,
                    user: user,
                  });
                  // Move past this occurrence
                  searchStart = index + userName.length;
                } else {
                  // Move past this occurrence even if not a match
                  searchStart = index + 1;
                }

                // Find next occurrence
                index = content.indexOf(userName, searchStart);
              }
            });

            // Sort mentions by start position and remove duplicates
            return mentions
              .sort((a, b) => a.start - b.start)
              .filter((mention, index, arr) => {
                // Remove duplicates (same userId and position)
                return index === 0 ||
                  mention.userId !== arr[index - 1].userId ||
                  mention.start !== arr[index - 1].start;
              });
          };

          // Create mapping to track message IDs to recordIds (similar to non-group chat)
          const messageIdToRecordIdMap = new Map<string, string>();
          const a2uiSubmissions = collectA2uiSubmissions(sortedMessages);
          const a2uiApprovals = collectA2uiApprovals(sortedMessages);

          // console.log('sortedMessages in fetchCollaborationMessages', sortedMessages)
          for (const msg of sortedMessages) {
            const isUserMessage = msg.type === 'user' && msg.senderId === currentUserId;
            const isUserAssistant = msg.type === 'user' && msg.senderId !== currentUserId;
            const isAssistantMessage = msg.type === 'assistant' || msg.type === 'agent';

            // const attachments = await fetchAttachmentMetadata(msg.attachments || [], isUserMessage ? MessageRole.USER : MessageRole.ASSISTANT);

            const messageRecordId = msg.id;

            // Helper function to get parentMessage from parentIds
            const getParentMessageData = (): { senderName: string; content: string; time: number } | undefined => {
              if (!msg.parentIds || !Array.isArray(msg.parentIds) || msg.parentIds.length === 0) {
                return undefined;
              }

              // Find the first parent message in sortedMessages
              const parentId = msg.parentIds[0];
              const parentMsg = sortedMessages.find((m: any) => m.id === parentId);

              if (!parentMsg) return undefined;

              // Get sender name from userMap
              let senderName = 'User';
              if (parentMsg.senderId) {
                const user = userMap.get(parentMsg.senderId);
                if (user) {
                  senderName = `${user.firstName}${user.lastName ? ` ${user.lastName}` : ''}`;
                }
              }

              return {
                senderName: senderName,
                content: parentMsg.content || '',
                time: timestampFromBackendCreatedDate(parentMsg.createdDate),
              };
            };

            const parentMessageData = getParentMessageData();

            if (isUserMessage) {
              // Convert backend mentions (array of user IDs) to Mention objects
              const mentions = msg.mentions && Array.isArray(msg.mentions) && msg.mentions.length > 0
                ? convertBackendMentionsToMentionObjects(msg.mentions, msg.content || '')
                : undefined;

              const backendShow = showMessageTimeFromBackend(msg.createdDate);
              fetchedMessages.push({
                recordId: messageRecordId,
                role: MessageRole.USER_CHAT,
                senderId: msg.senderId,
                generations: [{
                  id: messageRecordId,
                  content: msg.content || '',
                  createdDate: new Date(msg.createdDate).getTime() || Date.now(),
                  ...(backendShow && { showMessageTime: backendShow }),
                  attachments: addUserToAttachments(msg.attachments, MessageRole.USER),
                  ...(msg.parentIds && Array.isArray(msg.parentIds) && msg.parentIds.length > 0 && { parentIds: msg.parentIds }),
                  ...(parentMessageData && { parentMessage: parentMessageData }),
                  ...(mentions && mentions.length > 0 && { mentions: mentions }),
                }],
              });
              // Store mapping for parent lookup
              messageIdToRecordIdMap.set(msg.id, messageRecordId);
            } else if (isUserAssistant) {
              // Extract feedback from reactions
              const mentions = msg.mentions && Array.isArray(msg.mentions) && msg.mentions.length > 0
                ? convertBackendMentionsToMentionObjects(msg.mentions, msg.content || '')
                : undefined;
              const feedback = extractFeedbackFromReactions(msg.reactions, currentUserId);

              const backendShow = showMessageTimeFromBackend(msg.createdDate);
              fetchedMessages.push({
                recordId: messageRecordId,
                role: MessageRole.ASSISTANT_CHAT,
                senderId: msg.senderId,
                generations: [{
                  id: messageRecordId,
                  content: msg.content || '',
                  createdDate: new Date(msg.createdDate).getTime() || Date.now(),
                  ...(backendShow && { showMessageTime: backendShow }),
                  attachments: addUserToAttachments(msg.attachments, MessageRole.ASSISTANT),
                  feedback: feedback,
                  ...(msg.parentIds && Array.isArray(msg.parentIds) && msg.parentIds.length > 0 && { parentIds: msg.parentIds }),
                  ...(parentMessageData && { parentMessage: parentMessageData }),
                  ...(mentions && mentions.length > 0 && { mentions: mentions }),
                }],
                toolsStatus: msg.toolsStatus,

              });
              // Store mapping for parent lookup
              messageIdToRecordIdMap.set(msg.id, messageRecordId);
            } else if (isAssistantMessage) {
              const feedback = extractFeedbackFromReactions(msg.reactions, currentUserId);

              // Process metadata files for generated images
              const generatedImages: Attachment[] = [];
              let hasFileGeneratedResponse = false;
              let searchSources = [];
              let usedModelId = '';

              if (isAssistantMessage) {
                const modelFromMetadata =
                  msg.metadata?.selected_model ??
                  msg.metadata?.selectedModel ??
                  msg.metadata?.autoSelectedModel ??
                  msg.metadata?.model ??
                  msg.metadata?.model_name ??
                  msg.metadata?.modelName ??
                  msg.metadata?.model_id ??
                  msg.metadata?.modelId ??
                  msg.metadata?.agentRun?.model ??
                  msg.metadata?.agentRun?.Model ??
                  msg.metadata?.AgentRun?.Model ??
                  msg.metadata?.AgentRun?.model ??
                  msg.metadata?.agent_run?.model ??
                  msg.metadata?.agent_run?.Model;

                if (modelFromMetadata) {
                  usedModelId = modelFromMetadata;
                } else if (msg.senderId && !isDefaultMainChatAgentId(msg.senderId)) {
                  usedModelId = msg.senderId;
                }
              }

              if (msg.metadata) {
                if (msg.metadata.files && Array.isArray(msg.metadata.files) && msg.metadata.files.length > 0) {
                  for (const file of msg.metadata.files) {
                    if (file.fileId || file.FileId) {
                      const fileId = file.fileId || file.FileId;
                      generatedImages.push({
                        fileId: fileId,
                        fileName: file.fileName || file.FileName || `file_${fileId}`,
                        extension: file.extension || file.Extension || 'unknown',
                        sender: MessageRole.ASSISTANT,
                        title: file.title ?? file.Title ?? undefined,
                        version: file.version ?? file.Version ?? undefined,
                      });
                    }
                  }
                }
                hasFileGeneratedResponse = msg.metadata.hasFileGeneratedResponse || false;

                if (msg.metadata.searchSources && Array.isArray(msg.metadata.searchSources) && msg.metadata.searchSources.length > 0) {
                  searchSources = msg.metadata.searchSources.map((source: any) => ({
                    pageContent: source.PageContent || source.pageContent || '',
                    metadata: {
                      title: source.Metadata?.Title || source.metadata?.title || '',
                      url: source.Metadata?.Url || source.metadata?.url || '',
                    },
                  }));
                }
              }



              // Check if parentIds point to an assistant message (for grouping generations)
              let parentAssistantRecordId: string | null = null;
              if (msg.parentIds && Array.isArray(msg.parentIds) && msg.parentIds.length > 0) {
                for (const parentId of msg.parentIds) {
                  // First check if parentId is a message ID we've already mapped
                  if (messageIdToRecordIdMap.has(parentId)) {
                    const mappedRecordId = messageIdToRecordIdMap.get(parentId)!;
                    // Check if the parent is an assistant message
                    const parentMessage = fetchedMessages.find(m => m.recordId === mappedRecordId);
                    if (parentMessage && parentMessage.role === MessageRole.ASSISTANT) {
                      parentAssistantRecordId = mappedRecordId;
                      break;
                    }
                  } else {
                    // Check if parentId matches any generation ID in existing assistant messages
                    const parentMessage = fetchedMessages.find(m =>
                      m.role === MessageRole.ASSISTANT &&
                      m.generations.some(g => g.id === parentId)
                    );
                    if (parentMessage) {
                      parentAssistantRecordId = parentMessage.recordId;
                      // Store the mapping for future lookups
                      messageIdToRecordIdMap.set(parentId, parentMessage.recordId);
                      break;
                    }
                  }
                }
              }


              const userRecordIdForAssistant = msg.metadata?.relatedUserMessageIds?.[0];

              // Extract thinkContent for thinking models (same logic as regular chat)
              let content = msg.content || '';
              let thinkContent = '';


              // const modelUsed = useModelStore.getState().models.find(model => model.id === usedModelId);
              // const isThinkingModel = modelUsed?.isThinkingModel && !modelUsed?.isHarmonicModel && !modelUsed?.hasDynamicThinking;

              if (isAssistantMessage) {
                const parsedContent = parseThinkingContent(content);
                thinkContent = parsedContent.thinkContent;
                content = parsedContent.responseContent;
              }

              const backendShow = showMessageTimeFromBackend(msg.createdDate);
              const toolCalls = parseToolCallsFromMetadata(msg.metadata);
              const skillEvents = parseSkillEventsFromMetadata(msg.metadata);
              const subagentRuns = parseSubagentRunsFromMetadata(msg.metadata);
              const a2uiSurfaces = parseA2uiSurfacesFromMetadata(msg.metadata, a2uiSubmissions, a2uiApprovals);
              const newGeneration = {
                id: messageRecordId,
                content: content,
                createdDate: new Date(msg.createdDate).getTime() || Date.now(),
                ...(backendShow && { showMessageTime: backendShow }),
                attachments: addUserToAttachments(msg.attachments, MessageRole.ASSISTANT),
                isThisGenerationOnGoing: false,
                ...(msg.metadata?.isCancelled && { isCancelled: true }),
                HasFileGeneratedResponse: hasFileGeneratedResponse,
                generatedImages: generatedImages.length > 0 ? generatedImages : undefined,
                feedback: feedback,
                ...(thinkContent && { thinkContent: thinkContent }),
                ...(msg.parentIds && Array.isArray(msg.parentIds) && msg.parentIds.length > 0 && { parentIds: msg.parentIds }),
                ...(parentMessageData && { parentMessage: parentMessageData }),
                ...(searchSources && { searchSources: searchSources }),
                usedModelId: usedModelId,
                ...(msg.metadata && { metadata: msg.metadata }),
                ...(toolCalls && { toolCalls }),
                ...(skillEvents && { skillEvents }),
                ...(subagentRuns && { subagentRuns }),
                ...(a2uiSurfaces && { a2uiSurfaces }),
              };
              if (parentAssistantRecordId) {
                const parentAssistantIndex = fetchedMessages.findIndex(
                  (m) => m.recordId === parentAssistantRecordId
                );
                if (parentAssistantIndex !== -1) {
                  // Add generation to existing assistant message
                  fetchedMessages[parentAssistantIndex].generations.push(newGeneration);
                  // Store mapping using the parent's recordId so future messages can find it
                  messageIdToRecordIdMap.set(msg.id, parentAssistantRecordId);
                } else {
                  // Parent not found yet, create new message (shouldn't happen if sorted correctly)
                  fetchedMessages.push({
                    recordId: newGeneration.id,
                    ...(userRecordIdForAssistant && { userRecordId: userRecordIdForAssistant }),
                    // ...(userMessageData && { userMessage: userMessageData }),
                    role: MessageRole.ASSISTANT,
                    senderId: msg.senderId,
                    generations: [newGeneration],
                    toolsStatus: msg.toolsStatus,

                  });
                  messageIdToRecordIdMap.set(msg.id, newGeneration.id);
                }
              } else {
                // No parent assistant message, create new assistant message
                fetchedMessages.push({
                  recordId: newGeneration.id,
                  ...(userRecordIdForAssistant && { userRecordId: userRecordIdForAssistant }),
                  // ...(userMessageData && { userMessage: userMessageData }),
                  role: MessageRole.ASSISTANT,
                  senderId: msg.senderId,
                  generations: [newGeneration],
                  toolsStatus: msg.toolsStatus,
                });
                // Store mapping for parent lookup
                messageIdToRecordIdMap.set(msg.id, newGeneration.id);
              }
            }
          }
        }

        // Merge systemMessages with fetchedMessages and sort by createdDate
        const allMessages = applyEmptyAssistantContentFallback(
          hydrateMessageStreamBlocks(
            consolidateAssistantMessages(
              [...fetchedMessages, ...systemMessages].sort((a, b) => {
                const aDate = messageGenerationTimeMs(a.generations[0]);
                const bDate = messageGenerationTimeMs(b.generations[0]);
                return aDate - bDate;
              }),
            ),
          ),
        );

        set((state) => {
          const existing = state.conversations[conversationId];
          const hasContentFilterViolation = existing?.messages.some(
            (msg) =>
              msg.role === MessageRole.ASSISTANT &&
              msg.generations.some((g) => g.isContentFilterViolation)
          );
          const hasStreamingAssistant = existing?.messages.some(
            (msg) =>
              msg.role === MessageRole.ASSISTANT &&
              msg.generations.some((g) => g.isThisGenerationOnGoing)
          );
          const keepLiveReconnect =
            (existing?.isCurrentResponseGenerating && hasStreamingAssistant) ||
            hasContentFilterViolation ||
            ((existing?.messages.length ?? 0) > 0 && allMessages.length === 0);
          return {
            conversations: {
              ...state.conversations,
              [conversationId]: {
                ...(existing || initialConversationState(conversationId)),
                messages: keepLiveReconnect
                  ? preferLiveReconnectMessages(allMessages, existing.messages)
                  : allMessages,
                title: conversationTitle,
                isLoading: false,
                hasFetchedInitialMessages: true,
                isScheduledLiveStub: false,
                error: null,
                isGroupChat: true,
                hasManualTitle,
                participants: participants || [],
                removedParticipants: allRemovedParticipants,
              },
            },
          };
        });
        get().syncConversationTitleFromApi(conversationId, events?.conversationTitle, config);
        get().flushPendingStreamingNotifications(conversationId, config);
      } catch (error) {
        console.error('Failed to fetch collaboration messages:', error);

        set((state) => ({
          conversations: {
            ...state.conversations,
            [conversationId]: {
              ...(state.conversations[conversationId] || initialConversationState(conversationId)),
              isLoading: false,
              hasFetchedInitialMessages: true,
              isScheduledLiveStub: false,
              error: getConversationAccessErrorMessage(error),
              participants: participants || [],
            },
          },
        }));
        get().flushPendingStreamingNotifications(conversationId, config);
      }
    }

  },

  ensureScheduledConversationFromSocket: (notification: any) => {
    const conversationId = notification?.response?.conversationId as string | undefined;
    const message = notification?.response?.message as {
      id?: string;
      content?: string;
      senderId?: string;
      createdDate?: string;
      mentions?: string[];
      metadata?: { scheduledJob?: { jobName?: string; agentId?: string } };
    } | undefined;

    if (!conversationId || !message?.id) return;

    const jobName = message.metadata?.scheduledJob?.jobName?.trim() || '';
    const catalogueAgentId = firstCatalogueAgentId(
      get().conversations[conversationId]?.agentId,
      get().sidebarConversationsList.find((item) => item.ConversationId === conversationId)?.AgentId,
      message.metadata?.scheduledJob?.agentId,
      catalogueAgentIdFromMentions(message.mentions),
    );

    if (!get().conversations[conversationId]) {
      set((state) => ({
        conversations: {
          ...state.conversations,
          [conversationId]: {
            ...initialConversationState(conversationId, jobName),
            hasFetchedInitialMessages: true,
            isScheduledLiveStub: false,
            ...(catalogueAgentId && { agentId: catalogueAgentId }),
          },
        },
      }));
    } else if (catalogueAgentId && !get().conversations[conversationId]?.agentId) {
      set((state) => ({
        conversations: {
          ...state.conversations,
          [conversationId]: {
            ...state.conversations[conversationId],
            agentId: catalogueAgentId,
          },
        },
      }));
    }

    const currentConv = get().conversations[conversationId];
    if (!currentConv) return;

    const messageId = String(message.id);
    const alreadyExists = currentConv.messages.some(
      (m) =>
        m.recordId === messageId ||
        m.generations.some((generation) => generation.id === messageId)
    );

    if (!alreadyExists) {
      const backendShow = showMessageTimeFromBackend(message.createdDate);
      const userMessage: Message = {
        recordId: messageId,
        role: MessageRole.USER,
        senderId: message.senderId,
        deliveryStatus: MessageDeliveryStatus.DELIVERED,
        generations: [
          {
            id: messageId,
            content: message.content || '',
            createdDate: new Date(message.createdDate ?? Date.now()).getTime() || Date.now(),
            ...(backendShow && { showMessageTime: backendShow }),
          },
        ],
      };
      get()._addMessageToActiveConversation(userMessage, undefined, conversationId);
    }

    // Socket-seeded scheduled threads are live conversations, same as a user-started chat.
    // Mark them fetched so opening later (including after the stream ends) skips history.
    set((state) => {
      const conv = state.conversations[conversationId];
      if (!conv || (conv.hasFetchedInitialMessages && !conv.isScheduledLiveStub)) {
        return state;
      }
      return {
        conversations: {
          ...state.conversations,
          [conversationId]: {
            ...conv,
            hasFetchedInitialMessages: true,
            isScheduledLiveStub: false,
          },
        },
      };
    });

    const upsertScheduledSidebarEntry = (agentId?: string) => {
      const existing = get().sidebarConversationsList.find(
        (item) => item.ConversationId === conversationId,
      );
      const nextAgentId = firstCatalogueAgentId(agentId, existing?.AgentId);
      const currentUser = useCommonStore.getState().user;
      get().appendToSidebarConversationsList({
        ConversationId: conversationId,
        ConversationTitle: existing?.ConversationTitle?.trim() || jobName,
        CreatedDate: existing?.CreatedDate ?? message.createdDate ?? new Date().toISOString(),
        LastUpdatedDate: new Date().toISOString(),
        Conversations: existing?.Conversations ?? [],
        IsGroup: existing?.IsGroup ?? false,
        TenantId: existing?.TenantId ?? '',
        CreatedBy: existing?.CreatedBy ?? message.senderId ?? currentUser?.id ?? '',
        CustomGptId: existing?.CustomGptId,
        ShareToken: existing?.ShareToken,
        ProjectId: existing?.ProjectId,
        ...(nextAgentId && { AgentId: nextAgentId }),
      });
    };

    const alreadyInSidebar = get().sidebarConversationsList.some(
      (item) => item.ConversationId === conversationId
    );
    if (alreadyInSidebar && !catalogueAgentId) {
      get()._moveConversationToToday(conversationId);
    } else {
      upsertScheduledSidebarEntry(catalogueAgentId);
    }

    const config = useCollaborationSocketStore.getState().configurationSettings;
    if (!config) return;

    void getConversationById(conversationId, config)
      .then((freshData) => {
        const apiTitle = freshData?.conversationTitle?.trim();
        if (apiTitle) {
          get().updateConversationTitleInStore(conversationId, apiTitle);
        }

        const apiAgentId = catalogueAgentIdFromConversationPayload(freshData);
        if (!apiAgentId) return;

        const conv = get().conversations[conversationId];
        if (conv && conv.agentId !== apiAgentId) {
          set((state) => ({
            conversations: {
              ...state.conversations,
              [conversationId]: {
                ...state.conversations[conversationId],
                agentId: apiAgentId,
              },
            },
          }));
        }

        const sidebarItem = get().sidebarConversationsList.find(
          (item) => item.ConversationId === conversationId,
        );
        if (sidebarItem?.AgentId !== apiAgentId) {
          upsertScheduledSidebarEntry(apiAgentId);
        }
      })
      .catch((error) => {
        console.error('Failed to refresh scheduled conversation title:', error);
      });
  },

  handleGroupChatNotification: async (notification: any, config?: ConfigurationSettings, type?: string, userIds?: { invitedBy: string, invitedTo: string[] }) => {
    if (!config) return;

    if (type === "USER_MESSAGE") {
      try {
        if (!notification || !notification.response) return;
        const notificationData = notification.response;
        const { conversationId, message } = notificationData;
        if (!conversationId) {
          return;
        }
        const currentUser = useCommonStore.getState().user;
        const currentUserId = currentUser?.id;

        if (!currentUserId) {
          console.warn('Current user ID not found');
          return;
        }

        const isUserMessage = message?.senderId === currentUserId;
        const { conversations } = get();
        const currentConv = conversations[conversationId];

        if (!currentConv) {
          return;
        }


        if (isUserMessage && message?.id) {
          const newId = String(message.id);
          const serverCreatedDate = timestampFromBackendCreatedDate(message.createdDate);
          const originalMessageId = message.originalMessageId
            ? String(message.originalMessageId)
            : undefined;

          const alreadyInThread = (currentConv.messages ?? []).some(
            (m) =>
              m.recordId === newId ||
              m.generations.some((generation) => generation.id === newId)
          );
          if (alreadyInThread) {
            set((state) => {
              const current = state.conversations[conversationId];
              if (!current) return state;
              return {
                conversations: {
                  ...state.conversations,
                  [conversationId]: {
                    ...current,
                    messages: removeEchoUserMessageRows(current.messages ?? []),
                  },
                },
              };
            });
            return;
          }

          if (originalMessageId) {
            const editThread =
              findUserMessageForLinkedId(currentConv.messages ?? [], originalMessageId) ??
              [...(currentConv.messages ?? [])]
                .reverse()
                .find((m) => m.role === MessageRole.USER);
            if (editThread) {
              const backendShow = showMessageTimeFromBackend(message.createdDate);
              const incomingContent = String(message.content || '').trim();
              set((state) => {
                const current = state.conversations[conversationId];
                if (!current) return state;

                const threadMessage = (current.messages ?? []).find(
                  (m) => m.recordId === editThread.recordId
                );
                const lastGeneration =
                  threadMessage?.generations[threadMessage.generations.length - 1];
                const hasGeneration =
                  !!threadMessage?.generations.some((generation) => generation.id === newId) ||
                  lastGeneration?.content?.trim() === incomingContent;
                if (hasGeneration) {
                  return {
                    conversations: {
                      ...state.conversations,
                      [conversationId]: {
                        ...current,
                        messages: removeEchoUserMessageRows(current.messages ?? []),
                      },
                    },
                  };
                }

                const messages = (current.messages ?? []).map((msg) => {
                  if (msg.recordId !== editThread.recordId) return msg;
                  return {
                    ...msg,
                    deliveryStatus: MessageDeliveryStatus.DELIVERED,
                    generations: [
                      ...msg.generations,
                      {
                        id: newId,
                        content: message.content || '',
                        createdDate:
                          new Date(message.createdDate ?? Date.now()).getTime() || Date.now(),
                        ...(backendShow && { showMessageTime: backendShow }),
                      },
                    ],
                    activeGenerationIndex: msg.generations.length,
                  };
                });

                return {
                  conversations: {
                    ...state.conversations,
                    [conversationId]: {
                      ...current,
                      messages: removeEchoUserMessageRows(messages),
                    },
                  },
                };
              });
              return;
            }
          }

          // Find the *best* matching pending dummy user message.
          // Matching by content alone is risky (e.g., many "hi" messages). We mitigate by choosing the
          // most recent pending message with the closest createdDate to the server timestamp.
          const pendingCandidates = (currentConv.messages ?? [])
            .filter((m) =>
              m.role === MessageRole.USER_CHAT &&
              m.recordId !== newId &&
              (m.deliveryStatus !== MessageDeliveryStatus.DELIVERED))
            .map((m) => ({
              recordId: m.recordId,
              createdDate: messageGenerationTimeMs(m.generations?.[0]),
            }))
            .sort((a, b) => {
              // Closest to server time first; tie-breaker: latest client createdDate
              const da = Math.abs((a.createdDate ?? 0) - serverCreatedDate);
              const db = Math.abs((b.createdDate ?? 0) - serverCreatedDate);
              if (da !== db) return da - db;
              return (b.createdDate ?? 0) - (a.createdDate ?? 0);
            });

          const foundDummyId = pendingCandidates[0]?.recordId;
          if (!foundDummyId) {
            const backendShow = showMessageTimeFromBackend(message.createdDate);
            get()._addMessageToActiveConversation(
              {
                recordId: newId,
                role: currentConv.isGroupChat ? MessageRole.USER_CHAT : MessageRole.USER,
                senderId: message.senderId,
                deliveryStatus: MessageDeliveryStatus.DELIVERED,
                generations: [
                  {
                    id: newId,
                    content: message.content || '',
                    createdDate: new Date(message.createdDate ?? Date.now()).getTime() || Date.now(),
                    ...(backendShow && { showMessageTime: backendShow }),
                  },
                ],
              },
              undefined,
              conversationId,
            );
            set((state) => {
              const current = state.conversations[conversationId];
              if (!current) return state;
              return {
                conversations: {
                  ...state.conversations,
                  [conversationId]: {
                    ...current,
                    messages: removeEchoUserMessageRows(current.messages ?? []),
                  },
                },
              };
            });
            return;
          }

          set((state) => {
            const current = state.conversations[conversationId];
            if (!current) return state;

            const messages = current.messages ?? [];

            // If server message already exists, remove the dummy instead of creating a duplicate id.
            const serverAlreadyExists = messages.some((m) => m.role === MessageRole.USER_CHAT && m.recordId === newId);

            const nextMessages = messages
              // Drop any existing server-echo user message if we’re about to convert the dummy into it
              .filter((m) => !(m.role === MessageRole.USER_CHAT && m.recordId === newId && !serverAlreadyExists))
              // If server exists, drop the dummy to avoid duplicates; else keep and convert it below
              .filter((m) => !(serverAlreadyExists && m.role === MessageRole.USER_CHAT && m.recordId === foundDummyId))
              .map((msg) => {
                const isDummyUserMsg = msg.role === MessageRole.USER_CHAT && msg.recordId === foundDummyId;
                const isDummyAssistantLink =
                  msg.role === MessageRole.ASSISTANT && msg.userRecordId === foundDummyId;

                if (isDummyUserMsg && !serverAlreadyExists) {
                  return {
                    ...msg,
                    recordId: newId,
                    deliveryStatus: MessageDeliveryStatus.DELIVERED,
                    generations: (msg.generations ?? []).map((g) =>
                      g.id === foundDummyId ? { ...g, id: newId } : g
                    ),
                  };
                }

                if (isDummyAssistantLink && !serverAlreadyExists) {
                  return {
                    ...msg,
                    userRecordId: newId,
                  };
                }

                return msg;
              });

            return {
              conversations: {
                ...state.conversations,
                [conversationId]: {
                  ...current,
                  messages: nextMessages,
                },
              },
            };
          });

          // Remove from queue using the dummy id (stable), not the content.
          get().removeFromMessageQueue(foundDummyId);
          get().setIsProcessingMessageQueue(false);
          get().processNextMessageFromQueue(conversationId, config);
          return;
        }

        if (!isUserMessage) {
          const userMap = new Map<string, IamData>();
          const allParticipants = currentConv?.participants || [];
          allParticipants.forEach((participant) => {
            userMap.set(participant.itemId, participant);
          });

          // Add removed participants to userMap for lookup
          const removedParticipants = currentConv?.removedParticipants || [];
          removedParticipants.forEach((participant) => {
            // Only add if not already in map (participants take priority)
            if (!userMap.has(participant.itemId)) {
              userMap.set(participant.itemId, participant);
            }
          });

          // Get selected model to add as SeliseAI user
          const selectedModel = useModelStore.getState().selectedModel;

          // Add selected model (SeliseAI) to userMap if it exists
          if (selectedModel) {
            const modelAsUser: IamData = {
              itemId: selectedModel.id,
              createdDate: '',
              lastUpdatedDate: '',
              lastLoggedInTime: '',
              language: '',
              salutation: '',
              firstName: 'SeliseAI',
              lastName: null,
              email: selectedModel.name || selectedModel.id,
              userName: selectedModel.name || selectedModel.id,
              phoneNumber: null,
              roles: [],
              permissions: [],
              active: true,
              isVarified: false,
              profileImageUrl: null,
              mfaEnabled: false,
              organizations: [],
              plan: { itemId: '', name: '' },
              planItem: { itemId: '', title: null },
              groups: [],
            };
            userMap.set(selectedModel.id, modelAsUser);
          }

          const convertBackendMentionsToMentionObjects = (
            mentionUserIds: string[],
            content: string
          ): Mention[] => {
            if (!mentionUserIds || mentionUserIds.length === 0 || !content) {
              return [];
            }

            const mentions: Mention[] = [];

            // For each mentioned user ID, find their name in the content
            mentionUserIds.forEach((userId) => {
              const user = userMap.get(userId);
              if (!user) {
                // If user still not found, skip it (we already tried to fetch)
                console.warn('User not found for mention:', userId)
                return;
              }

              const userName = `${user.firstName}${user.lastName ? ` ${user.lastName}` : ''}`;

              // Find all occurrences of the user name in the content
              let searchStart = 0;
              let index = content.indexOf(userName, searchStart);
              while (index !== -1) {

                // Check if this is a word boundary (not part of another word)
                const beforeChar = index > 0 ? content[index - 1] : ' ';
                const afterChar = index + userName.length < content.length
                  ? content[index + userName.length]
                  : ' ';

                // Only consider it a mention if it's at word boundaries
                if ((beforeChar === ' ' || beforeChar === '\n' || index === 0) &&
                  (afterChar === ' ' || afterChar === '\n' || index + userName.length === content.length)) {
                  mentions.push({
                    userId: userId,
                    trigger: `@${userName}`,
                    start: index,
                    end: index + userName.length,
                    user: user,
                  });
                  // Move past this occurrence
                  searchStart = index + userName.length;
                } else {
                  // Move past this occurrence even if not a match
                  searchStart = index + 1;
                }

                // Find next occurrence
                index = content.indexOf(userName, searchStart);
              }
            });

            // Sort mentions by start position and remove duplicates
            return mentions
              .sort((a, b) => a.start - b.start)
              .filter((mention, index, arr) => {
                // Remove duplicates (same userId and position)
                return index === 0 ||
                  mention.userId !== arr[index - 1].userId ||
                  mention.start !== arr[index - 1].start;
              });
          };
          const messageRecordId = message.id;

          const addAssistantToAttachments = (attachments: Attachment[], sender: MessageRole): FileMetadata[] => {
            return attachments.map((attachment) => {
              return {
                fileId: attachment.fileId,
                fileName: attachment.fileName,
                extension: attachment.extension,
                sender: sender,
                createdDate: new Date().toISOString(),
                lastUpdatedDate: new Date().toISOString(),
                size: 0,
              };
            });
          };
          const mentions = message.mentions && Array.isArray(message.mentions) && message.mentions.length > 0
            ? convertBackendMentionsToMentionObjects(message.mentions, message.content || '')
            : undefined;

          // Helper function to get parentMessage from parentIds
          const getParentMessageFromIds = (parentIds: string[] | undefined): { senderName: string; content: string; time: number } | undefined => {
            if (!parentIds || !Array.isArray(parentIds) || parentIds.length === 0) {
              return undefined;
            }

            const parentId = parentIds[0];
            const parentMsg = currentConv.messages.find(
              (msg) => msg.recordId === parentId ||
                msg.generations.some(gen => gen.id === parentId)
            );

            if (!parentMsg) return undefined;

            // Get sender name from userMap
            let senderName = 'User';
            const parentSenderId = parentMsg.senderId;
            if (parentSenderId) {
              const user = userMap.get(parentSenderId);
              if (user) {
                senderName = `${user.firstName}${user.lastName ? ` ${user.lastName}` : ''}`;
              } else {
                // Fallback to current user if senderId matches
                const currentUser = useCommonStore.getState().user;
                if (currentUser && currentUser.id === parentSenderId) {
                  senderName = `${currentUser.firstName}${currentUser.lastName ? ` ${currentUser.lastName}` : ''}`;
                }
              }
            }

            // Get content from first generation
            const parentContent = parentMsg.generations[0]?.content || '';
            const parentTime = messageGenerationTimeMs(parentMsg.generations[0]);

            return {
              senderName: senderName,
              content: parentContent,
              time: parentTime,
            };
          };

          const parentMessageData = getParentMessageFromIds(message.parentIds);

          const backendAssistantShow = showMessageTimeFromBackend(message.createdDate);
          const newMessage: Message = {
            recordId: messageRecordId,
            role: MessageRole.ASSISTANT_CHAT,
            senderId: message.senderId,
            generations: [{
              id: messageRecordId,
              content: message.content || '',
              createdDate: new Date(message.createdDate).getTime() || Date.now(),
              ...(backendAssistantShow && { showMessageTime: backendAssistantShow }),
              attachments: addAssistantToAttachments(message.attachments || [], MessageRole.ASSISTANT),
              ...(message.parentIds && Array.isArray(message.parentIds) && message.parentIds.length > 0 && { parentIds: message.parentIds }),
              ...(parentMessageData && { parentMessage: parentMessageData }),
              ...(mentions && mentions.length > 0 && { mentions: mentions }),
            }],
            
            toolsStatus: message.toolsStatus,
          };




          // console.log('userData', userData, "newPlaceholderMessage", newPlaceholderMessage)




          set((state) => {
            const currentConvState = state.conversations[conversationId];
            if (!currentConvState) {
              return state;
            }

            // Check if this message ID already exists to prevent duplicates
            const messageAlreadyExists = currentConvState.messages.some(
              (msg) =>
                msg.recordId === message.id ||
                (msg.generations && msg.generations.some(gen => gen.id === message.id))
            );

            if (messageAlreadyExists) {
              return state; // Message already processed, skip update
            }
            const updatedMessages = [...currentConvState.messages, newMessage];

            return {
              conversations: {
                ...state.conversations,
                [conversationId]: {
                  ...currentConvState,
                  messages: updatedMessages,

                  isCurrentMessageLoading: false,
                  isCurrentResponseGenerating: true,
                },
              },
            };
          });
        }

      } catch (error) {
        console.error('❌ Error handling group chat notification:', error);
      } finally {
        const conversationId = notification?.response?.conversationId as string | undefined;
        if (config && conversationId) {
          const conv = get().conversations[conversationId];
          if (
            conv &&
            !shouldQueueReconnectUntilUserPrompt(conv.messages, conv.hasFetchedInitialMessages)
          ) {
            get().flushPendingStreamingNotifications(conversationId, config);
          }
        }
      }
    } else if (type === "ASSISTANT_REQUEST_PROCESSING_STARTED") {
      try {
        if (!notification || !notification.response) return;
        const notificationData = notification.response;
        const assistantPositionData = notification?.streamingResponse?.data;
        const { conversationId, message } = notificationData;
        const currentUser = useCommonStore.getState().user;
        const { conversations } = get();
        const currentConv = conversations[conversationId];

        if (!currentConv || (currentConv.participants?.length ?? 0) < 2) {
          return;
        }
        const assistantMessageId = message.id;
        // userRecordId should be the user message ID that triggered this response
        const userMessageId = message.metadata?.relatedUserMessageIds?.[0] || assistantMessageId;

        const newPlaceholderMessage: Message = {
          recordId: assistantMessageId,
          userRecordId: userMessageId,
          role: MessageRole.ASSISTANT,
          senderId: currentUser?.id,
          generations: [{
            id: assistantMessageId,
            content: '',
            createdDate: Date.now(),
            showMessageTime: formatShowMessageTimeForSend(),
            attachments: [],
            isThisGenerationOnGoing: true,
          }],
        };

        set((state) => {
          const currentConvState = state.conversations[conversationId];
          if (!currentConvState) {
            return state;
          }

          // Check if message already exists to prevent duplicates
          const messageExists = currentConvState.messages.some(
            (msg) => msg.recordId === assistantMessageId || 
                     msg.generations.some(g => g.id === assistantMessageId)
          );

          const updatedActiveStreamsMap = new Map(currentConv.activeStreamsMap);
          updatedActiveStreamsMap.set(userMessageId, true);

          if (messageExists) {
            return {
              conversations:{
                ...state.conversations,
                [conversationId]:{
                  ...currentConvState,
                  assistantResponseRemaining: assistantPositionData?.remaining ?? 0,
                  activeStreamsMap: updatedActiveStreamsMap,
                }
              }
            }
          }

          return {
            conversations: {
              ...state.conversations,
              [conversationId]: {
                ...currentConvState,
                messages: [...currentConvState.messages, newPlaceholderMessage],
                // Set streamingAssistantMessageId for group chat streaming
                streamingAssistantMessageId: assistantMessageId,
                isCurrentResponseGenerating: true,
                assistantResponseRemaining: assistantPositionData?.remaining ?? 0,
                activeStreamsMap: updatedActiveStreamsMap,
              },
            },
          };
        });
      } catch (error) {
        console.error('❌ Error handling ASSISTANT_REQUEST_PROCESSING_STARTED notification:', error);
      }
    } else if (type === "ASSISTANT_STREAMED_RESPONSE") {
      const notificationData = notification;
      // const { conversationId, message } = notificationData;
      // console.log('notificationData in assistant streamed response', notificationData)
      const conversationId = notificationData.response.conversationId;
      const message = notificationData.response.message;
      const messageId = message.id;
      const { conversations } = get();
      const convForSender = conversations[conversationId];
      const messageSenderId = convForSender
        ? resolveStreamUserMessageId(message, convForSender)
        : message.metadata?.relatedUserMessageIds?.[0];
      const streamReason = notificationData.reason;
      const isReconnectWhileStreaming = streamReason === NEW_CONNECTION_WHILE_STREAMING;

      if (isReconnectWhileStreaming) {
        const conv = conversations[conversationId];
        if (
          !conv ||
          shouldQueueReconnectUntilUserPrompt(conv.messages, conv.hasFetchedInitialMessages)
        ) {
          get().enqueuePendingStreamingNotification(conversationId, notification);
          return;
        }
        if (conv) {
          set((state) => {
            const current = state.conversations[conversationId];
            if (!current) return state;

            let messages = current.messages;
            if (isReconnectWhileStreaming) {
              messages = consolidateAssistantMessages(
                applyNewConnectionWhileStreaming(
                  current.messages,
                  messageId,
                  message?.content,
                  notificationData.streamingResponse,
                  messageSenderId,
                  message.parentIds
                )
              );
            } else if (messageSenderId) {
              messages = ensureStreamingAssistantPlaceholder(
                current.messages,
                messageId,
                messageSenderId,
                message.parentIds
              );
            }

            const streamingIds = current.isGroupChat
              ? [...new Set([...(current.streamingAssistantMessageIds || []), messageId])]
              : current.streamingAssistantMessageIds;

            return {
              conversations: {
                ...state.conversations,
                [conversationId]: {
                  ...current,
                  messages,
                  streamingAssistantMessageId: messageId,
                  ...(current.isGroupChat && { streamingAssistantMessageIds: streamingIds }),
                  isCurrentMessageLoading: false,
                  isCurrentResponseGenerating: true,
                },
              },
            };
          });
        }
      }

      const parsedResponse = {
        ...notificationData.streamingResponse,
        conversation_id: conversationId,
        assistant_message_id: messageId,
        parent_ids: message.parentIds || [],
        message_sender_id: messageSenderId,
        parentIds: message.parentIds || [],
        messageSenderId: messageSenderId,
        ...(streamReason && { reason: streamReason, stream_reason: streamReason }),
      };

      get()._handleWebSocketMessage(parsedResponse, config);
    } else if (type === "ADD_PARTICIPANT") {
      try {
        const { activeConversationId, conversations } = get();
        if (!config || !userIds || !activeConversationId) {
          return;
        }

        const currentConv = conversations[activeConversationId];
        if (!currentConv) {
          return;
        }

        // Fetch user data for sender and invited users
        let participants = currentConv?.participants || [];
        // console.log('participants in add participant', participants)
        let senderData = participants.find(
          participant => participant?.itemId === notification.actorId

        ) || null;
        if (!senderData) {
          const currentUser = useCommonStore.getState().user;
          if (currentUser && currentUser?.id === notification.actorId) {
            senderData = {
              itemId: currentUser.id,
              firstName: currentUser.firstName,
              lastName: currentUser.lastName,
              createdDate: new Date().toISOString(),
              lastUpdatedDate: new Date().toISOString(),
              lastLoggedInTime: new Date().toISOString(),
              language: '',
              salutation: '',
              email: currentUser.email,
              userName: currentUser.email,
              phoneNumber: '',
              roles: [],
              permissions: [],
              active: true,
              isVarified: false,
              profileImageUrl: currentUser.profileImageUrl,
              mfaEnabled: false,
              organizations: [],
              plan: { itemId: '', name: '' },
              planItem: { itemId: '', title: null },
              groups: [],
            } as IamData;
            participants = [...participants, senderData];
          }
        }


        const response = await get().fetchUsersData(notification?.userIdsByType, config);
        const invitedUsersData = response?.data || [];

        if (activeConversationId && invitedUsersData.length > 0) {
          const existingParticipantIds = new Set(participants.map(p => p.itemId));
          const newParticipants = invitedUsersData.filter(p => !existingParticipantIds.has(p.itemId));
          const updatedParticipants = [...participants, ...newParticipants];

          // Get conversationId from notification if available, otherwise use activeConversationId
          const notificationConversationId = notification?.response?.conversationId;
          const isNotActiveConversation = notificationConversationId !== activeConversationId;

          set((state) => ({
            conversations: {
              ...state.conversations,
              [activeConversationId]: {
                ...(state.conversations[activeConversationId] || initialConversationState(activeConversationId)),
                participants: updatedParticipants,
                isGroupChat: true,
                isRecentlyAdded: isNotActiveConversation,
              },
            },
          }));

          // Also update the global participants array and cache
          get().setHasGroupChatParticipantsArray(activeConversationId, updatedParticipants);
        }

        const senderName = getUserDisplayName(senderData, 'Someone');
        const invitedUserNames = invitedUsersData
          .map(user => getUserDisplayName(user, ''))
          .filter(name => name)
          .join(', ');

        // Check if actorId is in userIdsByType (person joined themselves)
        const isSelfJoin = notification?.actorId && notification?.userIdsByType?.includes(notification.actorId);
        
        const messageContent = isSelfJoin
          ? `${invitedUserNames || senderName || 'Someone'} joined the chat`
          : `${senderName} added ${invitedUserNames}`;
        
        // Add time separator (same pattern as timeline events block around 3521-3581)
        const eventDate = notification?.createdDate ? new Date(notification.createdDate) : new Date();
        const eventTimestamp = eventDate.getTime();
        const evtShowAdd = showMessageTimeFromBackend(notification?.createdDate) ?? formatShowMessageTimeForSend();

        const timeSeparatorId = uuidv4();
        const timeSeparatorMessage: Message = {
          recordId: timeSeparatorId,
          role: MessageRole.CONVERSATION_TITLE_UPDATED, // Using this role for time separator
          generations: [
            {
              id: timeSeparatorId,
              content: formatDateWithTime(eventDate),
              createdDate: eventTimestamp,
              showMessageTime: evtShowAdd,
            },
          ],
        };
        get()._addMessageToActiveConversation(timeSeparatorMessage);

        const generationId = uuidv4();

        const systemMessage: Message = {
          recordId: generationId,
          role: MessageRole.ADD_PARTICIPANT,
          generations: [
            {
              id: generationId,
              content: messageContent,
              createdDate: eventTimestamp,
              showMessageTime: evtShowAdd,
            },
          ],
        };

        // console.log('systemMessage in add participant', systemMessage)
        get()._addMessageToActiveConversation(systemMessage);



      } catch (error) {
        console.error('❌ Error handling ADD_PARTICIPANT notification:', error);
      }
    } else if (type === "USER_ADDED_BY_TOKEN") {
      try {
        const { activeConversationId, conversations } = get();
        if (!config || !userIds || !activeConversationId) {
          return;
        }

        const currentConv = conversations[activeConversationId];
        if (!currentConv) {
          return;
        }

        // Fetch user data for sender and invited users
        let participants = currentConv?.participants || [];
        let senderData = participants.find(
          participant => participant?.itemId === notification.actorId
        ) || null;
        if (!senderData) {
          const currentUser = useCommonStore.getState().user;
          if (currentUser && currentUser?.id === notification.actorId) {
            senderData = {
              itemId: currentUser.id,
              firstName: currentUser.firstName,
              lastName: currentUser.lastName,
              createdDate: new Date().toISOString(),
              lastUpdatedDate: new Date().toISOString(),
              lastLoggedInTime: new Date().toISOString(),
              language: '',
              salutation: '',
              email: currentUser.email,
              userName: currentUser.email,
              phoneNumber: '',
              roles: [],
              permissions: [],
              active: true,
              isVarified: false,
              profileImageUrl: currentUser.profileImageUrl,
              mfaEnabled: false,
              organizations: [],
              plan: { itemId: '', name: '' },
              planItem: { itemId: '', title: null },
              groups: [],
            } as IamData;
            participants = [...participants, senderData];
          }
        }

        const response = await get().fetchUsersData(notification?.userIdsByType, config);
        const invitedUsersData = response?.data || [];

        if (activeConversationId && invitedUsersData.length > 0) {
          const existingParticipantIds = new Set(participants.map(p => p.itemId));
          const newParticipants = invitedUsersData.filter(p => !existingParticipantIds.has(p.itemId));
          const updatedParticipants = [...participants, ...newParticipants];

          // Get conversationId from notification if available, otherwise use activeConversationId
          const notificationConversationId = notification?.response?.conversationId;
          const isNotActiveConversation = notificationConversationId !== activeConversationId;

          set((state) => ({
            conversations: {
              ...state.conversations,
              [activeConversationId]: {
                ...(state.conversations[activeConversationId] || initialConversationState(activeConversationId)),
                participants: updatedParticipants,
                isGroupChat: true,
                isRecentlyAdded: isNotActiveConversation,
              },
            },
          }));

          // Also update the global participants array and cache
          get().setHasGroupChatParticipantsArray(activeConversationId, updatedParticipants);
        }

        const invitedUserNames = invitedUsersData
          .map(user => getUserDisplayName(user, ''))
          .filter(name => name)
          .join(', ');

        // Get timestamp from notification or use current time
        const eventDate = notification?.createdDate 
          ? new Date(notification.createdDate) 
          : new Date();
        const eventTimestamp = eventDate.getTime();
        const evtShowToken = showMessageTimeFromBackend(notification?.createdDate) ?? formatShowMessageTimeForSend();

        // Show "joined" message - the users in userIdsByType are the ones who joined
        const messageContent = invitedUserNames 
          ? `${invitedUserNames} ${invitedUsersData.length === 1 ? 'joined' : 'joined'} the chat`
          : 'Someone joined the chat4';
        
        const generationId = uuidv4();

        const systemMessage: Message = {
          recordId: generationId,
          role: MessageRole.ADD_PARTICIPANT,
          generations: [
            {
              id: generationId,
              content: messageContent,
              createdDate: eventTimestamp,
              showMessageTime: evtShowToken,
            },
          ],
        };

        get()._addMessageToActiveConversation(systemMessage);

      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('❌ Error handling USER_ADDED_BY_TOKEN notification:', error);
      }
    } else if (type === "CONVERSATION_TITLE_UPDATED") {
      // console.log('type in conversation title updated', notification)
      const { activeConversationId, conversations } = get();
      const currentConv = activeConversationId ? conversations[activeConversationId] : null;
      const participants = currentConv?.participants || [];
      // console.log('participants', participants, "notification.actorId", notification)
      const updatedBy = participants.find((participant: any) => participant.itemId === notification.actorId);
      const messageContent = `${getUserDisplayName(updatedBy, 'Someone')} updated the conversation title`;
      const generationId = uuidv4();
      const evtShowTitle = formatShowMessageTimeForSend();
      const systemMessage: Message = {
        recordId: generationId,
        role: MessageRole.CONVERSATION_TITLE_UPDATED,
        generations: [
          {
            id: generationId,
            content: messageContent,
            createdDate: Date.now(),
            showMessageTime: evtShowTitle,
          },
        ],
      };
      // console.log('systemMessage in conversation title updated', systemMessage)
      get()._addMessageToActiveConversation(systemMessage);
    } else if (type === "REMOVE_PARTICIPANT") {
      try {
        const { activeConversationId, conversations } = get();
        if (!config || !activeConversationId) {
          return;
        }

        const currentConv = conversations[activeConversationId];
        if (!currentConv) {
          return;
        }

        const currentParticipants = currentConv.participants || [];
        const removedUserIds = notification.userIdsByType || notification.targetIds || [];

        if (!removedUserIds.length) {
          return;
        }

        const removedParticipants = currentParticipants.filter((participant) =>
          removedUserIds.includes(participant.itemId)
        );
        const resolvedIds = new Set(removedParticipants.map((participant) => participant.itemId));

        (currentConv.removedParticipants || []).forEach((participant) => {
          if (removedUserIds.includes(participant.itemId) && !resolvedIds.has(participant.itemId)) {
            removedParticipants.push(participant);
            resolvedIds.add(participant.itemId);
          }
        });

        const missingRemovedIds = removedUserIds.filter((id: string) => !resolvedIds.has(id));
        if (missingRemovedIds.length > 0) {
          const response = await get().fetchUsersData(missingRemovedIds, config);
          (response?.data || []).forEach((participant: IamData) => {
            if (!resolvedIds.has(participant.itemId)) {
              removedParticipants.push(participant);
              resolvedIds.add(participant.itemId);
            }
          });
        }

        let remover = currentParticipants.find(
          (participant) => participant.itemId === notification.actorId
        );
        if (!remover && notification.actorId) {
          const currentUser = useCommonStore.getState().user;
          if (currentUser?.id === notification.actorId) {
            remover = {
              itemId: currentUser?.id || '',
              firstName: currentUser?.firstName,
              lastName: currentUser?.lastName,
              email: currentUser?.email,
              userName: currentUser?.email,
            } as IamData;
          } else {
            const removerResponse = await get().fetchUsersData([notification.actorId], config);
            remover = removerResponse?.data?.[0];
          }
        }

        const isSelfRemoval = removedUserIds.includes(notification.actorId);

        let messageContent: string;
        if (isSelfRemoval) {
          const leavingUser =
            removedParticipants.find((participant) => participant.itemId === notification.actorId) ||
            remover;
          messageContent = `${getUserDisplayName(leavingUser, 'Someone')} left the conversation`;
        } else {
          const removedParticipantNames = removedParticipants
            .map((participant) => getUserDisplayName(participant, ''))
            .filter((name: string) => name.length > 0)
            .join(', ') || 'a user';
          messageContent = `${getUserDisplayName(remover, 'Someone')} removed ${removedParticipantNames}`;
        }

        const remainingParticipants = currentParticipants.filter(
          (participant) => !removedUserIds.includes(participant.itemId)
        );

        const existingRemovedParticipants = currentConv.removedParticipants || [];
        const updatedRemovedParticipants = [
          ...existingRemovedParticipants,
          ...removedParticipants.filter(
            (removed) =>
              !existingRemovedParticipants.some((existing) => existing.itemId === removed.itemId)
          ),
        ];

        set((state) => ({
          conversations: {
            ...state.conversations,
            [activeConversationId]: {
              ...(state.conversations[activeConversationId] ||
                initialConversationState(activeConversationId)),
              participants: remainingParticipants,
              removedParticipants: updatedRemovedParticipants,
            },
          },
        }));
        get().setHasGroupChatParticipantsArray(activeConversationId, remainingParticipants);

        const generationId = uuidv4();
        const evtShowRemoved = formatShowMessageTimeForSend();
        const systemMessage: Message = {
          generations: [
            {
              id: generationId,
              content: messageContent,
              createdDate: Date.now(),
              showMessageTime: evtShowRemoved,
            },
          ],
          recordId: generationId,
          role: MessageRole.USER_REMOVED,
        };

        get()._addMessageToActiveConversation(systemMessage);
      } catch (error) {
        console.error('❌ Error handling REMOVE_PARTICIPANT notification:', error);
      }
    }else if (type === "CONVERSATION_CREATED") {
      // console.log('type in conversation title updated', notification)
      const { activeConversationId, conversations } = get();
      const currentConv = activeConversationId ? conversations[activeConversationId] : null;
      const participants = currentConv?.participants || [];
      // console.log('participants', participants, "notification.actorId", notification)
      const createdBy = participants.find((participant: any) => participant.itemId === notification.actorId) || useCommonStore.getState().user?.id === notification.actorId ? useCommonStore.getState().user : null;
 
      const messageContent = `${getUserDisplayName(createdBy, 'Someone')} created the conversation`;
      const generationId = uuidv4();
      const evtShowCreated = showMessageTimeFromBackend(notification?.createdDate) ?? formatShowMessageTimeForSend();
 
      // console.log('messageContent in conversation created', messageContent,"participants", participants)
      const systemMessage: Message = {
        recordId: generationId,
        role: MessageRole.CONVERSATION_CREATED,
        generations: [
          {
            id: generationId,
            content: messageContent,
            createdDate: Date.now(),
            showMessageTime: evtShowCreated,
          },
        ],
      };
      // console.log('systemMessage in conversation created', systemMessage)
      get()._addMessageToActiveConversation(systemMessage);
    }


  },
  /**
   * Batch process streaming messages for optimal performance.
   * This function processes multiple ASSISTANT_STREAMED_RESPONSE messages
   * and accumulates content updates before triggering a single state update.
   */
  handleStreamingMessageBatch: (messages: any[], config: ConfigurationSettings) => {
    if (!messages || messages.length === 0 || !config) return;

    // Replay frames land before live ones; within a burst the wire order is the render order.
    const orderedMessages = [...messages].sort((a, b) => {
      const aReconnect = a?.reason === NEW_CONNECTION_WHILE_STREAMING;
      const bReconnect = b?.reason === NEW_CONNECTION_WHILE_STREAMING;
      if (aReconnect === bReconnect) return 0;
      return aReconnect ? -1 : 1;
    });

    // Process all messages - don't skip any to ensure all content is captured
    for (const notification of orderedMessages) {
      if (!notification?.response?.conversationId || !notification?.response?.message?.id) {
        continue;
      }

      const conversationId = notification.response.conversationId;
      // Re-fetch conversations for each message to get the latest state
      // This ensures we see placeholder messages created by prior processing
      const { conversations } = get();
      let currentConv = conversations[conversationId];
      const streamReason = notification.reason;
      const isReconnectWhileStreaming = streamReason === NEW_CONNECTION_WHILE_STREAMING;

      if (!currentConv) {
        if (!isReconnectWhileStreaming) {
          get().enqueuePendingStreamingNotification(conversationId, notification);
          continue;
        }
        set((state) => ({
          conversations: {
            ...state.conversations,
            [conversationId]: {
              ...initialConversationState(conversationId),
              isLoading: true,
              isCurrentResponseGenerating: true,
            },
          },
        }));
        currentConv = get().conversations[conversationId];
        if (!currentConv) continue;
      }

      const awaitingConversationHistory =
        !currentConv.hasFetchedInitialMessages && currentConv.messages.length === 0;

      if (awaitingConversationHistory && !isReconnectWhileStreaming) {
        get().enqueuePendingStreamingNotification(conversationId, notification);
        continue;
      }

      if (
        isReconnectWhileStreaming &&
        shouldQueueReconnectUntilUserPrompt(
          currentConv.messages,
          currentConv.hasFetchedInitialMessages
        )
      ) {
        get().enqueuePendingStreamingNotification(conversationId, notification);
        if (!currentConv.isLoading) {
          set((state) => {
            const conv = state.conversations[conversationId];
            if (!conv) return state;
            return {
              conversations: {
                ...state.conversations,
                [conversationId]: {
                  ...conv,
                  isLoading: true,
                  isCurrentResponseGenerating: true,
                },
              },
            };
          });
        }
        continue;
      }

      const message = notification.response.message;
      const messageId = message.id;
      const messageSenderId = resolveStreamUserMessageId(message, currentConv);
      
      const messageExists = hasStreamingAssistantTarget(
        currentConv.messages,
        messageId,
        messageSenderId
      );

      if (!messageExists || isReconnectWhileStreaming) {
        set((state) => {
          const conv = state.conversations[conversationId];
          if (!conv) return state;

          const messages = isReconnectWhileStreaming
            ? applyNewConnectionWhileStreaming(
                conv.messages,
                messageId,
                message?.content,
                notification.streamingResponse,
                messageSenderId,
                message.parentIds
              )
            : ensureStreamingAssistantPlaceholder(
                conv.messages,
                messageId,
                messageSenderId || messageId,
                message.parentIds
              );

          const streamingIds = conv.isGroupChat
            ? [...new Set([...(conv.streamingAssistantMessageIds || []), messageId])]
            : conv.streamingAssistantMessageIds;

          return {
            conversations: {
              ...state.conversations,
              [conversationId]: {
                ...conv,
                messages,
                streamingAssistantMessageId: messageId,
                ...(conv.isGroupChat && { streamingAssistantMessageIds: streamingIds }),
                isCurrentMessageLoading: shouldClearMessageLoadingForStreamEvent(
                  notification.streamingResponse?.type
                )
                  ? false
                  : conv.isCurrentMessageLoading,
                isCurrentResponseGenerating: true,
              },
            },
          };
        });
      }

      const parsedResponse = {
        ...notification.streamingResponse,
        conversation_id: conversationId,
        assistant_message_id: messageId,
        parent_ids: message.parentIds || [],
        message_sender_id:
          messageSenderId ?? message.metadata?.relatedUserMessageIds?.[0],
        parentIds: message.parentIds || [],
        messageSenderId:
          messageSenderId ?? message.metadata?.relatedUserMessageIds?.[0],
        ...(streamReason && { reason: streamReason, stream_reason: streamReason }),
      };

      // Process each message using existing handler. A single unhandled frame
      // must not abort the rest of the burst (e.g. STREAM_CANCELLED then END_OF_STREAM).
      try {
        get()._handleWebSocketMessage(parsedResponse, config);
      } catch (error) {
        console.error('Failed to handle streamed websocket frame:', error, parsedResponse);
      }
    }
  },

  handleStreamingQueue: (streamingQueue: StreamingQueueItem[], config: ConfigurationSettings) => {
    const collaborationStore = useCollaborationSocketStore.getState();

    if (!streamingQueue || streamingQueue.length === 0) {
      return;
    }

    // Group queue items by messageID to avoid creating duplicate messages
    const messagesMap = new Map<string, {
      conversationId: string;
      messageID: string;
      userID: string[] | null;
      parentIds: string[];
      streamingResponses: any[];
      type: string;
      positions: number[];
    }>();

    streamingQueue.forEach((queueItem) => {
      const { conversationId, messageID, userID, parentIds, streamingResponse, position } = queueItem;

      // console.log('queueItem', queueItem)

      if (!conversationId || !messageID || !streamingResponse || streamingResponse.length === 0) {
        return;
      }

      const key = `${conversationId}-${messageID}`;
      if (messagesMap.has(key)) {
        // Append streaming responses to existing entry
        const existing = messagesMap.get(key)!;
        existing.streamingResponses.push(...streamingResponse);
        existing.positions.push(position);
      } else {
        // Create new entry
        messagesMap.set(key, {
          conversationId,
          messageID,
          userID,
          parentIds,
          streamingResponses: [...streamingResponse],
          type: queueItem.type,
          positions: [position],
        });
      }
    });
    // console.log('messagesMap', messagesMap)

    // Track positions of processed messages (only remove from queue if processed)
    const processedPositions: number[] = [];

    // Process each unique messageID only once
    messagesMap.forEach((messageData) => {
      const { conversationId, messageID, userID, parentIds, streamingResponses, positions } = messageData;
      const userMessageId = userID && Array.isArray(userID) && userID[0] ? userID[0] : undefined;
      const serverAssignedMessageId = messageID;

      // Check if user message is delivered before processing streaming responses
      const currentState = get();
      const conv = currentState.conversations[conversationId];

      if (!conv || !conv.messages || !Array.isArray(conv.messages)) {
        return;
      }

      // For group chats, check if the user message is delivered
      if (conv.isGroupChat && userMessageId) {
        const userMessage = conv.messages.find(
          (msg) => msg.recordId === userMessageId &&
            (msg.role === MessageRole.USER_CHAT || msg.role === MessageRole.ASSISTANT_CHAT)
        );

        console.log('userMessage', userMessage)
        // If user message doesn't exist yet, skip processing (HTTP response hasn't completed)
        if (!userMessage) {
          // Message doesn't exist yet, skip processing but keep in queue
          return;
        }
        // Only process streaming if message is delivered or doesn't have delivery status (for backward compatibility)
        if (userMessage.deliveryStatus && userMessage.deliveryStatus !== MessageDeliveryStatus.DELIVERED) {
          // Message not delivered yet, skip processing but keep in queue
          return;
        }
      }

      processedPositions.push(...positions);

      // First, create/update the message structure in chat store (similar to original logic)
      set((state) => {
        const conv = state.conversations[conversationId];
        // console.log('conv in handleStreamingQueue', conv)
        if (!conv || !conv.messages || !Array.isArray(conv.messages)) return state;

        // This is how placeholders are set in sendGroupChatStream
        let messageIndex = -1;
        if (serverAssignedMessageId) {
          messageIndex = conv.messages.findIndex(
            (msg) =>
              msg.role === MessageRole.ASSISTANT &&
              (msg.recordId === serverAssignedMessageId ||
                msg.generations.some((gen) => gen.id === serverAssignedMessageId))
          );
        }

        if (messageIndex === -1 && conv.streamingAssistantMessageId) {
          messageIndex = conv.messages.findIndex(
            (msg) =>
              msg.role === MessageRole.ASSISTANT &&
              (msg.recordId === conv.streamingAssistantMessageId ||
                msg.generations.some((gen) => gen.id === conv.streamingAssistantMessageId)),
          );
        }

        if (messageIndex === -1 && userMessageId) {
          const linkedUserMessage = findUserMessageForLinkedId(conv.messages, userMessageId);
          if (linkedUserMessage) {
            messageIndex = conv.messages.findIndex(
              (msg) =>
                msg.role === MessageRole.ASSISTANT &&
                resolveUserMessageRecordIdForAssistant(conv.messages, msg) ===
                  linkedUserMessage.recordId,
            );
          }
        }

        const hasServerGeneration =
          messageIndex !== -1 &&
          conv.messages[messageIndex].generations.some((gen) => gen.id === serverAssignedMessageId);

        if (!hasServerGeneration) {
          return {
            conversations: {
              ...state.conversations,
              [conversationId]: {
                ...conv,
                messages: consolidateAssistantMessages(
                  ensureStreamingAssistantPlaceholder(
                    conv.messages,
                    serverAssignedMessageId,
                    userMessageId || '',
                    parentIds
                  )
                ),
                ...(!conv.isGroupChat &&
                  serverAssignedMessageId && {
                    streamingAssistantMessageId: serverAssignedMessageId,
                  }),
              },
            },
          };
        }

        const existingMsg = conv.messages[messageIndex];
        const isPlaceholder = existingMsg.userRecordId === userMessageId;

        const updatedMessages = conv.messages
          .filter((msg, index) => {
            if (index === messageIndex) return true;
            if (msg.role === MessageRole.ASSISTANT) {
              const hasMatchingGeneration = msg.generations.some(gen => gen.id === serverAssignedMessageId);
              const isDuplicatePlaceholder = (msg.userRecordId === userMessageId) &&
                index !== messageIndex;
              return !hasMatchingGeneration && !isDuplicatePlaceholder;
            }
            return true;
          })
          .map((msg) => {
            if (msg.recordId !== existingMsg.recordId) return msg;

            const updatedGenerations = msg.generations.map((gen) => {
              if (gen.id === serverAssignedMessageId) return gen;
              const isInFlightGeneration =
                gen.id === conv.streamingAssistantMessageId || !!gen.isThisGenerationOnGoing;
              if (isPlaceholder && msg.generations.length === 1) {
                const hasTypingContent = gen.content === 'SeliseAI is typing...';
                return {
                  ...gen,
                  id: serverAssignedMessageId,
                  ...(hasTypingContent && { content: '' }),
                };
              }
              if (isInFlightGeneration) {
                return { ...gen, id: serverAssignedMessageId };
              }
              return gen;
            });

            return {
              ...msg,
              ...(isPlaceholder && msg.generations.length === 1 && {
                recordId: serverAssignedMessageId,
              }),
              generations: updatedGenerations,
            };
          });

        const shouldUpdateStreamingId = !conv.isGroupChat && serverAssignedMessageId &&
          (serverAssignedMessageId !== conv.streamingAssistantMessageId);

        return {
          conversations: {
            ...state.conversations,
            [conversationId]: {
              ...conv,
              messages: consolidateAssistantMessages(updatedMessages),
              ...(shouldUpdateStreamingId && { streamingAssistantMessageId: serverAssignedMessageId }),
            },
          },
        };
      });

      // console.log('streamingResponses', streamingResponses)

      // Now process each streamingResponse to update the content
      streamingResponses.forEach((response: any) => {
        if (!response) return;

        // Create parsedResponse similar to ASSISTANT_STREAMED_RESPONSE handling
        const parsedResponse = {
          ...response,
          conversation_id: conversationId,
          assistant_message_id: messageID,
          parent_ids: parentIds || [],
          message_sender_id: userMessageId,
          parentIds: parentIds || [],
          messageSenderId: userMessageId,
        };

        // Process the streaming update using existing handler
        get()._handleWebSocketMessage(parsedResponse, config);
      });
    });

    // Remove only processed items from the queue (by position)
    // Only positions for messages that passed delivery check are included
    if (processedPositions.length > 0) {
      const removeFromStreamingQueue = (collaborationStore as any).removeFromStreamingQueue;
      if (removeFromStreamingQueue) {
        removeFromStreamingQueue(processedPositions);
      }
    }
  },
  _sortSidebarConversations: (list: SidebarItem[]): SidebarItem[] => {
    return [...list].sort(
      (a, b) => new Date(b.LastUpdatedDate).getTime() - new Date(a.LastUpdatedDate).getTime()
    );
  },

  fetchSidebarConversations: async (
    config: ConfigurationSettings,
    params: fetchConversationsListParams = {
      page: 0,
      pageSize: 20,
      isGroupConversation: false,
    }
  ) => {

    const isGroup = params.isGroupConversation ?? false;

    set((state) => ({
      isSidebarLoading: true,
      sidebarError: null,
      ...(isGroup ? { isSidebarGroupLoading: true } : { isSidebarIndividualLoading: true }),
    }));
    try {

      const payload = {
        "page": (params?.page ?? 0) + 1,
        "pageSize": params?.pageSize ?? 50,
        "isArchived": false,
        "isGroupConversation": params.isGroupConversation ?? false,
        ...(params.includeAgentConversations ? { includeAgentConversations: true } : {}),
      };
      const baseUrl = `${config.paths.seliseAiBaseUrl}`;
      const url = `${baseUrl}${config.paths.seliseAi.collaborationConversation}`;
      const res = await clients.post<{ data: ApiConversationResponse[], totalCount: number }>(
        `${url}`,
        config.blocksKey,
        JSON.stringify(payload)
      );

      if (res && res.data) {
      // Initialize shareTokens map from fetched data
      const shareTokens: Record<string, string> = {};
        set((state) => {
          const existingById = new Map(
            state.sidebarConversationsList.map((item) => [item.ConversationId, item])
          );

          const mappedData: SidebarItem[] = res.data.map((item) => {
            const existing = existingById.get(item.conversationId);
            const agentId = catalogueAgentIdFromSidebarItem(item, existing?.AgentId);

            const mapped: SidebarItem = {
              ConversationId: item.conversationId,
              ConversationTitle: item.conversationTitle,
              CreatedBy: item.createdBy,
              TenantId: item.tenantId,
              CreatedDate: item.createdDate,
              LastUpdatedDate: item.lastUpdatedDate,
              Conversations: item.messages,
              ProjectId: item.projectId ?? undefined,
              IsGroup: params.isGroupConversation ?? false,
              ShareToken: item.shareMetadata?.[0]?.token ?? undefined,
              CustomGptId: item.customGptId ?? undefined,
              AgentId: agentId,
              ...conversationVisualFields({
                logo_url: item.logo_url !== undefined ? item.logo_url : existing?.logo_url,
                logoUrl: item.logoUrl,
                color: item.color !== undefined ? item.color : existing?.color,
              }),
            };

            if (mapped.ShareToken) {
              shareTokens[mapped.ConversationId] = mapped.ShareToken;
            }

            return mapped;
          });

          const isGroup = params.isGroupConversation ?? false;
          const isFirstPage = (params?.page ?? 0) === 0;

          let newList: SidebarItem[];
          if (isFirstPage) {
            // First page: replace existing items of this type
            const otherTypeConversations = state.sidebarConversationsList.filter(
              (item) => item.IsGroup !== isGroup
            );

            if (!isGroup) {
              // Agent chats are nested under Agents — keep optimistic entries the general
              // sidebar fetch doesn't return yet (or returns without agentId).
              const mappedIds = new Set(mappedData.map((item) => item.ConversationId));
              const preservedAgentChats = state.sidebarConversationsList.filter(
                (item) => item.AgentId && !mappedIds.has(item.ConversationId)
              );
              newList = [...otherTypeConversations, ...preservedAgentChats, ...mappedData];
            } else {
              newList = [...otherTypeConversations, ...mappedData];
            }
          } else {
            // Subsequent pages: append and ensure uniqueness
            const existingIds = new Set(state.sidebarConversationsList.map(item => item.ConversationId));
            const uniqueNewItems = mappedData.filter(item => !existingIds.has(item.ConversationId));
            newList = [...state.sidebarConversationsList, ...uniqueNewItems];
          }

          const sortedResults = get()._sortSidebarConversations(newList);

          return {
            sidebarConversationsList: sortedResults,
            searchConversationsList: sortedResults,
            totalIndividualConversationsCount: isGroup
              ? state.totalIndividualConversationsCount
              : res.totalCount,
            totalGroupConversationsCount: isGroup
              ? res.totalCount
              : state.totalGroupConversationsCount,
            totalConversationsCount: res.totalCount,
            ...(isGroup
              ? { isSidebarGroupLoading: false }
              : { isSidebarIndividualLoading: false }),
            isSidebarLoading: isGroup
              ? state.isSidebarIndividualLoading
              : state.isSidebarGroupLoading,
            shareTokens: { ...get().shareTokens, ...shareTokens },
          };
        });
      } else {
        throw new Error('Invalid response structure from conversations-list API');
      }
    } catch (error) {
      console.error('Failed to fetch sidebar conversations:', error);
      const isGroup = params.isGroupConversation ?? false;
      set((state) => ({
        ...(isGroup
          ? { isSidebarGroupLoading: false }
          : { isSidebarIndividualLoading: false }),
        isSidebarLoading: isGroup
          ? state.isSidebarIndividualLoading
          : state.isSidebarGroupLoading,
        sidebarError: error instanceof Error ? error.message : 'Unknown error fetching sidebar',
      }));
    }
  },

  removeConversationFromSidebarById: (conversationId: string) => {
    if (!conversationId) return;
    set((state) => ({
      sidebarConversationsList: state.sidebarConversationsList.filter(
        (item) => item.ConversationId !== conversationId
      ),
    }));
  },

  removeConversationFromSidebarProjectListById: (conversationId: string) => {
    set((state) => ({
      sidebarProjectsList: state.sidebarProjectsList.map((item) => ({
        ...item,
        items: item.items?.filter((conv) => conv.ConversationId !== conversationId),
      })),
    }));
  },

  removeConversationFromRecentAndProjects: (conversationId: string) => {
    if (!conversationId) return;
    get().removeConversationFromSidebarById(conversationId);
    get().removeConversationFromSidebarProjectListById(conversationId);
  },

  appendToSidebarConversationsList: (newItem: SidebarItem) => {
    set((state) => {
      // Prevent duplicates if the item somehow already exists
      if (
        state.sidebarConversationsList.find((it) => it.ConversationId === newItem.ConversationId)
      ) {
        // If it exists, we might want to call _moveConversationToToday instead,
        // but for a true "append new", it shouldn't exist.
        // console.warn(`appendToSidebarConversationsList: Item ${newItem.ConversationId} already exists. Moving to top.`);
        // For now, let's ensure it's at the top if it exists, or add it if it doesn't.
        // This might be redundant if _moveConversationToToday is called.
        // Let's simplify: if called, assume it's new and should be at the top.
        const filteredList = state.sidebarConversationsList.filter(
          (it) => it.ConversationId !== newItem.ConversationId
        );
        return { sidebarConversationsList: [newItem, ...filteredList] }; // newItem is newest
      }
      // newItem is the newest, so prepend it. The list remains sorted.
      return { sidebarConversationsList: [newItem, ...state.sidebarConversationsList] };
    });
  },

  appendProjectItemToSidebarProjectsList: (newItem: ProjectItem, projectId: string) => {
    set((state) => {
      const project = state.sidebarProjectsList.find((it) => it.id === projectId);
      if (project) {
        const updatedItems = [...project.items, newItem];
        updatedItems.sort((a, b) => new Date(b.LastUpdatedDate).getTime() - new Date(a.LastUpdatedDate).getTime());
        return {
          sidebarProjectsList: state.sidebarProjectsList.map((project) => {
            if (project.id === projectId) {
              return { ...project, items: updatedItems };
            }
            return project;
          })
        };
      }
      return { sidebarProjectsList: [...state.sidebarProjectsList] };
    });
  },

  appendProjectToSidebarProjectsList: (newItem: Project) => {
    set((state) => {
      return { sidebarProjectsList: [...state.sidebarProjectsList, newItem] };
    });
  },

  _moveConversationToToday: (conversationId: string) => {
    set((state) => {
      const itemIndex = state.sidebarConversationsList.findIndex(
        (it) => it.ConversationId === conversationId
      );

      if (itemIndex === -1) {
        // console.warn(`_moveConversationToToday: Item ${conversationId} not found in sidebar. It might be a new chat handled by append.`);
        // If item is not in sidebar, it might be a brand new chat not yet fetched,
        // or an edge case. sendMessage should handle adding new items via appendToSidebarConversationsList.
        return {};
      }

      const itemToMove = { ...state.sidebarConversationsList[itemIndex] };
      itemToMove.LastUpdatedDate = new Date().toISOString();

      // Update title from main conversation state if available and more specific
      const conversationState = state.conversations[conversationId];
      console.log('conversationState', conversationState)
      // if (
      //   conversationState &&
      //   conversationState.title &&
      //   conversationState.title !== 'New Chat' &&
      //   conversationState.title !== 'Loading...'
      // ) {
      //   itemToMove.ConversationTitle = conversationState.title;
      // }

      const newListWithoutItem = [
        ...state.sidebarConversationsList.slice(0, itemIndex),
        ...state.sidebarConversationsList.slice(itemIndex + 1),
      ];

      // Add the updated item to the beginning, making it the most recent.
      return { sidebarConversationsList: [itemToMove, ...newListWithoutItem] };
    });
  },

  fetchSearchResults: async (
    search: string,
    config: ConfigurationSettings,
    options?: { page?: number; pageSize?: number }
  ) => {
    try {
      const page = options?.page ?? 1;
      const pageSize = options?.pageSize ?? 100;

      const payload = {
        page,
        pageSize,
        isArchived: false,
        searchQuery: search,
      };

      const baseUrl = `${config.paths.seliseAiBaseUrl}`;
      const url = `${baseUrl}${config.paths.seliseAi.collaborationConversation}`;
      const res = await clients.post<{ data: ApiConversationResponse[], totalCount: number }>(
        `${url}`,
        config.blocksKey,
        JSON.stringify(payload)
      );

      if (res && res.data) {
        const mappedData: SidebarItem[] = res.data.map((item) => {
          const existing = get().sidebarConversationsList.find(
            (conversation) => conversation.ConversationId === item.conversationId
          );
          const participants = item.participants;
          const participantCount = Array.isArray(participants) ? participants.length : 0;

          return {
            ConversationId: item.conversationId,
            ConversationTitle: item.conversationTitle,
            CreatedBy: item.createdBy,
            TenantId: item.tenantId,
            CreatedDate: item.createdDate,
            LastUpdatedDate: item.lastUpdatedDate,
            Conversations: item.messages,
            ProjectId: item.projectId ?? undefined,
            IsGroup: existing?.IsGroup ?? participantCount > 1,
            ShareToken: item.shareMetadata?.[0]?.token ?? existing?.ShareToken,
            CustomGptId: item.customGptId ?? undefined,
            AgentId: catalogueAgentIdFromSidebarItem(item, existing?.AgentId),
            ...conversationVisualFields({
              logo_url: item.logo_url !== undefined ? item.logo_url : existing?.logo_url,
              logoUrl: item.logoUrl,
              color: item.color !== undefined ? item.color : existing?.color,
            }),
          };
        });

        const sortedResults = get()._sortSidebarConversations(mappedData);
        set({
          searchConversationsList: sortedResults,
          searchConversationsTotalCount: res.totalCount ?? sortedResults.length,
        });
      } else {
        throw new Error('Invalid response structure from conversations-list API');
      }
    } catch (error) {
      console.error('Failed to fetch search results:', error);
    }
  },

  changeActiveGenerationIndex: (recordId: string, index: number) => {
    set((state) => {
      const activeConversationId = state.activeConversationId;
      if (!activeConversationId) return {};
      const conversation = state.conversations[activeConversationId];
      if (!conversation) return {};
      const messageIndex = conversation.messages.findIndex(
        (m) => (m.role === 'assistant' || m.role === 'user') && m.recordId === recordId
      );
      if (messageIndex === -1) return {};
      return {
        conversations: {
          ...state.conversations,
          [activeConversationId]: {
            ...conversation,
            messages: conversation.messages.map((m, idx) => {
              if (idx === messageIndex) {
                return {
                  ...m,
                  activeGenerationIndex: index,
                };
              }
              return m;
            }),
          },
        },
      };
    });
  },

  updateMessageFeedback: (recordId: string, messageId: string, feedback: boolean | undefined) => {
    set((state) => {
      const conversationId = state.activeConversationId;
      if (!conversationId) return {};
      const conversation = state.conversations[conversationId];
      if (!conversation) return {};

      let didUpdate = false;
      return {
        conversations: {
          ...state.conversations,
          [conversationId]: {
            ...conversation,
            messages: conversation.messages.map((m) => {
              if (m.role !== 'assistant') return m;

              const matchesRecordId =
                !!recordId && (m.recordId === recordId || m.userRecordId === recordId);
              const hasGeneration = (m.generations ?? []).some((g) => g.id === messageId);

              if (!matchesRecordId && !hasGeneration) return m;

              const updatedGenerations = (m.generations ?? []).map((g) => {
                if (g.id !== messageId) return g;
                didUpdate = true;
                return {
                  ...g,
                  feedback,
                };
              });
              if (!didUpdate) return m;

              return {
                ...m,
                generations: updatedGenerations,
              };
            }),
          },
        },
      };
    });
  },
  setNewChat: (isNew: boolean) => {
    set({
      isNewChat: isNew,
      disableNewChat: false,
      hasParticipant: false,
      hasParticipantsArray: [],
      hasAddedAssistantAsParticipant: false,
      headerTitle: "",
      activeConversationId: null,
      isLoadingParticipants: false,
    });
    if (isNew) {
      // Leave the chip alone. Setting Smart here, then the agent's model after
      // navigation, changes the label width and shifts the composer buttons.
      useModelStore.getState().userChangedModel(false);
    }
  },
  disableNewChat: false,
  setDisableNewChat: (disable: boolean) => {
    set({ disableNewChat: disable });
  },
  setNewChatWithModel: (isNew: boolean) => {
    console.log('isNew in setNewChatWithModel', isNew)
    set({
      // isNewChat: isNew,
      hasParticipant: false,
      hasParticipantsArray: [],
      hasAddedAssistantAsParticipant: false,
      headerTitle: '',
      activeConversationId: null,
      isLoadingParticipants: false,
    });

  },
  setHasParticipant: (value: boolean) => {
    set({ hasParticipant: value });
  },
  setHasParticipantsArray: (value: IamData[]) => {
    set((state) => {
      const activeConversationId = state.activeConversationId;
      return {
        hasParticipantsArray: value,
        ...(activeConversationId && {
          conversations: {
            ...state.conversations,
            [activeConversationId]: {
              ...(state.conversations[activeConversationId] || initialConversationState(activeConversationId)),
              participants: value,
            },
          },
        }),
      };
    });
  },
  setIsLoadingParticipants: (value: boolean) => {
    set({ isLoadingParticipants: value });
  },
  setChatErrorMessage: (conversationId: string) => {
    clearAssistantStreamTimeout(conversationId);
    set((state) => {
      const conversation = state.conversations[conversationId];
      if (!conversation) return state;

      return {
        conversations: {
          ...state.conversations,
          [conversationId]: {
            ...conversation,
            messages: withChatErrorMessage(conversation),
            streamingAssistantMessageId: null,
            streamingAssistantMessageIds: [],
            assistantResponseRemaining: 0,
            isCurrentResponseGenerating: false,
            isCurrentMessageLoading: false,
            isAnalyzing: false,
            error: CHAT_ERROR_MESSAGE_KEY,
          },
        },
      };
    });
  },
  reset: () => {
    set({
      activeConversationId: null,
      activeAudioMessageId: null,
      conversations: {},
      sidebarConversationsList: [],
      searchConversationsList: [],
      searchConversationsTotalCount: 0,
      sidebarProjectsList: [],
      isSidebarLoading: false,
      isSidebarIndividualLoading: false,
      isSidebarGroupLoading: false,
      sidebarError: null,
      isNewChat: false,
      hasParticipant: false,
      hasParticipantsArray: [],
      isLoadingParticipants: false,
      isGroupChat: false,
      itemIdFilePathMap: new Map<string, string>(),
    });
  },

  resetErrorByConversationId: (conversationId: string) => {
    set((state) => ({
      conversations: {
        ...state.conversations,
        [conversationId]: {
          ...state.conversations[conversationId],
          error: null,
        },
      },
    }));
  },

  getSearchRelatedList: async ({ conversationId, messageId, type, modelName }, config: ConfigurationSettings) => {

    try {
      const response = await getSearchRelatedQuestionList({ conversationId, messageId, type, modelName }, config);
      get().setrelatedSearcheQuestionsList(conversationId, response as RelativeSearchQuestion[], messageId);
      return response;
    } catch (error) {
      get().setrelatedSearcheQuestionsList(conversationId, [] as RelativeSearchQuestion[], messageId);
    }
  },

  getSearchRelatedImageList: async ({ conversationId, messageId, type, modelName }, config: ConfigurationSettings) => {
    try {
      const response = await getSearchRelatedQuestionList({ conversationId, messageId, type, modelName }, config);
      get().setrelatedSearcheImageList(conversationId, response as RelativeSearchQuestion[], messageId);
      return response;
    } catch (error) {
      get().setrelatedSearcheImageList(conversationId, [] as RelativeSearchQuestion[], messageId);
      console.log(error);
    }
  },

  getSearchRelatedVideoList: async ({ conversationId, messageId, type, modelName }, config: ConfigurationSettings) => {
    try {
      const response = await getSearchRelatedQuestionList({ conversationId, messageId, type, modelName }, config);
      get().setrelatedSearcheVideoList(conversationId, response as RelativeSearchQuestion[], messageId);
      return response;
    } catch (error) {
      get().setrelatedSearcheVideoList(conversationId, [] as RelativeSearchQuestion[], messageId);
      console.log(error);
    }
  },

  fetchUsersData: async (userIds: string[], config: ConfigurationSettings) => {
    const response = await getUsers({
      page: 0,
      pageSize: 100,
      filter: {
        userIds: userIds,
      },
    }, config);
    return response;
  },

  updateMessageAnalytics: (recordId: string, messageId: string, analytics: ResponseTimeAnalytics | undefined) => {
    set((state) => {
      const conversationId = state.activeConversationId;
      if (!conversationId) return {};
      const conversation = state.conversations[conversationId];
      if (!conversation) return {};
      const message = conversation.messages.find(
        (m) => m.role === 'assistant' && m.userRecordId === recordId
      );
      if (!message) return {};
      return {
        conversations: {
          ...state.conversations,
          [conversationId]: {
            ...conversation,
            messages: conversation.messages.map((m) => {
              if (m.role === 'assistant' && m.userRecordId === recordId) {
                m.generations = m.generations.map((g) => {
                  if (g.id === messageId) {
                    g.responseAnalytics = analytics;
                  }
                  return g;
                });
              }
              return m;
            }),
          },
        },
      };
    });
  },

  stopGeneration: async (conversationId: string, config: ConfigurationSettings) => {
    const conv = get().conversations[conversationId];
    if (!conv) return;

    const userMessage = [...(conv.messages ?? [])]
      .reverse()
      .find((m) => isUserMessageRole(m.role) || m.role === MessageRole.USER_CHAT);
    const userGenIndex = userMessage
      ? (userMessage.activeGenerationIndex ?? userMessage.generations.length - 1)
      : -1;
    const payload = buildCollaborationCancelPayload(conversationId, {
      assistantMessageId: conv.streamingAssistantMessageId,
      userMessageId:
        userMessage?.generations[userGenIndex]?.id ?? userMessage?.recordId,
    });
    if (!payload) return;

    let error: string | null = null;

    set((state) => ({
      conversations: {
        ...state.conversations,
        [conversationId]: {
          ...state.conversations[conversationId],
          isCancelOnGoing: true
        },
      }
    }))
    
    try{
      await stopGeneration(payload, config);
    }catch(err){
      error = "COUDNT_STOP_GENERATION"
    }

    if(error){
      set((state) => ({
        conversations: {
          ...state.conversations,
          [conversationId]: {
            ...state.conversations[conversationId],
            error
          },
        }
      }))
    }
  }
}));

setScheduledMessageTriggeredHandler((notification) => {
  useChatStore.getState().ensureScheduledConversationFromSocket(notification);
});

setBuddyOpenConversationIds(() => {
  const state = useChatStore.getState();
  const ids = Object.keys(state.conversations ?? {});
  for (const item of state.sidebarConversationsList ?? []) {
    if (item?.ConversationId) ids.push(item.ConversationId);
  }
  return ids;
});
